import React, { ReactNode } from 'react'
import { SvgIcon, SvgIconProps, Theme, styled } from '@mui/material'
import { TBPalette } from '~/src/shared/styles/theme/material-ui'

interface StyledSvgIconProps extends SvgIconProps {
  filling?: keyof TBPalette // color name
  // fontSize is already in use by SvgIconProps
  size?: string | number
  // Do not change the maskId property
  maskId?: string // Weirdly, in Safari browser, icons are sometimes hidden if they are used in multiple places. Giving own IDs solves the issue somehow.
}

const StyledSvgIcon = styled(SvgIcon)(
  ({ theme, filling, size }: { theme: Theme } & StyledSvgIconProps) => ({
    color: filling != null ? theme.palette.tb[filling] : 'inherit',
    fontSize: size ?? 'inherit',
  })
)

export const Edit = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    width="24"
    fill="none"
    {...svgIconProps}
  >
    <mask id="mask0_1:5793" maskUnits="userSpaceOnUse" x="1" y="1" width="22" height="23">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21.7431 2.2476C20.0478 0.568887 18.0839 0.584997 16.3623 2.29155L1.95504 16.5469C1.79879 16.7017 1.6982 16.9029 1.66793 17.1206L1.00973 21.8711C0.964814 22.19 1.07711 22.5103 1.31051 22.7325C1.49801 22.9112 1.74508 23.0083 1.99996 23.0083C2.06148 23.0083 2.12301 23.0025 2.18453 22.9912L6.77242 22.128C6.96871 22.0909 7.14937 21.9961 7.29097 21.856L20.3877 8.8975C20.3877 8.8975 20.3877 8.89731 20.3879 8.89716C20.388 8.89708 20.3883 8.89701 20.3886 8.89701L21.6982 7.60111C22.5625 6.74662 23 5.83402 23 4.89018C23 3.96195 22.5771 3.07279 21.7431 2.2476ZM6.1025 20.2188L3.1816 20.7686L3.6025 17.7305L14.4991 6.9484L16.9348 9.50132L6.1025 20.2188ZM18.357 8.09422L20.2929 6.17875C20.7617 5.71439 21 5.2808 21 4.89066C21 4.51566 20.7763 4.10502 20.3369 3.66947C19.4277 2.76957 18.707 2.78422 17.7685 3.71342L15.9213 5.54124L18.357 8.09422Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_1:5793)">
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const Failure = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    width="24"
    fill="none"
    {...svgIconProps}
  >
    <mask id="mask0_1:6351" maskUnits="userSpaceOnUse" x="1" y="1" width="22" height="22">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.9997 1C5.92505 1 1 5.92505 1 11.9996C1 18.0756 5.92505 23 11.9997 23C18.0763 23 23 18.0756 23 11.9996C23 5.92505 18.0763 1 11.9997 1ZM11.9997 21C7.03723 21 3 16.9625 3 11.9996C3 7.03723 7.03723 3 11.9997 3C16.9625 3 21 7.03723 21 11.9996C21 16.9625 16.9625 21 11.9997 21ZM14.4072 8.3125C14.7978 7.92188 15.4307 7.92188 15.8213 8.3125C16.2119 8.70312 16.2119 9.33594 15.8213 9.72656L13.5679 11.98L15.8213 14.2334C16.2119 14.624 16.2119 15.2568 15.8213 15.6475C15.626 15.8428 15.3701 15.9404 15.1143 15.9404C14.8584 15.9404 14.6025 15.8428 14.4072 15.6475L12.1538 13.394L9.90039 15.6475C9.70508 15.8428 9.44922 15.9404 9.19336 15.9404C8.9375 15.9404 8.68164 15.8428 8.48633 15.6475C8.09571 15.2568 8.09571 14.624 8.48633 14.2334L10.7397 11.98L8.48633 9.72656C8.09571 9.33594 8.09571 8.70312 8.48633 8.3125C8.87695 7.92188 9.50977 7.92188 9.90039 8.3125L12.1538 10.5659L14.4072 8.3125Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_1:6351)">
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const ChevronDown = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    width="24"
    fill="none"
    {...svgIconProps}
  >
    <mask
      id={svgIconProps.maskId ?? 'mask0_1:6391'}
      maskUnits="userSpaceOnUse"
      x="1"
      y="6"
      width="22"
      height="12"
    >
      <path
        d="M12 18C11.7441 18 11.4883 17.9023 11.293 17.707L1.29296 7.70703C0.902345 7.31641 0.902345 6.68358 1.29296 6.29296C1.68358 5.90235 2.31641 5.90235 2.70703 6.29296L12 15.5859L21.293 6.29296C21.6836 5.90235 22.3164 5.90235 22.707 6.29296C23.0976 6.68358 23.0976 7.31641 22.707 7.70703L12.707 17.707C12.5117 17.9023 12.2559 18 12 18Z"
        fill="white"
      />
    </mask>
    <g mask={`url(#${svgIconProps.maskId ?? 'mask0_1:6391'})`}>
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const AlertIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    width="24"
    fill="none"
    {...svgIconProps}
  >
    <mask id="mask0_1:6827" maskUnits="userSpaceOnUse" x="1" y="2" width="22" height="20">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.85927 21.1292C3.35769 21.1236 2.87197 20.9919 2.44511 20.749C1.77539 20.3661 1.29716 19.7477 1.09821 19.0061C0.900197 18.2682 1.00388 17.4985 1.3887 16.8372L9.53829 3.37758L9.53969 3.37524L9.54109 3.37291C9.78115 2.9806 10.1174 2.64809 10.5134 2.41177C11.8669 1.59635 13.636 2.03068 14.4598 3.37291L22.6038 16.8242C22.8597 17.2641 22.9942 17.7479 22.9998 18.2383C23.0082 19.0052 22.7149 19.7272 22.1751 20.2736C21.638 20.8172 20.9188 21.1208 20.1519 21.1292H3.85927ZM11.044 12.521C11.044 13.0497 11.4718 13.4775 12.0005 13.4775C12.5291 13.4775 12.9569 13.0497 12.9569 12.521V8.69512C12.9569 8.16645 12.5291 7.73866 12.0005 7.73866C11.4718 7.73866 11.044 8.16645 11.044 8.69512V12.521ZM12.0005 17.3033C12.5287 17.3033 12.9569 16.8751 12.9569 16.3469C12.9569 15.8186 12.5287 15.3904 12.0005 15.3904C11.4722 15.3904 11.044 15.8186 11.044 16.3469C11.044 16.8751 11.4722 17.3033 12.0005 17.3033ZM3.03357 17.8143L11.1729 4.3714C11.2523 4.24157 11.3634 4.13229 11.4961 4.0529C11.9528 3.77735 12.5515 3.92119 12.8261 4.36767L20.9589 17.8002C21.0411 17.9413 21.085 18.1001 21.0869 18.2598C21.0897 18.5111 20.9926 18.7483 20.8151 18.9286C20.6339 19.1116 20.391 19.2135 20.1416 19.2163H3.87048C3.70329 19.2144 3.53795 19.1696 3.39317 19.0874C3.16994 18.9603 3.01209 18.7558 2.94576 18.5111C2.88132 18.2701 2.91495 18.0179 3.03357 17.8143Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_1:6827)">
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const AddCircle = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    height="24"
    width="24"
    fill="none"
    {...svgIconProps}
  >
    <path
      d="M10 .833c5.063 0 9.167 4.104 9.167 9.167S15.063 19.167 10 19.167.833
      15.063.833 10 4.937.833 10 .833zm0 .834c-4.595 0-8.333 3.738-8.333 8.333 0 4.595 3.738 8.333
      8.333 8.333 4.595 0 8.333-3.738 8.333-8.333 0-4.595-3.738-8.333-8.333-8.333zm.014 4.701c.23
      0 .417.187.417.417v2.798h2.798c.23 0 .417.187.417.417 0 .23-.187.417-.417.417h-2.798v2.798c0
      .23-.187.417-.417.417-.23 0-.417-.187-.417-.417v-2.798H6.8c-.23 0-.417-.187-.417-.417
      0-.23.186-.417.417-.417h2.798V6.785c0-.23.187-.417.417-.417z"
    />
  </StyledSvgIcon>
)

export const SuccessFilledIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    width="24"
    fill="none"
    {...svgIconProps}
  >
    <mask id="SuccessFilledIcon" maskUnits="userSpaceOnUse" x="1" y="1" width="22" height="22">
      <path
        d="M1 11.9997C1 5.92506 5.92505 1 11.9997 1C18.0763 1 23 5.92506 23 11.9997C23 18.0756 18.0763 23 11.9997 23C5.92505 23 1 18.0756 1 11.9997ZM9.8418 15.3467C10.0371 15.542 10.293 15.6397 10.5488 15.6397C10.8047 15.6397 11.0606 15.542 11.2559 15.3467L16.5762 10.0269C16.9668 9.63624 16.9668 9.00342 16.5762 8.6128C16.1855 8.22218 15.5527 8.22218 15.1621 8.6128L10.5488 13.2256L8.83789 11.5147C8.44727 11.124 7.81445 11.124 7.42383 11.5147C7.03321 11.9053 7.03321 12.5381 7.42383 12.9287L9.8418 15.3467Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
      />
    </mask>
    <g mask="url(#SuccessFilledIcon)">
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const ExportIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 39 39"
    height="39"
    width="39"
    fill="none"
    {...svgIconProps}
  >
    <mask id="ExportIcon" maskUnits="userSpaceOnUse" x="2" y="6" width="35" height="27">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M28.255 29.8856C28.328 30.2775 28.6692 30.5513 29.0532 30.5513L29.2024 30.5378C33.7426 29.7023 36.5625 26.3237 36.5625 21.7209C36.5625 16.7213 32.7793 12.8048 27.9487 12.8048C27.7218 12.8048 27.4742 12.8254 27.2172 12.8556C25.8635 9.07952 22.3295 6.5 18.3939 6.5C13.1793 6.5 8.9375 10.9045 8.9375 16.319V16.3191C8.9375 16.4563 8.9375 16.6039 8.94067 16.757C5.33362 16.88 2.4375 19.9602 2.4375 23.7291C2.4375 27.5306 4.27039 29.7562 8.03931 30.5322C8.47254 30.6211 8.90893 30.3418 8.9978 29.9006C9.08984 29.4611 8.80579 29.031 8.36621 28.9405C5.38917 28.328 4.0625 26.7205 4.0625 23.7291C4.0625 20.7783 6.35082 18.378 9.16443 18.378L9.36596 18.4034C9.79761 18.4701 10.1912 18.4463 10.4324 18.163C10.5863 17.9821 10.6545 17.7433 10.618 17.5092C10.5622 17.1513 10.5623 16.7366 10.5625 16.3739C10.5625 16.3555 10.5625 16.3372 10.5625 16.319C10.5625 11.8011 14.0759 8.125 18.3939 8.125C21.8359 8.125 24.9098 10.53 25.8683 13.9736C25.9778 14.3679 26.3539 14.6186 26.7728 14.5583C26.8029 14.5538 26.833 14.5493 26.863 14.5447C27.2439 14.4871 27.6222 14.4298 27.9487 14.4298C31.8684 14.4298 34.9375 17.6322 34.9375 21.7209C34.9375 25.5461 32.6841 28.2447 28.9072 28.9398C28.4661 29.0207 28.1741 29.4444 28.255 29.8856ZM23.5974 25.8032C23.91 25.4835 24.4258 25.4803 24.7463 25.7929C25.0669 26.1079 25.0701 26.6221 24.7559 26.9426L19.5365 32.2572C19.3842 32.4127 19.1747 32.5 18.9573 32.5L18.9541 32.4992L18.9525 32.5C18.7208 32.5 18.5145 32.4008 18.3669 32.2461L13.1571 26.9426C12.8429 26.6221 12.8461 26.1079 13.1666 25.7937C13.4872 25.4795 14.0013 25.4835 14.3155 25.8032L18.14 29.6959V20.3918C18.14 19.9427 18.5034 19.5793 18.9525 19.5793C19.4016 19.5793 19.765 19.9427 19.765 20.3918V29.7055L23.5974 25.8032Z"
        fill="white"
      />
    </mask>
    <g mask="url(#ExportIcon)">
      <rect width="39" height="39" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const ChevronLeftIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    fill="none"
    width="24"
    {...svgIconProps}
  >
    <mask id="ChevronLeftIcon" maskUnits="userSpaceOnUse" x="6" y="1" width="12" height="22">
      <path
        d="M17 23C16.7441 23 16.4883 22.9023 16.293 22.707L6.29296 12.707C5.90235 12.3164 5.90235 11.6836 6.29296 11.293L16.293 1.29296C16.6836 0.902345 17.3164 0.902345 17.707 1.29296C18.0976 1.68358 18.0976 2.31641 17.707 2.70703L8.41406 12L17.707 21.293C18.0976 21.6836 18.0976 22.3164 17.707 22.707C17.5117 22.9023 17.2559 23 17 23Z"
        fill="white"
      />
    </mask>
    <g mask="url(#ChevronLeftIcon)">
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const Drag = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    fill="none"
    width="24"
    {...svgIconProps}
  >
    <mask id="Drag" maskUnits="userSpaceOnUse" x="4" y="1" width="16" height="22">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.5 5C7.60457 5 8.5 4.10457 8.5 3C8.5 1.89543 7.60457 1 6.5 1C5.39543 1 4.5 1.89543 4.5 3C4.5 4.10457 5.39543 5 6.5 5ZM17.5 5C18.6046 5 19.5 4.10457 19.5 3C19.5 1.89543 18.6046 1 17.5 1C16.3954 1 15.5 1.89543 15.5 3C15.5 4.10457 16.3954 5 17.5 5ZM8.5 12C8.5 13.1046 7.60457 14 6.5 14C5.39543 14 4.5 13.1046 4.5 12C4.5 10.8954 5.39543 10 6.5 10C7.60457 10 8.5 10.8954 8.5 12ZM17.5 14C18.6046 14 19.5 13.1046 19.5 12C19.5 10.8954 18.6046 10 17.5 10C16.3954 10 15.5 10.8954 15.5 12C15.5 13.1046 16.3954 14 17.5 14ZM8.5 21C8.5 22.1046 7.60457 23 6.5 23C5.39543 23 4.5 22.1046 4.5 21C4.5 19.8954 5.39543 19 6.5 19C7.60457 19 8.5 19.8954 8.5 21ZM17.5 23C18.6046 23 19.5 22.1046 19.5 21C19.5 19.8954 18.6046 19 17.5 19C16.3954 19 15.5 19.8954 15.5 21C15.5 22.1046 16.3954 23 17.5 23Z"
        fill="white"
      />
    </mask>
    <g mask="url(#Drag)">
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)
export const OpenLinkIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    fill="none"
    width="24"
    {...svgIconProps}
  >
    <mask id="OpenLinkIcon" maskUnits="userSpaceOnUse" x="1" y="1" width="22" height="22">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21 16.1313C21 15.5791 21.4478 15.1313 22 15.1313C22.5522 15.1313 23 15.5791 23 16.1313V19.7778C23 21.5547 21.5547 23 19.7778 23H4.22217C2.44531 23 1 21.5547 1 19.7778V4.22217C1 2.44531 2.44531 1 4.22217 1H8C8.55225 1 9 1.44775 9 2C9 2.55225 8.55225 3 8 3H4.22217C3.54834 3 3 3.54834 3 4.22217V19.7778C3 20.4517 3.54834 21 4.22217 21H19.7778C20.4517 21 21 20.4517 21 19.7778V16.1313ZM14.6123 10.8071C14.4175 11.002 14.1616 11.0996 13.9058 11.0996C13.6499 11.0996 13.3935 11.002 13.1982 10.8061C12.8081 10.4155 12.8081 9.78223 13.1992 9.39209L19.5669 3.03101L14.3364 3.00635C13.7842 3.00391 13.3389 2.55371 13.3413 2.00147C13.3438 1.45117 13.791 1.00635 14.3413 1.00635H14.3462L21.9688 1.04248C22.2445 1.0437 22.4933 1.15717 22.6735 1.33893C22.6737 1.33912 22.6739 1.33925 22.6741 1.33939C22.6744 1.33953 22.6746 1.33967 22.6748 1.33985C22.6839 1.34895 22.6898 1.35969 22.6956 1.37045C22.7007 1.37976 22.7058 1.38908 22.713 1.39735C22.7812 1.47663 22.845 1.56019 22.8859 1.65821C22.9407 1.78791 22.9664 1.92536 22.964 2.06391L23 9.65235C23.0024 10.2046 22.5571 10.6548 22.0049 10.6572H22C21.4497 10.6572 21.0024 10.2129 21 9.66211L20.9754 4.45056L14.6123 10.8071Z"
        fill="white"
      />
    </mask>
    <g mask="url(#OpenLinkIcon)">
      <rect width="24" height="24" fill="#inherit" />
    </g>
  </StyledSvgIcon>
)

export const DropdownMenuIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    fill="none"
    width="24"
    {...svgIconProps}
  >
    <mask
      id={svgIconProps.maskId ?? 'DropdownMenuIcon'}
      maskUnits="userSpaceOnUse"
      x="6"
      y="1"
      width="16"
      height="22"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.99999 23C6.74414 23 6.48827 22.9023 6.29296 22.707C5.90235 22.3164 5.90235 21.6836 6.29296 21.293L15.5859 12L6.29296 2.70703C5.90235 2.31641 5.90235 1.68358 6.29296 1.29296C6.68358 0.902345 7.31641 0.902345 7.70703 1.29296L17.707 11.293C18.0976 11.6836 18.0976 12.3164 17.707 12.707L7.70703 22.707C7.51172 22.9023 7.25585 23 6.99999 23Z"
        fill="white"
      />
    </mask>
    <g mask={`url(#${svgIconProps.maskId ?? 'DropdownMenuIcon'})`}>
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const CompanionAppsIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    fill="none"
    width="24"
    {...svgIconProps}
  >
    <mask id="CompanionAppsIcon" maskUnits="userSpaceOnUse" x="1" y="0" width="20" height="21">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.375 7.50838H13.475V2.625H18.375V7.50838ZM13.2808 9.25838H18.5701C19.4276 9.25838 20.125 8.561 20.125 7.7035V2.42987C20.125 1.57237 19.4276 0.875 18.5701 0.875H13.2808C12.4224 0.875 11.725 1.57237 11.725 2.42987V7.7035C11.725 8.561 12.4224 9.25838 13.2808 9.25838ZM4.15887 20.125H16.0046C17.3338 20.125 18.4144 19.0461 18.4144 17.7196V12.0164C18.4144 11.5334 18.0233 11.1414 17.5394 11.1414C17.0555 11.1414 16.6644 11.5334 16.6644 12.0164V17.7196C16.6644 18.081 16.3686 18.375 16.0046 18.375H4.15887C3.79575 18.375 3.5 18.081 3.5 17.7196V5.775C3.5 5.41362 3.79575 5.11963 4.15712 5.11963L9.22162 5.131H9.22338C9.7055 5.131 10.0975 4.74075 10.0984 4.25775C10.0993 3.77475 9.709 3.38187 9.22513 3.381L4.15887 3.36962C2.83063 3.36962 1.75 4.4485 1.75 5.775V17.7196C1.75 19.0461 2.83063 20.125 4.15887 20.125Z"
        fill="white"
      />
    </mask>
    <g mask="url(#CompanionAppsIcon)">
      <rect width="21" height="21" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const RegisterIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    fill="none"
    width="24"
    {...svgIconProps}
  >
    <mask id="RegisterIcon" maskUnits="userSpaceOnUse" x="1" y="1" width="22" height="22">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.21973 23H19.7598C21.5352 23 22.9795 21.5547 22.9795 19.7773V13.8447C22.9795 13.292 22.5322 12.8447 21.9795 12.8447C21.4268 12.8447 20.9795 13.292 20.9795 13.8447V19.7773C20.9795 20.4512 20.4326 21 19.7598 21H4.21973C3.54688 21 3 20.4512 3 19.7773V4.22266C3 3.54883 3.54688 3 4.21973 3H10.1475C10.7002 3 11.1475 2.55273 11.1475 2C11.1475 1.44727 10.7002 1 10.1475 1H4.21973C2.44434 1 1 2.44531 1 4.22266V19.7773C1 21.5547 2.44434 23 4.21973 23ZM7.74902 16.3037C7.93555 16.4814 8.18164 16.5791 8.4375 16.5791C8.46094 16.5791 8.48535 16.5781 8.50878 16.5762L11.8887 16.333C12.127 16.3164 12.3506 16.2148 12.5205 16.0479L21.2402 7.43653V7.43555L22.1133 6.57422C22.8447 5.85059 22.999 5.11328 23 4.62207C23.001 3.80176 22.5967 2.9834 21.7988 2.19142C20.3223 0.72755 18.6787 0.60645 17.4004 1.86915L7.8086 11.3428C7.62402 11.5264 7.51758 11.7734 7.51172 12.0332L7.4375 15.5576C7.43164 15.8389 7.54492 16.1103 7.74902 16.3037ZM9.45996 14.5029L9.50293 12.4815L18.8066 3.29297C19.0723 3.02832 19.4375 2.66699 20.3906 3.61133C20.7832 4.00196 21 4.36035 21 4.62012C21 4.68066 21 4.86328 20.709 5.15039L11.377 14.3652L9.45996 14.5029Z"
        fill="white"
      />
    </mask>
    <g mask="url(#RegisterIcon)">
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const UndoIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    fill="none"
    width="24"
    {...svgIconProps}
  >
    <mask id="UndoIcon" maskUnits="userSpaceOnUse" x="2" y="4" width="21" height="16">
      <path
        d="M17.873 8.52772H17.8662L17.8135 8.53846L17.7607 8.52772H3.69629L7.30859 4.85096C7.50244 4.65369 7.49951 4.33729 7.30273 4.14393C7.10449 3.9496 6.78809 3.95253 6.5957 4.14979L2.1582 8.66639L2.03711 8.84315L2.0166 8.94472L2 9.02772L2.00049 9.02968C2 9.15663 2.04736 9.28456 2.14355 9.38222L6.59521 13.9145C6.69335 14.0141 6.82275 14.0639 6.95214 14.0639C7.0786 14.0639 7.20507 14.016 7.30273 13.9203C7.49951 13.727 7.50244 13.4105 7.30859 13.2133L3.68847 9.52773H17.7607L17.8115 9.51699L17.8594 9.52773C20.1426 9.55897 22 11.4594 22 13.7641C22 16.1 20.1167 18.0004 17.8018 18.0004L17.6831 17.9984L17.6719 18.0004H11.4741C11.1978 18.0004 10.9741 18.224 10.9741 18.5004C10.9741 18.7768 11.1978 19.0004 11.4741 19.0004H17.7607L17.7632 18.9994L17.8018 19.0004C20.668 19.0004 23 16.6518 23 13.7641C23 10.9154 20.7002 8.56679 17.873 8.52772Z"
        fill="white"
      />
    </mask>
    <g mask="url(#UndoIcon)">
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const RedoIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    fill="none"
    width="24"
    {...svgIconProps}
  >
    <mask id="RedoIcon" maskUnits="userSpaceOnUse" x="1" y="4" width="21" height="16">
      <path
        d="M6.12695 8.52772H6.13379L6.18652 8.53846L6.23925 8.52772H20.3037L16.6914 4.85096C16.4976 4.65369 16.5005 4.33729 16.6973 4.14393C16.8955 3.9496 17.2119 3.95253 17.4043 4.14979L21.8418 8.66639L21.9629 8.84315L21.9834 8.94472L22 9.02772L21.9995 9.02968C22 9.15663 21.9526 9.28456 21.8565 9.38222L17.4048 13.9145C17.3067 14.0141 17.1772 14.0639 17.0479 14.0639C16.9214 14.0639 16.7949 14.016 16.6973 13.9203C16.5005 13.727 16.4976 13.4105 16.6914 13.2133L20.3115 9.52773H6.23926L6.18848 9.51699L6.14063 9.52773C3.85742 9.55897 2 11.4594 2 13.7641C2 16.1 3.8833 18.0004 6.19824 18.0004L6.31689 17.9984L6.32812 18.0004H12.5259C12.8022 18.0004 13.0259 18.224 13.0259 18.5004C13.0259 18.7768 12.8022 19.0004 12.5259 19.0004H6.23925L6.23681 18.9994L6.19824 19.0004C3.33203 19.0004 1 16.6518 1 13.7641C1 10.9154 3.2998 8.56679 6.12695 8.52772Z"
        fill="white"
      />
    </mask>
    <g mask="url(#RedoIcon)">
      <rect width="24" height="24" transform="matrix(-1 0 0 1 24 0)" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const MobileIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    fill="none"
    width="24"
    {...svgIconProps}
  >
    <mask id="MobileIcon" maskUnits="userSpaceOnUse" x="3" y="1" width="18" height="23">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.43506 1.00023H18.5649C19.9077 1.00023 21 2.09203 21 3.4348V20.5647C21 21.9075 19.9077 23.0002 18.5649 23.0002H5.43506C4.09229 23.0002 3 21.9074 3 20.5647V3.4348C3 2.09203 4.09229 1.00023 5.43506 1.00023ZM12 21.2708C12.4098 21.2708 12.7421 20.9412 12.7421 20.5347C12.7421 20.1282 12.4098 19.7986 12 19.7986C11.5902 19.7986 11.2579 20.1282 11.2579 20.5347C11.2579 20.9412 11.5902 21.2708 12 21.2708ZM5.43506 2.00023H18.5649C19.3565 2.00023 20 2.64379 20 3.4348V18.0354H4V3.4348C4 2.64379 4.64355 2.00023 5.43506 2.00023ZM5.43506 22.0002H18.5649C19.3565 22.0002 20 21.3567 20 20.5647V19.0354H4V20.5647C4 21.3567 4.64355 22.0002 5.43506 22.0002Z"
        fill="white"
      />
    </mask>
    <g mask="url(#MobileIcon)">
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const DesktopIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    height="24"
    fill="none"
    width="24"
    {...svgIconProps}
  >
    <mask id="DesktopIcon" maskUnits="userSpaceOnUse" x="1" y="2" width="22" height="21">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.35218 2.00023H20.6478C21.945 2.00023 23 3.05297 23 4.34691V16.175C23 17.469 21.945 18.5217 20.6478 18.5217H15.0245V21.0002H15.5892C15.8661 21.0002 16.0903 21.2239 16.0903 21.5002C16.0903 21.7766 15.8661 22.0002 15.5892 22.0002H8.38145C8.10449 22.0002 7.88038 21.7766 7.88038 21.5002C7.88038 21.2239 8.10449 21.0002 8.38145 21.0002H9.05036V18.5217H3.35218C2.05498 18.5217 1 17.469 1 16.175V4.34691C1 3.05297 2.05498 2.00023 3.35218 2.00023ZM20.6478 3.00023H3.35218C2.60792 3.00023 2.00214 3.60473 2.00214 4.34691V13.9446H21.9979V4.34691C21.9979 3.60472 21.3921 3.00023 20.6478 3.00023ZM14.0224 21.0002H10.0525V18.5217H14.0224V21.0002ZM3.35218 17.5217H20.6478C21.3921 17.5217 21.9979 16.9172 21.9979 16.175V14.9446H2.00214V16.175C2.00214 16.9172 2.60792 17.5217 3.35218 17.5217Z"
        fill="white"
      />
    </mask>
    <g mask="url(#DesktopIcon)">
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const Visibility = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    height="20"
    fill="none"
    width="20"
    {...svgIconProps}
  >
    <path
      d="M10 17.0833C4.23927 17.0833 0.929948 11.1693 0.542113 10.4762L0.542107 10.4762C0.52544 10.4464 0.51417 10.4263 0.508332 10.4167C0.389475 10.1808 0.389475 9.90254 0.508332 9.66667C0.649999 9.39167 3.99167 3 10 3C16.0083 3 19.35 9.43333 19.4917 9.70833C19.6105 9.94421 19.6105 10.2225 19.4917 10.4583C19.4824 10.4709 19.4594 10.5102 19.4228 10.5728L19.4227 10.5729C18.9005 11.4656 15.6146 17.0833 10 17.0833ZM2.20833 10C2.975 11.2833 5.775 15.4167 10 15.4167C14.225 15.4167 17.025 11.2917 17.7917 10C17.025 8.71667 14.225 4.58333 10 4.58333C5.775 4.58333 2.975 8.70833 2.20833 10ZM10 13.175C8.2521 13.1521 6.85154 11.7206 6.86679 9.97259C6.88203 8.22461 8.30734 6.81776 10.0554 6.8253C11.8034 6.83285 13.2165 8.25195 13.2167 10C13.2029 11.7641 11.7641 13.1842 10 13.175ZM10 8.49167C9.17278 8.51453 8.51845 9.19962 8.53358 10.027C8.54872 10.8544 9.22767 11.5151 10.0552 11.5077C10.8827 11.5003 11.5497 10.8275 11.55 10C11.5456 9.59407 11.3796 9.20665 11.0886 8.92353C10.7977 8.64042 10.4059 8.48499 10 8.49167Z"
      fillRule="evenodd"
      clipRule="evenodd"
      fill="inherit"
    />
  </StyledSvgIcon>
)

export const VisibilityOff = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    color="primary"
    height="24"
    fill="none"
    width="24"
    {...svgIconProps}
  >
    <path
      d="M23.39 11.55C22.128 9.23485 20.3817 7.21904 18.27 5.64L19.44 4.11C19.6579 3.82598 19.7078 3.44726 19.5708 3.11651C19.4338 2.78576 19.1307 2.55323 18.7758 2.50651C18.4208 2.45979 18.0679 2.60598 17.85 2.89L16.55 4.6C15.1387 3.8866 13.5813 3.51008 12 3.5C4.78 3.5 0.77 11.22 0.6 11.55C0.457371 11.833 0.457371 12.167 0.6 12.45C1.89791 14.8366 3.71199 16.9034 5.91 18.5L4.85 19.89C4.50732 20.3188 4.57426 20.9436 5 21.29C5.17183 21.4234 5.38248 21.4971 5.6 21.5C5.91301 21.5024 6.20907 21.358 6.4 21.11L7.67 19.5C9.01803 20.1495 10.4937 20.4911 11.99 20.5C18.9127 20.5 22.8843 13.4027 23.3495 12.5714C23.3695 12.5357 23.383 12.5115 23.39 12.5C23.55 12.2036 23.55 11.8465 23.39 11.55ZM2.64 12C3.56 10.45 6.91 5.5 12 5.5C13.1428 5.50412 14.2715 5.75304 15.31 6.23L13.55 8.52C12.3644 7.99602 10.9949 8.10212 9.90416 8.80246C8.8134 9.5028 8.14698 10.7039 8.13 12C8.12396 12.8843 8.4323 13.742 9 14.42L7.1 16.91C5.2927 15.5961 3.77464 13.9249 2.64 12ZM12 13.81C13.0124 13.8211 13.8436 13.0123 13.86 12C13.8522 11.8325 13.8185 11.6672 13.76 11.51L12 13.81ZM10.13 12C10.1379 11.512 10.3394 11.0471 10.6901 10.7076C11.0408 10.3682 11.512 10.182 12 10.19H12.26L10.32 12.74C10.2036 12.5101 10.1387 12.2576 10.13 12ZM8.93 17.86C9.90109 18.2738 10.9445 18.4913 12 18.5C17.08 18.5 20.43 13.55 21.35 12C20.2427 10.1439 18.7817 8.52322 17.05 7.23L15.11 9.77C15.596 10.4119 15.8593 11.1949 15.86 12C15.838 14.1146 14.1147 15.8156 12 15.81C11.5495 15.8067 11.1029 15.7255 10.68 15.57L8.93 17.86Z"
      fillRule="evenodd"
      clipRule="evenodd"
      fill="inherit"
    />
  </StyledSvgIcon>
)

export const Check = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 11 8"
    fill="none"
    height="10"
    width="11"
    {...svgIconProps}
  >
    <path
      d="M3.97126 8.0002C3.84564 8.0002 3.7255 7.94625 3.63756 7.85054L0.71393 4.66866C0.5328 4.47164 0.535542 4.15499 0.719871 3.96163C0.904195 3.76778 1.19999 3.77071 1.38134 3.96798L3.97126 6.78658L10.0695 0.149862C10.2507 -0.0474031 10.5462 -0.0503331 10.731 0.143512C10.9153 0.336872 10.9181 0.653522 10.737 0.850542L4.30497 7.85054C4.21703 7.94625 4.09689 8.0002 3.97126 8.0002Z"
      fill="inherit"
    />
  </StyledSvgIcon>
)

export const CloseIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 10 10"
    height="10"
    fill="none"
    width="11"
    {...svgIconProps}
  >
    <path
      d="M5.70703 5L9.85351 0.853513C10.0488 0.658203 10.0488 0.341792 9.85351 0.146482C9.6582 -0.0488275 9.34179 -0.0488275 9.14648 0.146482L5 4.29297L0.853513 0.146482C0.658203 -0.0488275 0.341792 -0.0488275 0.146482 0.146482C-0.0488275 0.341792 -0.0488275 0.658203 0.146482 0.853513L4.29297 5L0.146482 9.14648C-0.0488275 9.34179 -0.0488275 9.6582 0.146482 9.85351C0.244137 9.95117 0.372068 10 0.499997 10C0.627927 10 0.755858 9.95117 0.853513 9.85351L5 5.70703L9.14648 9.85351C9.24414 9.95117 9.37207 10 9.5 10C9.62793 10 9.75586 9.95117 9.85351 9.85351C10.0488 9.6582 10.0488 9.34179 9.85351 9.14648L5.70703 5Z"
      fill="inherit"
    />
  </StyledSvgIcon>
)

/** @public */
export const TBLogo = (): JSX.Element => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 207 36" width="207" height="36" fill="none">
    <rect width="207" height="36" fill="url(#pattern0)" />
    <defs>
      <pattern patternContentUnits="objectBoundingBox" id="pattern0" height="1" width="1">
        <use xlinkHref="#image0" transform="scale(0.00483092 0.0277778)" />
      </pattern>
      <image
        xlinkHref="data:image/png;base64,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"
        height="36"
        width="207"
        id="image0"
      />
    </defs>
  </svg>
)

export const WarningIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  <StyledSvgIcon
    width="28"
    height="27"
    viewBox="0 0 28 27"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...svgIconProps}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" />
      <rect width="1895" height="1522" transform="translate(-1306 -480)" />
      <mask id="mask0_0_1" maskUnits="userSpaceOnUse" x="1" y="1" width="22" height="22">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1 12C1 5.9248 5.9248 1 12 1C18.0752 1 23 5.9248 23 12C23 18.0752 18.0752 23 12 23C5.9248 23 1 18.0752 1 12ZM22 12C22 6.48633 17.5142 2 12 2C6.48584 2 2 6.48633 2 12C2 17.5137 6.48584 22 12 22C17.5142 22 22 17.5137 22 12ZM12.0522 13.4756C11.7759 13.4756 11.5522 13.252 11.5522 12.9756V7.48047C11.5522 7.2041 11.7759 6.98047 12.0522 6.98047C12.3286 6.98047 12.5522 7.2041 12.5522 7.48047V12.9756C12.5522 13.252 12.3286 13.4756 12.0522 13.4756ZM12.0522 15.252C11.458 15.252 10.977 15.7334 10.977 16.3272C10.977 16.9219 11.458 17.4033 12.0522 17.4033C12.646 17.4033 13.1274 16.9219 13.1274 16.3272C13.1274 15.7334 12.646 15.252 12.0522 15.252Z"
          fill="white"
        />
      </mask>
      <g mask="url(#mask0_0_1)">
        <rect width="24" height="24" fill="#666666" />
      </g>
    </svg>
  </StyledSvgIcon>
)

export const IconInactive1pt = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    height="16"
    width="16"
    fill="none"
    {...svgIconProps}
  >
    <mask
      id="IconInactive1pt"
      mask-type="alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="16"
      height="16"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.666016 8.00008C0.666016 3.94999 3.94926 0.666748 7.99935 0.666748C9.94427 0.666748 11.8095 1.43936 13.1848 2.81463C14.5601 4.1899 15.3327 6.05516 15.3327 8.00008C15.3327 12.0502 12.0494 15.3334 7.99935 15.3334C3.94926 15.3334 0.666016 12.0502 0.666016 8.00008ZM7.99935 2.00008C11.3131 2.00008 13.9993 4.68637 13.9993 8.00008C13.9993 11.3138 11.3131 14.0001 7.99935 14.0001C4.68564 14.0001 1.99935 11.3138 1.99935 8.00008C1.99935 4.68637 4.68564 2.00008 7.99935 2.00008ZM10.586 8.66675H5.43935C5.07116 8.66675 4.77268 8.36827 4.77268 8.00008C4.77268 7.63189 5.07116 7.33342 5.43935 7.33342H10.586C10.9542 7.33342 11.2527 7.63189 11.2527 8.00008C11.2527 8.36827 10.9542 8.66675 10.586 8.66675Z"
        fill="white"
      />
    </mask>
    <g mask="url(#IconInactive1pt)">
      <rect width="16" height="16" fill="#DB331A" />
    </g>
  </StyledSvgIcon>
)

export const IconActive1pt = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    height="16"
    width="16"
    fill="none"
    {...svgIconProps}
  >
    <mask
      id="IconActive1pt"
      mask-type="alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="16"
      height="16"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.99912 0.666748C3.94938 0.666748 0.666016 3.95012 0.666016 7.99985C0.666016 12.0505 3.94938 15.3334 7.99912 15.3334C12.0502 15.3334 15.3327 12.0505 15.3327 7.99985C15.3327 3.95012 12.0502 0.666748 7.99912 0.666748ZM7.99912 2.00008C11.3077 2.00008 13.9993 4.69157 13.9993 7.99985C13.9993 11.3084 11.3077 14.0001 7.99912 14.0001C4.69084 14.0001 1.99935 11.3084 1.99935 7.99985C1.99935 4.69157 4.69084 2.00008 7.99912 2.00008ZM6.56055 10.2312C6.69076 10.3614 6.86133 10.4265 7.0319 10.4265C7.20248 10.4265 7.37305 10.3614 7.50326 10.2312L11.0501 6.68465C11.3105 6.42424 11.3105 6.00236 11.0501 5.74195C10.7897 5.48153 10.3678 5.48153 10.1074 5.74195L7.0319 8.81714L5.89128 7.67651C5.63086 7.4161 5.20898 7.4161 4.94857 7.67651C4.68816 7.93693 4.68816 8.35881 4.94857 8.61922L6.56055 10.2312Z"
        fill="white"
      />
    </mask>
    <g mask="url(#IconActive1pt)">
      <rect width="16" height="16" fill={svgIconProps.fill ?? '#159948'} />
    </g>
  </StyledSvgIcon>
)

export const DuplicateIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  <StyledSvgIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" {...svgIconProps}>
    <g>
      <path
        d="M19.94824,22.5h-9.23047c-1.40625,0-2.55078-1.1499-2.55078-2.56299v-9.28369c0-1.41309,1.14453-2.56299,2.55078-2.56299
        h9.23047c1.40723,0,2.55176,1.1499,2.55176,2.56299v9.28369C22.5,21.3501,21.35547,22.5,19.94824,22.5z M10.71777,9.09033
        c-0.85547,0-1.55078,0.70117-1.55078,1.56299v9.28369c0,0.86182,0.69531,1.56299,1.55078,1.56299h9.23047
        c0.85547,0,1.55176-0.70117,1.55176-1.56299v-9.28369c0-0.86182-0.69629-1.56299-1.55176-1.56299H10.71777z"
      />
      <path
        d="M5.07715,15.90967H4.05176c-1.40723,0-2.55176-1.1499-2.55176-2.56299V4.06299C1.5,2.6499,2.64453,1.5,4.05176,1.5h9.23047
        c1.40625,0,2.55078,1.1499,2.55078,2.56299v1.03174c0,0.27637-0.22363,0.5-0.5,0.5s-0.5-0.22363-0.5-0.5V4.06299
        c0-0.86182-0.69531-1.56299-1.55078-1.56299H4.05176C3.19629,2.5,2.5,3.20117,2.5,4.06299v9.28369
        c0,0.86182,0.69629,1.56299,1.55176,1.56299h1.02539c0.27637,0,0.5,0.22363,0.5,0.5S5.35352,15.90967,5.07715,15.90967z"
      />
    </g>
  </StyledSvgIcon>
)

export const TagIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  // https://www.figma.com/file/FdPaL0YuGy75gZR5eXWZY2/Icons
  <StyledSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 39 39"
    height="39"
    width="39"
    fill="none"
    {...svgIconProps}
  >
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <g id="Layer_2" data-name="Layer 2">
        <path d="M23.94,10,23.08,2.5A2.12,2.12,0,0,0,21.15.64L14,.07a2,2,0,0,0-1.66.61L.72,12.27A2.06,2.06,0,0,0,.1,13.76a2.1,2.1,0,0,0,.62,1.49l8.06,8.06a2.1,2.1,0,0,0,1.49.62,2.06,2.06,0,0,0,1.49-.62L23.33,11.75A2.12,2.12,0,0,0,23.94,10Zm-1.36,1L11,22.57a1.07,1.07,0,0,1-1.5,0l-8-8.06a1.06,1.06,0,0,1,0-1.5L13.05,1.42a1.09,1.09,0,0,1,.83-.3l7.18.57a1.06,1.06,0,0,1,1,.93l.86,7.51A1.07,1.07,0,0,1,22.58,11Z" />
        <circle cx="17.43" cy="6.41" r="1.25" />
      </g>
    </svg>
  </StyledSvgIcon>
)

export const More1pt = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  <StyledSvgIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" {...svgIconProps}>
    <g>
      <path
        d="M12,2c5.51367,0,10,4.48584,10,9.99951C22,17.51367,17.51367,22,12,22C6.48535,22,2,17.51367,2,11.99951
        C2,6.48584,6.48535,2,12,2 M12,1C5.9248,1,1,5.9248,1,11.99951C1,18.07568,5.9248,23,12,23c6.07617,0,11-4.92432,11-11.00049
        C23,5.9248,18.07617,1,12,1z"
      />
      <circle cx="7.4082" cy="11.98584" r="1.4043" />
      <circle cx="12" cy="11.98584" r="1.4043" />
      <circle cx="16.5918" cy="11.98584" r="1.4043" />
    </g>
  </StyledSvgIcon>
)

export const More2pt = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  <StyledSvgIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" {...svgIconProps}>
    <g>
      <path
        d="M11.99965,3C16.96246,3,21,7.03723,21,11.99965C21,16.96246,16.96246,21,11.99965,21C7.03723,21,3,16.96246,3,11.99965
        C3,7.03723,7.03723,3,11.99965,3 M11.99965,1C5.92505,1,1,5.92505,1,11.99965C1,18.07565,5.92505,23,11.99965,23
        C18.07628,23,23,18.07565,23,11.99965C23,5.92505,18.07628,1,11.99965,1L11.99965,1z"
      />
    </g>
    <g>
      <circle cx="7.40806" cy="11.9858" r="1.40444" />
    </g>
    <g>
      <circle cx="12" cy="11.9858" r="1.40444" />
    </g>
    <g>
      <circle cx="16.59194" cy="11.9858" r="1.40444" />
    </g>
  </StyledSvgIcon>
)

// REFER & EARN
export const PhoneIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  <StyledSvgIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 31 31" {...svgIconProps}>
    <mask id="phoneIcon-mask" maskUnits="userSpaceOnUse" x="1" y="1" width="28" height="28">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M23.8147 1.93283C25.3731 1.8487 26.5817 3.1295 27.5461 4.1467C27.7157 4.32628 27.8819 4.50103 28.0451 4.66428C28.6289 5.24574 28.9363 6.17927 28.9338 7.35959C28.9286 10.2832 26.9232 15.6756 21.2873 21.2512C16.1387 26.3923 10.7161 28.9372 7.3342 28.9387C6.18397 28.9387 5.27055 28.6442 4.68283 28.0578C4.52839 27.9034 4.36238 27.7464 4.19162 27.5832C3.1556 26.5974 1.86564 25.3702 1.93088 23.7678C1.97609 22.6677 2.64101 21.6149 3.96213 20.55C7.58873 17.628 9.06088 18.6287 10.8918 20.4885L11.1887 20.7893C11.2866 20.8872 12.2895 21.6077 16.9699 16.9348C20.4936 13.4186 20.8742 11.9882 20.8742 11.5373C20.8742 11.3577 20.8196 11.2795 20.7668 11.2268L20.5061 10.968C18.6451 9.1321 17.645 7.65505 20.591 4.00705C21.6608 2.65979 22.7173 1.98073 23.8147 1.93283ZM23.8703 3.21799C23.1771 3.24816 22.4112 3.78424 21.5949 4.81272C19.1575 7.83034 19.9097 8.57361 21.4104 10.0529L21.676 10.3176C21.9925 10.6341 22.1604 11.0551 22.1604 11.5373C22.1604 12.9061 20.7599 14.9695 17.8791 17.844C13.917 21.801 11.5743 22.9907 10.2795 21.6985L9.9758 21.3918C9.18341 20.5869 8.60175 19.9973 7.7717 19.9973C7.05595 19.9975 6.15269 20.4358 4.76779 21.552C3.76692 22.3582 3.24491 23.1223 3.21603 23.8205C3.17465 24.8402 4.18549 25.8024 5.07834 26.6526C5.25662 26.8221 5.43005 26.9867 5.59201 27.1487C6.9436 28.4934 13.388 27.3186 20.3801 20.3391C25.3946 15.3786 27.6426 10.2448 27.6477 7.35764C27.6489 6.53775 27.4684 5.90467 27.1369 5.57443C26.9649 5.40366 26.7918 5.21884 26.6135 5.03049C25.806 4.17906 24.8913 3.21604 23.9319 3.21604L23.8703 3.21799Z"
        fill="white"
      />
    </mask>
    <g mask="url(#phoneIcon-mask)">
      <rect width="31" height="31" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const DollarThinIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  <StyledSvgIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" {...svgIconProps}>
    <mask id="dollarThinIcon-mask" maskUnits="userSpaceOnUse" x="1" y="1" width="22" height="22">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 22.5C6.21045 22.5 1.5 17.79 1.5 12C1.5 6.20996 6.21045 1.5 12 1.5C17.7896 1.5 22.5 6.20996 22.5 12C22.5 17.79 17.7896 22.5 12 22.5ZM12 2.5C6.76172 2.5 2.5 6.76172 2.5 12C2.5 17.2383 6.76172 21.5 12 21.5C17.2383 21.5 21.5 17.2383 21.5 12C21.5 6.76172 17.2383 2.5 12 2.5ZM9.36133 10.1709C9.36133 10.9033 9.95801 11.5 10.6909 11.5H13.3042C14.5889 11.5 15.6338 12.5449 15.6338 13.8291C15.6338 15.1143 14.5889 16.1592 13.3042 16.1592H12.4917L12.4976 16.1875V17.75C12.4976 18.0264 12.2739 18.25 11.9976 18.25C11.7212 18.25 11.4976 18.0264 11.4976 17.75V16.1875L11.5034 16.1592H8.86133C8.58496 16.1592 8.36133 15.9355 8.36133 15.6592C8.36133 15.3828 8.58496 15.1592 8.86133 15.1592H13.3042C14.0371 15.1592 14.6338 14.5625 14.6338 13.8291C14.6338 13.0967 14.0371 12.5 13.3042 12.5H10.6909C9.40625 12.5 8.36133 11.4551 8.36133 10.1709C8.36133 8.88574 9.40625 7.84082 10.6909 7.84082H11.5034L11.4976 7.8125V6.25C11.4976 5.97363 11.7212 5.75 11.9976 5.75C12.2739 5.75 12.4976 5.97363 12.4976 6.25V7.8125L12.4917 7.84082H14.6113C14.8877 7.84082 15.1113 8.06445 15.1113 8.34082C15.1113 8.61719 14.8877 8.84082 14.6113 8.84082H10.6909C9.95801 8.84082 9.36133 9.4375 9.36133 10.1709Z"
        fill="white"
      />
    </mask>
    <g mask="url(#dollarThinIcon-mask)">
      <rect width="24" height="24" fill="inherit" />
    </g>
  </StyledSvgIcon>
)

export const MegaphoneIcon = ({ ...svgIconProps }: StyledSvgIconProps): JSX.Element => (
  <StyledSvgIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" {...svgIconProps}>
    <path d="M1.25002 15C1.25167 13.4755 1.85801 12.0139 2.93598 10.9359C4.01396 9.85797 5.47553 9.25164 7.00002 9.24999H11.9813C12.3038 9.23124 18.7438 8.80374 24.875 3.66124C25.1301 3.44717 25.4409 3.31032 25.7711 3.26675C26.1012 3.22319 26.4369 3.27471 26.7387 3.41527C27.0406 3.55584 27.2961 3.77961 27.4752 4.06031C27.6544 4.34101 27.7497 4.667 27.75 4.99999V25C27.7523 25.3332 27.6583 25.6601 27.4794 25.9412C27.3004 26.2224 27.0441 26.4459 26.7413 26.585C26.509 26.693 26.2561 26.7493 26 26.75C25.588 26.7502 25.1892 26.604 24.875 26.3375C19.875 22.1475 14.6788 21.0875 12.75 20.8287V25.0787C12.7501 25.3668 12.6791 25.6505 12.5432 25.9045C12.4074 26.1585 12.2109 26.3751 11.9713 26.535L10.5963 27.4512C10.3637 27.6061 10.0974 27.703 9.81973 27.7339C9.54203 27.7648 9.26097 27.7288 9.00005 27.6288C8.73914 27.5288 8.50596 27.3678 8.32002 27.1592C8.13408 26.9507 8.00078 26.7006 7.93127 26.43L6.41502 20.7162C5.00012 20.5696 3.68956 19.9044 2.7359 18.849C1.78223 17.7935 1.25294 16.4225 1.25002 15ZM25.8388 25.19C25.8751 25.2207 25.9195 25.2403 25.9666 25.2467C26.0137 25.2531 26.0617 25.2458 26.1049 25.2259C26.1481 25.2059 26.1846 25.1741 26.2104 25.1341C26.2361 25.0941 26.2498 25.0475 26.25 25V4.99999C26.2514 4.95239 26.2384 4.90549 26.2128 4.86537C26.1871 4.82525 26.15 4.79376 26.1063 4.77499C26.073 4.75923 26.0368 4.75071 26 4.74999C25.942 4.75017 25.886 4.7715 25.8425 4.80999C20.3425 9.42498 14.7175 10.4587 12.7538 10.685V19.31C14.7138 19.5412 20.3375 20.5762 25.8388 25.19ZM9.38377 26.065C9.39347 26.1039 9.41237 26.1399 9.43889 26.1699C9.46541 26.1999 9.49876 26.2232 9.53613 26.2376C9.57351 26.2521 9.61381 26.2574 9.65364 26.253C9.69348 26.2486 9.73168 26.2347 9.76502 26.2125L11.14 25.2962C11.1744 25.2733 11.2025 25.2423 11.2219 25.2059C11.2413 25.1694 11.2514 25.1288 11.2513 25.0875V20.75H7.97627L9.38377 26.065ZM7.00002 19.25H11.25V10.75H7.00002C5.87285 10.75 4.79185 11.1978 3.99482 11.9948C3.19779 12.7918 2.75002 13.8728 2.75002 15C2.75002 16.1272 3.19779 17.2082 3.99482 18.0052C4.79185 18.8022 5.87285 19.25 7.00002 19.25Z" />
  </StyledSvgIcon>
)

// APP BAR
const SCHEDULE_1PT = (
  <>
    <path
      d="M20.06641,3.74121h-2.4668V2c0-0.27637-0.22363-0.5-0.5-0.5s-0.5,0.22363-0.5,0.5v1.74121h-9.2168V2
  		c0-0.27637-0.22363-0.5-0.5-0.5s-0.5,0.22363-0.5,0.5v1.74121H3.93359c-1.33984,0-2.43066,1.09033-2.43066,2.43018v13.89844
  		c0,1.33984,1.09082,2.43018,2.43066,2.43018h16.13281c1.33984,0,2.43066-1.09033,2.43066-2.43018V6.17139
  		C22.49707,4.83154,21.40625,3.74121,20.06641,3.74121z M21.49707,20.06982c0,0.78857-0.6416,1.43018-1.43066,1.43018H3.93359
  		c-0.78906,0-1.43066-0.6416-1.43066-1.43018V6.17139c0-0.78857,0.6416-1.43018,1.43066-1.43018h2.44922v1.74121
  		c0,0.27637,0.22363,0.5,0.5,0.5s0.5-0.22363,0.5-0.5V4.74121h9.2168v1.74121c0,0.27637,0.22363,0.5,0.5,0.5s0.5-0.22363,0.5-0.5
  		V4.74121h2.4668c0.78906,0,1.43066,0.6416,1.43066,1.43018V20.06982z"
    />
    <path
      d="M15.51562,10.98535l-4.9668,4.96631l-2.06445-2.06445c-0.19531-0.19531-0.51172-0.19531-0.70703,0
  		s-0.19531,0.51172,0,0.70703l2.41797,2.41797c0.09766,0.09766,0.22559,0.14648,0.35352,0.14648s0.25586-0.04883,0.35352-0.14648
  		l5.32031-5.31982c0.19531-0.19531,0.19531-0.51172,0-0.70703S15.71094,10.79004,15.51562,10.98535z"
    />
  </>
)

const SCHEDULE_2PT = (
  <>
    <path
      d="M20.06641,3.24121h-1.9668V2c0-0.55225-0.44727-1-1-1s-1,0.44775-1,1v1.24121h-8.2168V2c0-0.55225-0.44727-1-1-1
    s-1,0.44775-1,1v1.24121H3.93359c-1.61621,0-2.93066,1.31445-2.93066,2.93018v13.89844C1.00293,21.68555,2.31738,23,3.93359,23
    h16.13281c1.61621,0,2.93066-1.31445,2.93066-2.93018V6.17139C22.99707,4.55566,21.68262,3.24121,20.06641,3.24121z
     M20.99707,20.06982c0,0.5127-0.41797,0.93018-0.93066,0.93018H3.93359c-0.5127,0-0.93066-0.41748-0.93066-0.93018V6.17139
    c0-0.5127,0.41797-0.93018,0.93066-0.93018h1.94922v1.24121c0,0.55225,0.44727,1,1,1s1-0.44775,1-1V5.24121h8.2168v1.24121
    c0,0.55225,0.44727,1,1,1s1-0.44775,1-1V5.24121h1.9668c0.5127,0,0.93066,0.41748,0.93066,0.93018V20.06982z"
    />
    <path
      d="M15.16211,10.63184l-4.61328,4.61279l-1.71094-1.71094c-0.39062-0.39062-1.02344-0.39062-1.41406,0
    s-0.39062,1.02344,0,1.41406l2.41797,2.41797c0.19531,0.19531,0.45117,0.29297,0.70703,0.29297s0.51172-0.09766,0.70703-0.29297
    l5.32031-5.31982c0.39062-0.39062,0.39062-1.02344,0-1.41406S15.55273,10.24121,15.16211,10.63184z"
    />
  </>
)

const HAMBURGER_1PT = (
  <>
    <path
      d="M22,12.53418H2c-0.27637,0-0.5-0.22363-0.5-0.5s0.22363-0.5,0.5-0.5h20c0.27637,0,0.5,0.22363,0.5,0.5
  		S22.27637,12.53418,22,12.53418z"
    />
    <path d="M22,6H2C1.72363,6,1.5,5.77637,1.5,5.5S1.72363,5,2,5h20c0.27637,0,0.5,0.22363,0.5,0.5S22.27637,6,22,6z" />
    <path d="M22,19H2c-0.27637,0-0.5-0.22363-0.5-0.5S1.72363,18,2,18h20c0.27637,0,0.5,0.22363,0.5,0.5S22.27637,19,22,19z" />
  </>
)

const NOTIFICATION_1PT = (
  <path
    d="M19.8916,14.60107c-0.6875-0.17236-0.7666-1.53369-0.80371-2.18799c-0.00879-0.146-0.0166-0.27051-0.02637-0.36279
		c-0.03027-0.26025-0.03418-0.5918-0.04102-0.97998c-0.02734-1.59912-0.06836-4.0166-1.56836-6.53857
		c-1.36426-2.29199-3.44531-2.88623-4.89551-2.99805C12.08984,1.47363,8.67871,1.1626,6.5332,4.62598
		C4.99414,7.10889,4.98047,9.4834,4.97168,11.0542c-0.00195,0.39355-0.00293,0.73047-0.0332,0.99658
		c-0.00977,0.0918-0.01758,0.21631-0.02637,0.3623C4.875,13.06738,4.7959,14.42871,4.1084,14.60107
		C2.80762,14.92627,2,15.81934,2,16.93164v1.67188c0,0.27637,0.22363,0.5,0.5,0.5h6.08594
		c0.00293,0.4668,0.09473,1.58496,0.87109,2.4248C10.05273,22.17285,10.89941,22.5,11.97559,22.5
		c1.08398,0,1.93652-0.32861,2.5332-0.97705c0.77148-0.83887,0.86035-1.95166,0.86133-2.41943H21.5c0.27637,0,0.5-0.22363,0.5-0.5
		v-1.67188C22,15.81934,21.19238,14.92627,19.8916,14.60107z M13.77246,20.8457C13.37305,21.27979,12.76855,21.5,11.97559,21.5
		c-0.78223,0-1.38086-0.21729-1.7793-0.64551c-0.54395-0.58398-0.60742-1.42188-0.61035-1.75098h1.41797h1.15137h2.21484
		C14.36816,19.42822,14.30762,20.26318,13.77246,20.8457z M21,18.10352h-8.84473h-1.15137H3v-1.17188
		c0-0.64014,0.50488-1.14893,1.35059-1.36084c1.40137-0.35059,1.50879-2.20752,1.56055-3.1001
		c0.00684-0.12402,0.0127-0.22949,0.02148-0.30713c0.0332-0.29443,0.03711-0.66748,0.03906-1.10352
		c0.00879-1.45898,0.02148-3.66357,1.41113-5.90771c1.12012-1.80615,2.63477-2.39746,3.71875-2.57764h0.00684
		c0.01172-0.00244,0.4502-0.08691,1.08887-0.06836c0.14453,0.00781,0.24023,0.02002,0.25977,0.02246l0.06836-0.00293
		c1.21094,0.10156,2.93164,0.60986,4.06738,2.51758c1.36426,2.29297,1.40234,4.55029,1.42773,6.04395
		c0.00781,0.42578,0.01465,0.78955,0.04688,1.07568c0.00879,0.07764,0.01465,0.18359,0.02148,0.30762
		c0.05176,0.89258,0.15918,2.74951,1.56055,3.1001C20.49512,15.78271,21,16.2915,21,16.93164V18.10352z"
  />
)

const MESSAGE_1PT = (
  <path
    d="M9.50049,21l-0.22656-0.0542C9.10596,20.86035,9.00049,20.68799,9.00049,20.5v-3.14258H4.5c-1.6543,0-3-1.31396-3-2.92871
		v-8.5C1.5,4.28662,2.81787,3,4.5,3h15.00049C21.1543,3,22.5,4.31396,22.5,5.92871v8.5c0,1.5874-1.37354,2.92871-2.99951,2.92871
		H14.6626L9.79492,20.9043C9.70752,20.96777,9.60449,21,9.50049,21z M4.5,4c-1.12158,0-2,0.84717-2,1.92871v8.5
		c0,1.06348,0.89697,1.92871,2,1.92871h5.00049c0.27637,0,0.5,0.22363,0.5,0.5v2.65967l4.20508-3.06396
		c0.08545-0.06201,0.18848-0.0957,0.29443-0.0957h5.00049c1.06494,0,1.99951-0.90137,1.99951-1.92871v-8.5
		C21.5,4.86523,20.60303,4,19.50049,4H4.5z"
  />
)

// MENU

const DASHBOARD_1PT = (
  <path
    d="M19.77734,1.5H4.22168C2.7207,1.5,1.5,2.72168,1.5,4.22266v15.55469C1.5,21.27832,2.7207,22.5,4.22168,22.5h15.55566
  c1.50098,0,2.72266-1.22168,2.72266-2.72266V4.22266C22.5,2.72168,21.27832,1.5,19.77734,1.5z M4.22168,2.5h15.55566
  C20.72754,2.5,21.5,3.27246,21.5,4.22266v3.94434h-19V4.22266C2.5,3.27246,3.27246,2.5,4.22168,2.5z M2.5,19.77734V9.16699h6.77734
  V21.5H4.22168C3.27246,21.5,2.5,20.72754,2.5,19.77734z M19.77734,21.5h-9.5V9.16699H21.5v10.61035
  C21.5,20.72754,20.72754,21.5,19.77734,21.5z"
  />
)

const MENU_1PT = (
  <>
    <path
      d="M22.30176,6.52734c0-2.77197-1.79395-5.02734-4-5.02734s-4,2.25537-4,5.02734c0,2.55664,1.53223,4.65039,3.5,4.96387V22
  		c0,0.27637,0.22363,0.5,0.5,0.5s0.5-0.22363,0.5-0.5V11.49121C20.76953,11.17773,22.30176,9.08398,22.30176,6.52734z
  		 M18.30176,10.55469c-1.6543,0-3-1.80664-3-4.02734s1.3457-4.02734,3-4.02734s3,1.80664,3,4.02734
  		S19.95605,10.55469,18.30176,10.55469z"
    />
    <path
      d="M9.19824,1.5c-0.27637,0-0.5,0.22363-0.5,0.5v6.38574c0,1.00781-1.12109,1.85938-2.44824,1.85938H6.19824V2
  		c0-0.27637-0.22363-0.5-0.5-0.5s-0.5,0.22363-0.5,0.5v8.24512H5.14551c-1.32617,0-2.44727-0.85156-2.44727-1.85938V2
  		c0-0.27637-0.22363-0.5-0.5-0.5s-0.5,0.22363-0.5,0.5v6.38574c0,1.57666,1.54688,2.85938,3.44727,2.85938h0.05273V22
  		c0,0.27637,0.22363,0.5,0.5,0.5s0.5-0.22363,0.5-0.5V11.24512H6.25c1.90137,0,3.44824-1.28271,3.44824-2.85938V2
  		C9.69824,1.72363,9.47461,1.5,9.19824,1.5z"
    />
  </>
)

const ORDERING_1PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M20.046 7.43948C20.0231 7.16982 19.7978 6.96224 19.5272 6.96224H16.1164C16.1011 6.2408 16.0792 5.55801 16.0467 5.06246C15.9114 3.02125 14.8123 2.30998 14.3825 2.03219C14.2975 1.97724 14.1266 1.8938 13.8993 1.81239C13.4247 1.62618 12.9527 1.55597 12.5163 1.5631C12.0626 1.55496 11.5703 1.62924 11.0749 1.83173C10.8648 1.91009 10.7005 1.98945 10.6166 2.04338L10.6157 2.04396C10.2015 2.31204 9.10333 3.02274 8.96864 5.06246C8.93609 5.55802 8.91422 6.2408 8.89947 6.96225H5.58933C5.32026 6.96225 5.09545 7.1678 5.07052 7.43541L3.6479 22.8687C3.63467 23.0142 3.68299 23.1597 3.78167 23.2676C3.88035 23.3754 4.02022 23.4375 4.1667 23.4375H20.8334C20.9793 23.4375 21.1182 23.3764 21.2169 23.2686C21.3155 23.1617 21.3649 23.0183 21.3522 22.8728L20.046 7.43948ZM10.0083 5.13166C10.1039 3.67757 10.7697 3.18813 11.1476 2.94188C11.2727 2.8676 11.4004 2.81367 11.5275 2.76584C11.7661 2.68545 12.1094 2.60812 12.4965 2.59998C12.9105 2.60609 13.2726 2.69359 13.5209 2.78111C13.6378 2.8269 13.7553 2.87472 13.8703 2.94289C14.2299 3.17693 14.9099 3.66027 15.0071 5.13166C15.0381 5.60279 15.0589 6.26115 15.0737 6.96225H9.94165C9.9564 6.26115 9.97724 5.60279 10.0083 5.13166ZM6.06439 8.00423L4.73789 22.3955H20.2668L19.0486 8.00423H16.1326C16.1499 9.42678 16.1459 10.7354 16.1454 10.829C16.1443 11.1159 15.9114 11.3479 15.6245 11.3479H15.6225C15.3351 11.3469 15.1027 11.1129 15.1037 10.8249C15.1037 10.8238 15.1037 10.8196 15.1037 10.8125C15.104 10.6717 15.1069 9.38637 15.0905 8.00424H9.92487C9.9084 9.38631 9.91129 10.6716 9.91161 10.8124C9.91163 10.8196 9.91164 10.8238 9.91164 10.8249C9.91265 11.1129 9.68021 11.3469 9.39284 11.3479H9.3908C9.10394 11.3479 8.87099 11.1159 8.86997 10.829C8.86946 10.7353 8.8654 9.42677 8.88269 8.00423H6.06439Z"
  />
)

const REPORTS_1PT = (
  <>
    <path
      d="M10.8623,22.5c-5.16211,0-9.36182-4.19971-9.36182-9.3623c0-5.16162,4.19971-9.36133,9.36182-9.36133
		c0.27637,0,0.5,0.22363,0.5,0.5v8.36133h8.36133c0.27637,0,0.5,0.22363,0.5,0.5C20.22363,18.30029,16.02393,22.5,10.8623,22.5z
		 M10.3623,4.79102C5.9834,5.05029,2.50049,8.69482,2.50049,13.1377c0,4.61084,3.75098,8.3623,8.36182,8.3623
		c4.44287,0,8.0874-3.48291,8.34668-7.8623H10.8623c-0.27637,0-0.5-0.22363-0.5-0.5V4.79102z"
    />
    <path
      d="M22,10.37061h-7.87061c-0.27637,0-0.5-0.22363-0.5-0.5V2.00049c0-0.27637,0.22363-0.5,0.5-0.5
		c4.61572,0,8.37061,3.75488,8.37061,8.37012C22.5,10.14697,22.27637,10.37061,22,10.37061z M14.62939,9.37061h6.854
		c-0.24707-3.6665-3.18701-6.60645-6.854-6.85352V9.37061z"
    />
  </>
)

const PAYMENTS_1PT = (
  <>
    <path
      d="M11.3335,15.1665H6.09521c-0.27637,0-0.5,0.22363-0.5,0.5s0.22363,0.5,0.5,0.5h5.23828c0.27637,0,0.5-0.22363,0.5-0.5
  		S11.60986,15.1665,11.3335,15.1665z"
    />
    <path
      d="M20.15723,3.5H3.84277C2.55078,3.5,1.5,4.55078,1.5,5.84277v12.31445C1.5,19.44922,2.55078,20.5,3.84277,20.5h16.31445
  		c1.29199,0,2.34277-1.05078,2.34277-2.34277V5.84277C22.5,4.55078,21.44922,3.5,20.15723,3.5z M3.84277,4.5h16.31445
  		C20.89746,4.5,21.5,5.10254,21.5,5.84277v2.6416h-19v-2.6416C2.5,5.10254,3.10254,4.5,3.84277,4.5z M20.15723,19.5H3.84277
  		C3.10254,19.5,2.5,18.89746,2.5,18.15723V9.48438h19v8.67285C21.5,18.89746,20.89746,19.5,20.15723,19.5z"
    />
  </>
)

const MARKETPLACE_1PT = (
  <path
    d="M22.5,6.92383c0-0.27637-0.22363-0.5-0.5-0.5h-4.55322c0.24316-0.47363,0.38525-0.99512,0.38525-1.53711
  c0-1.86719-1.52344-3.38672-3.39648-3.38672c-1.87256,0-3.396,1.51953-3.396,3.38672c0,0.5459,0.14307,1.06543,0.38574,1.53711
  H6.87158c-0.27637,0-0.5,0.22363-0.5,0.5v4.50586c-0.44678-0.21582-0.93311-0.3457-1.43896-0.35059
  C3.02734,11.07227,1.51953,12.56152,1.5,14.42969c-0.00928,0.9043,0.33496,1.75879,0.97021,2.40527
  c0.63477,0.64648,1.4834,1.00684,2.39014,1.0166h0.03613c0.51855,0,1.01758-0.12988,1.4751-0.35059V22
  c0,0.27637,0.22363,0.5,0.5,0.5H22c0.27637,0,0.5-0.22363,0.5-0.5v-5.37109l-0.00195-0.01074l-0.02246-0.11035l-0.01904-0.08008
  l-0.03955-0.05859l-0.07324-0.10059l-0.00586-0.00781l-0.05566-0.0332l-0.10303-0.0625l-0.1001-0.02051L22,16.12891
  l-0.01172,0.00195l-0.10205,0.02148l-0.08789,0.02051l-0.05615,0.03809l-0.10254,0.07422l-0.00781,0.00586
  c-0.36523,0.39746-0.79541,0.53809-1.64502,0.53809c-1.32129,0-2.39648-1.07031-2.39648-2.38574
  c0-1.31641,1.0752-2.38672,2.39648-2.38672c0.62354,0,1.21436,0.2373,1.66357,0.66797
  c0.19971,0.19238,0.51562,0.18359,0.70703-0.01367c0.11084-0.11621,0.14746-0.27051,0.12598-0.41797L22.5,12.21094V6.92383z
   M16.59033,14.44336c0,1.86719,1.52344,3.38574,3.39648,3.38574c0.50684,0,1.02637-0.04102,1.51318-0.24707V21.5H7.37158v-4.88281
  c0-0.27637-0.22363-0.5-0.5-0.5l-0.01172,0.00195c-0.12891-0.00781-0.26123,0.03223-0.36621,0.125
  c-0.44629,0.39844-1.04834,0.61621-1.62256,0.60742c-0.64062-0.00684-1.23975-0.26172-1.6875-0.71777
  C2.73633,15.67871,2.49316,15.07715,2.5,14.43945c0.01416-1.30664,1.08691-2.36035,2.39648-2.36035h0.02539
  c0.59424,0.00586,1.1626,0.23047,1.6001,0.63184c0.13525,0.12402,0.31982,0.15137,0.48438,0.10059
  c0.20947-0.05957,0.36523-0.24414,0.36523-0.47266V7.42383h4.97363l0.01123-0.00195l0.03125,0.00586
  c0.12842,0,0.25635-0.04883,0.354-0.14746c0.19531-0.19531,0.19434-0.51172-0.00098-0.70703
  c-0.45215-0.4502-0.70117-1.04883-0.70117-1.68652c0-1.31641,1.07471-2.38672,2.396-2.38672s2.39648,1.07031,2.39648,2.38672
  c0,0.59473-0.22119,1.16504-0.62305,1.60547l-0.10156,0.16895c-0.04883,0.07715-0.08496,0.16406-0.08496,0.2627
  c0,0.27637,0.22363,0.5,0.5,0.5H21.5v4.00293c-0.4668-0.2334-0.97852-0.37012-1.51318-0.37012
  C18.11377,11.05664,16.59033,12.57617,16.59033,14.44336z"
  />
)

const SETTINGS_1PT = (
  <path
    d="M14,22.5h-4c-0.27637,0-0.5-0.22363-0.5-0.5v-2.01562c-0.48242-0.14746-0.94727-0.33594-1.3877-0.56152l-1.41602,1.41602
		c-0.09375,0.09375-0.2207,0.14648-0.35352,0.14648s-0.25977-0.05273-0.35352-0.14648l-2.82715-2.82812
		c-0.19531-0.19531-0.19531-0.51172,0-0.70703l1.37695-1.37793c-0.24805-0.45508-0.4541-0.93262-0.61523-1.4248H2
		c-0.27637,0-0.5-0.22363-0.5-0.5V10c0-0.27637,0.22363-0.5,0.5-0.5h1.84277c0.15332-0.52637,0.35547-1.03418,0.60352-1.51758
		L3.16211,6.69727c-0.19531-0.19531-0.19531-0.51172,0-0.70703l2.82715-2.82812c0.19531-0.19531,0.51172-0.19531,0.70703,0
		L7.94336,4.4082C8.43945,4.1377,8.96094,3.91797,9.5,3.75195V2c0-0.27637,0.22363-0.5,0.5-0.5h4c0.27637,0,0.5,0.22363,0.5,0.5
		v1.75195c0.53906,0.16602,1.06055,0.38574,1.55762,0.65625l1.24609-1.24609c0.19531-0.19531,0.51172-0.19531,0.70703,0
		l2.82812,2.82812c0.19531,0.19531,0.19531,0.51172,0,0.70703l-1.28516,1.28516c0.24805,0.48242,0.4502,0.99023,0.60352,1.51758H22
		c0.27637,0,0.5,0.22363,0.5,0.5v4.00098c0,0.27637-0.22363,0.5-0.5,0.5h-1.9248c-0.16113,0.49316-0.36719,0.9707-0.61426,1.4248
		l1.37793,1.37793c0.09375,0.09375,0.14648,0.2207,0.14648,0.35352s-0.05273,0.25977-0.14648,0.35352l-2.82812,2.82812
		c-0.19531,0.19531-0.51172,0.19531-0.70703,0l-1.41699-1.41602c-0.44043,0.22559-0.9043,0.41406-1.38672,0.56152V22
		C14.5,22.27637,14.27637,22.5,14,22.5z M10.5,21.5h3v-1.89355c0-0.22852,0.1543-0.42676,0.375-0.48438
		c0.65723-0.16895,1.28027-0.42188,1.85156-0.75098c0.19238-0.11328,0.44043-0.08105,0.60254,0.08008l1.32812,1.32715
		l2.12109-2.12109l-1.2959-1.29492c-0.16309-0.16309-0.19336-0.41602-0.07422-0.61328
		c0.35645-0.58594,0.62988-1.21875,0.81445-1.88184c0.05957-0.2168,0.25684-0.36621,0.48145-0.36621H21.5V10.5h-1.72754
		c-0.23047,0-0.43164-0.1582-0.48633-0.38281c-0.16797-0.69824-0.43262-1.3623-0.78418-1.97461
		c-0.1123-0.19531-0.08008-0.44238,0.08008-0.60254l1.19629-1.19629l-2.12109-2.12109l-1.16309,1.16309
		c-0.16406,0.16309-0.41602,0.19434-0.6123,0.07422c-0.62598-0.37891-1.30078-0.66309-2.00684-0.8457
		C13.6543,4.55664,13.5,4.35742,13.5,4.12988V2.5h-3v1.62988c0,0.22754-0.1543,0.42676-0.375,0.48438
		c-0.70605,0.18164-1.37988,0.4668-2.00586,0.8457C7.92285,5.58008,7.6709,5.54883,7.50684,5.38574L6.34277,4.22266L4.22266,6.34375
		l1.19434,1.19629C5.57715,7.7002,5.60938,7.94629,5.49707,8.14258c-0.35156,0.6123-0.61523,1.27734-0.7832,1.97461
		C4.65918,10.3418,4.45801,10.5,4.22754,10.5H2.5v3.00098h1.7959c0.22461,0,0.42188,0.15039,0.48242,0.36719
		c0.18164,0.65918,0.45508,1.29297,0.81348,1.88086c0.11914,0.19727,0.08887,0.4502-0.07422,0.61328l-1.29492,1.29492
		l2.12012,2.12109l1.32715-1.32715c0.16113-0.16113,0.40918-0.19238,0.60254-0.08008
		c0.57031,0.3291,1.19336,0.58105,1.85254,0.75098c0.2207,0.05762,0.375,0.25586,0.375,0.48438V21.5z M12.00098,16.10059
		c-2.26172,0-4.10156-1.83984-4.10156-4.10059s1.83984-4.10059,4.10156-4.10059c2.25977,0,4.09863,1.83984,4.09863,4.10059
		S14.26074,16.10059,12.00098,16.10059z M12.00098,8.89941c-1.70996,0-3.10156,1.39062-3.10156,3.10059
		s1.3916,3.10059,3.10156,3.10059c1.70898,0,3.09863-1.39062,3.09863-3.10059S13.70996,8.89941,12.00098,8.89941z"
  />
)

const STAFF_1PT = (
  <path
    d="M20.875,16.97754c-0.93164-3.61133-3.53711-5.19482-4.70605-5.73975c0.97559-1.03564,1.57715-2.42139,1.57715-3.94678
  c0-3.19336-2.61816-5.79102-5.83594-5.79102S6.0752,4.09766,6.0752,7.29102c0,1.5542,0.62305,2.96387,1.62988,4.00488
  c-1.22266,0.59814-3.68164,2.19824-4.58105,5.68164c-0.27734,1.07764-0.0918,2.0415,0.55371,2.86475
  c1.60645,2.05127,5.74316,2.62793,8.1875,2.65527l0.00293,0.00049l0.07031,0.00049L12.05176,22.5l0.16406-0.00098l0.02441-0.00537
  c2.87793-0.02637,6.58105-0.71826,8.08008-2.63232C20.96875,19.03418,21.15527,18.06396,20.875,16.97754z M11.91016,2.5
  c2.66699,0,4.83594,2.14941,4.83594,4.79102s-2.16895,4.79102-4.83594,4.79102c-2.66602,0-4.83496-2.14941-4.83496-4.79102
  S9.24414,2.5,11.91016,2.5z M19.5332,19.24463c-1.21191,1.54688-4.64062,2.24268-7.41504,2.25244
  c-2.81934,0.01221-6.4082-0.68115-7.65332-2.27148c-0.44922-0.57373-0.57129-1.22705-0.37207-1.99805
  c0.92969-3.60547,3.72949-4.92676,4.45312-5.21289c0.95215,0.66992,2.11133,1.06738,3.36426,1.06738
  c1.28027,0,2.46191-0.41602,3.4248-1.1123c0.4502,0.16064,3.57812,1.40625,4.57129,5.25781
  C20.10742,18.00732,19.98535,18.66699,19.5332,19.24463z"
  />
)

const HELP_1PT = (
  <>
    <path
      d="M12,2c5.51416,0,10,4.48584,10,10s-4.48584,10-10,10S2,17.51416,2,12S6.48584,2,12,2 M12,1C5.9248,1,1,5.9248,1,12
  		s4.9248,11,11,11s11-4.9248,11-11S18.0752,1,12,1z"
    />
    <path
      d="M11.86621,14.88232c-0.27637,0-0.5-0.22363-0.5-0.5v-1.43604c0-0.23926,0.17139-0.4458,0.40625-0.49121
  		c0.08984-0.01758,2.20117-0.44775,2.34277-2.29492c-0.00146-1.10156-0.49463-1.8125-1.46777-2.07471
  		c-1.28223-0.34717-2.50195,0.22754-2.90186,1.36328c-0.0918,0.26074-0.37891,0.39551-0.6377,0.30566
  		C8.84766,9.6626,8.71045,9.37695,8.80225,9.1167c0.56836-1.61621,2.33203-2.47461,4.10596-1.99707
  		c1.42236,0.38379,2.20557,1.47705,2.20557,3.07861c-0.14209,1.87402-1.68359,2.80811-2.74756,3.13721v1.04688
  		C12.36621,14.65869,12.14258,14.88232,11.86621,14.88232z"
    />
    <circle cx="11.94385" cy="17.6665" r="0.75" />
  </>
)

const GUEST_ENGAGEMENT_1PT = (
  <>
    <path d="M11.64 21.9601C11.7333 22.0588 11.8642 22.1133 12 22.1101C12.13 22.124 12.26 22.0843 12.36 22.0001L21.24 12.8001C23.5816 10.3341 23.5816 6.46613 21.24 4.00011C20.1443 2.8172 18.6122 2.13427 17 2.11011C15.3661 2.1142 13.8078 2.79911 12.7 4.00011L12 4.73011L11.3 4.00011C10.1978 2.81789 8.65702 2.14243 7.04077 2.13296C5.42452 2.1235 3.87589 2.78088 2.75998 3.95011C0.418384 6.41613 0.418384 10.2841 2.75998 12.7501L11.64 21.9601ZM3.47998 4.64011C4.3978 3.6746 5.6679 3.12254 6.99998 3.11011C8.35293 3.10618 9.64778 3.65957 10.58 4.64011L11.64 5.75011C11.8432 5.93814 12.1568 5.93814 12.36 5.75011L13.46 4.64011C14.3812 3.66767 15.6605 3.1148 17 3.11011C18.3321 3.12254 19.6022 3.6746 20.52 4.64011C22.4921 6.71648 22.4921 9.97374 20.52 12.0501L12 20.8901L3.47998 12.0501C1.50791 9.97374 1.50791 6.71648 3.47998 4.64011Z" />
  </>
)

const DOLLAR_SIGN_1PT = (
  <>
    <path d="M12 1C5.93457 1 1 5.93457 1 12C1 18.0654 5.93457 23 12 23C18.0654 23 23 18.0654 23 12C23 5.93457 18.0654 1 12 1ZM12 21C7.0376 21 3 16.9629 3 12C3 7.03711 7.0376 3 12 3C16.9624 3 21 7.03711 21 12C21 16.9629 16.9624 21 12 21ZM9.86133 10.1709C9.86133 10.6279 10.2334 11 10.6909 11H13.3042C14.8643 11 16.1338 12.2695 16.1338 13.8291C16.1338 15.3896 14.8643 16.6592 13.3042 16.6592H12.9976V17.75C12.9976 18.3027 12.5498 18.75 11.9976 18.75C11.4453 18.75 10.9976 18.3027 10.9976 17.75V16.6592H8.86133C8.30908 16.6592 7.86133 16.2119 7.86133 15.6592C7.86133 15.1065 8.30908 14.6592 8.86133 14.6592H13.3042C13.7617 14.6592 14.1338 14.2871 14.1338 13.8291C14.1338 13.3721 13.7617 13 13.3042 13H10.6909C9.13086 13 7.86133 11.7305 7.86133 10.1709C7.86133 8.61035 9.13086 7.34082 10.6909 7.34082H10.9976V6.25C10.9976 5.69727 11.4453 5.25 11.9976 5.25C12.5498 5.25 12.9976 5.69727 12.9976 6.25V7.34082H14.6113C15.1636 7.34082 15.6113 7.78809 15.6113 8.34082C15.6113 8.89355 15.1636 9.34082 14.6113 9.34082H10.6909C10.2334 9.34082 9.86133 9.71289 9.86133 10.1709Z" />
  </>
)

const PERCENTAGE_SIGN_1PT = (
  <>
    <g>
      <path
        d="M6.06543,11.54785C3.27246,11.54785,1,9.18164,1,6.27344S3.27246,1,6.06543,1s5.06494,2.36523,5.06494,5.27344
        S8.8584,11.54785,6.06543,11.54785z M6.06543,3C4.375,3,3,4.46875,3,6.27344C3,8.0791,4.375,9.54785,6.06543,9.54785
        c1.68994,0,3.06494-1.46875,3.06494-3.27441C9.13037,4.46875,7.75537,3,6.06543,3z"
      />
    </g>
    <g>
      <path
        d="M17.8418,23c-2.84424,0-5.1582-2.41016-5.1582-5.37207s2.31396-5.37207,5.1582-5.37207S23,14.66602,23,17.62793
        S20.68604,23,17.8418,23z M17.8418,14.25586c-1.74121,0-3.1582,1.5127-3.1582,3.37207S16.10059,21,17.8418,21
        S21,19.4873,21,17.62793S19.58301,14.25586,17.8418,14.25586z"
      />
    </g>
    <g>
      <path
        d="M4.64453,22.49414c-0.2085,0-0.41846-0.06445-0.59863-0.19922c-0.44189-0.33105-0.53174-0.95801-0.20068-1.40039
        L17.0415,3.27637c0.33105-0.44043,0.95801-0.5293,1.3999-0.20117c0.44189,0.33105,0.53174,0.95801,0.20068,1.40039L5.4458,22.09375
        C5.24951,22.35547,4.94873,22.49414,4.64453,22.49414z"
      />
    </g>
  </>
)

const LOGOUT_1PT = (
  <>
    <path
      d="M8.70996,21.5H4.23633C3.2793,21.5,2.5,20.72754,2.5,19.77734V4.22266C2.5,3.27246,3.2793,2.5,4.23633,2.5h4.47363
  		c0.27637,0,0.5-0.22363,0.5-0.5s-0.22363-0.5-0.5-0.5H4.23633C2.72754,1.5,1.5,2.72168,1.5,4.22266v15.55469
  		C1.5,21.27832,2.72754,22.5,4.23633,22.5h4.47363c0.27637,0,0.5-0.22363,0.5-0.5S8.98633,21.5,8.70996,21.5z"
    />
    <path
      d="M22.35156,11.7627l-5.92188-5.86816c-0.19434-0.19434-0.5127-0.19238-0.70703,0.00391
  		c-0.19434,0.19531-0.19238,0.5127,0.00391,0.70703l5.06445,5.01855H8.99219c-0.27637,0-0.5,0.22363-0.5,0.5s0.22363,0.5,0.5,0.5
  		H20.7793l-5.05273,5.00684c-0.19629,0.19434-0.19824,0.51172-0.00391,0.70703c0.09766,0.09863,0.22656,0.14844,0.35547,0.14844
  		c0.12695,0,0.25488-0.04785,0.35156-0.14453l5.92188-5.86816C22.44629,12.37988,22.5,12.25195,22.5,12.11816
  		S22.44629,11.85645,22.35156,11.7627z"
    />
  </>
)

// MISC

const EYE_1PT = (
  <>
    <g>
      <path d="M12,20.5C4.77,20.5.72,12.56.55,12.22a.49.49,0,0,1,0-.44C.72,11.44,4.77,3.5,12,3.5s11.28,7.94,11.45,8.28a.49.49,0,0,1,0,.44C23.28,12.56,19.23,20.5,12,20.5ZM1.57,12C2.27,13.27,6,19.5,12,19.5s9.73-6.23,10.43-7.5C21.73,10.73,18,4.5,12,4.5S2.27,10.73,1.57,12Z" />
    </g>
    <g>
      <path d="M12,15.5A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Zm0-6A2.5,2.5,0,1,0,14.5,12,2.5,2.5,0,0,0,12,9.5Z" />
    </g>
  </>
)

const EYE_2PT = (
  <>
    <g>
      <path d="M12,20.5c-7.21,0-11.22-7.72-11.39-8a1,1,0,0,1,0-.9c.17-.33,4.18-8,11.39-8s11.22,7.72,11.39,8.05a1,1,0,0,1,0,.9C23.22,12.78,19.21,20.5,12,20.5ZM2.65,12c.92,1.54,4.28,6.5,9.35,6.5s8.43-4.95,9.35-6.5c-.92-1.54-4.28-6.5-9.35-6.5S3.57,10.45,2.65,12Z" />
    </g>
    <g>
      <path d="M12,15.81A3.81,3.81,0,1,1,15.86,12,3.84,3.84,0,0,1,12,15.81Zm0-5.62A1.81,1.81,0,1,0,13.86,12,1.83,1.83,0,0,0,12,10.19Z" />
    </g>
  </>
)

const EYE_CROSS_1PT = (
  <path d="M23.45,11.78a18.47,18.47,0,0,0-5.6-6.41L19,3.8a.5.5,0,1,0-.79-.6L17,4.85A10.2,10.2,0,0,0,12,3.5C4.77,3.5.72,11.44.55,11.78a.49.49,0,0,0,0,.44,18.38,18.38,0,0,0,5.8,6.53L5.25,20.2a.49.49,0,0,0,.4.8A.49.49,0,0,0,6,20.8l1.18-1.53A10.09,10.09,0,0,0,12,20.5c7.23,0,11.28-7.94,11.45-8.28A.49.49,0,0,0,23.45,11.78ZM1.57,12C2.27,10.73,6,4.5,12,4.5a9,9,0,0,1,4.36,1.17L13.79,9A3.43,3.43,0,0,0,12,8.5a3.49,3.49,0,0,0-2.4,6L7,17.94A17.64,17.64,0,0,1,1.57,12Zm12.9,0A2.5,2.5,0,0,1,12,14.5a2.57,2.57,0,0,1-1-.19l2.93-3.83A2.44,2.44,0,0,1,14.47,12Zm-4.29,1.74A2.47,2.47,0,0,1,9.47,12,2.5,2.5,0,0,1,12,9.5a2.39,2.39,0,0,1,1.21.33ZM12,19.5a9,9,0,0,1-4.15-1.05l2.55-3.34A3.44,3.44,0,0,0,12,15.5a3.5,3.5,0,0,0,3.5-3.5,3.46,3.46,0,0,0-.91-2.33l2.66-3.48A17.82,17.82,0,0,1,22.43,12C21.73,13.27,18,19.5,12,19.5Z" />
)
const EYE_CROSS_2PT = (
  <path d="M23.39,11.55a18.34,18.34,0,0,0-5.12-5.91l1.17-1.53a1,1,0,0,0-1.59-1.22L16.55,4.6A10.23,10.23,0,0,0,12,3.5C4.78,3.5.77,11.22.6,11.55a1,1,0,0,0,0,.9A18.26,18.26,0,0,0,5.91,18.5L4.85,19.89A1,1,0,0,0,5,21.29a1,1,0,0,0,.6.21,1,1,0,0,0,.8-.39L7.67,19.5a10.09,10.09,0,0,0,4.32,1c7.22,0,11.23-7.72,11.4-8A1,1,0,0,0,23.39,11.55ZM2.64,12C3.56,10.45,6.91,5.5,12,5.5a8,8,0,0,1,3.31.73L13.55,8.52A3.86,3.86,0,0,0,8.13,12,3.73,3.73,0,0,0,9,14.42l-1.9,2.49A16.31,16.31,0,0,1,2.64,12Zm11.22,0A1.84,1.84,0,0,1,12,13.81l1.76-2.3A1.62,1.62,0,0,1,13.86,12Zm-3.73,0A1.84,1.84,0,0,1,12,10.19l.26,0-1.94,2.55A1.77,1.77,0,0,1,10.13,12ZM12,18.5a8,8,0,0,1-3.07-.64l1.75-2.29a3.91,3.91,0,0,0,1.32.24A3.85,3.85,0,0,0,15.86,12a3.7,3.7,0,0,0-.75-2.23l1.94-2.54A16.51,16.51,0,0,1,21.35,12C20.43,13.55,17.08,18.5,12,18.5Z" />
)

const ERROR_1PT = (
  <>
    <path
      d="M12,1C5.9248,1,1,5.9248,1,11.99951C1,18.07568,5.9248,23,12,23c6.07617,0,11-4.92432,11-11.00049
		C23,5.9248,18.07617,1,12,1z M12,22C6.48633,22,2,17.51367,2,11.99951C2,6.48584,6.48633,2,12,2s10,4.48584,10,9.99951
		C22,17.51367,17.51367,22,12,22z"
    />
    <path
      d="M15.46777,8.66602c-0.19531-0.19531-0.51172-0.19531-0.70703,0l-2.60645,2.60693L9.54688,8.66602
		c-0.19531-0.19531-0.51172-0.19531-0.70703,0s-0.19531,0.51172,0,0.70703l2.60742,2.60693l-2.60742,2.60693
		c-0.19531,0.19531-0.19531,0.51172,0,0.70703c0.09766,0.09766,0.22559,0.14648,0.35352,0.14648s0.25586-0.04883,0.35352-0.14648
		l2.60742-2.60693l2.60645,2.60693c0.09766,0.09766,0.22559,0.14648,0.35352,0.14648s0.25586-0.04883,0.35352-0.14648
		c0.19531-0.19531,0.19531-0.51172,0-0.70703l-2.60645-2.60693l2.60645-2.60693C15.66309,9.17773,15.66309,8.86133,15.46777,8.66602
		z"
    />
  </>
)

const ERROR_2PT = (
  <>
    <g>
      <path
        d="M11.99969,1C5.92505,1,1,5.92505,1,11.99963C1,18.07562,5.92505,23,11.99969,23C18.07629,23,23,18.07562,23,11.99963
        C23,5.92505,18.07629,1,11.99969,1z M11.99969,21C7.03723,21,3,16.96246,3,11.99963C3,7.03723,7.03723,3,11.99969,3
        C16.96246,3,21,7.03723,21,11.99963C21,16.96246,16.96246,21,11.99969,21z"
      />
      <path
        d="M15.82129,8.3125c-0.39062-0.39062-1.02344-0.39062-1.41406,0l-2.25342,2.25342L9.90039,8.3125
        c-0.39062-0.39062-1.02344-0.39062-1.41406,0s-0.39062,1.02344,0,1.41406l2.25342,2.25342L8.48633,14.2334
        c-0.39062,0.39062-0.39062,1.02344,0,1.41406c0.19531,0.19531,0.45117,0.29297,0.70703,0.29297s0.51172-0.09766,0.70703-0.29297
        l2.25342-2.25342l2.25342,2.25342c0.19531,0.19531,0.45117,0.29297,0.70703,0.29297s0.51172-0.09766,0.70703-0.29297
        c0.39062-0.39062,0.39062-1.02344,0-1.41406l-2.25342-2.25342l2.25342-2.25342C16.21191,9.33594,16.21191,8.70312,15.82129,8.3125z
        "
      />
    </g>
  </>
)

const SUCCESS_1PT = (
  <>
    <path
      d="M12,2c5.51367,0,10,4.48584,10,9.99951C22,17.51367,17.51367,22,12,22C6.48535,22,2,17.51367,2,11.99951
		C2,6.48584,6.48535,2,12,2 M12,1C5.9248,1,1,5.92529,1,11.99951C1,18.07568,5.9248,23,12,23c6.07617,0,11-4.92432,11-11.00049
		C23,5.92529,18.07617,1,12,1z"
    />
    <path
      d="M10.54883,15.13965c-0.12793,0-0.25586-0.04883-0.35352-0.14648L7.77734,12.5752
		c-0.19531-0.19531-0.19531-0.51172,0-0.70703s0.51172-0.19531,0.70703,0l2.06445,2.06445l4.9668-4.96631
		c0.19531-0.19531,0.51172-0.19531,0.70703,0s0.19531,0.51172,0,0.70703l-5.32031,5.31982
		C10.80469,15.09082,10.67676,15.13965,10.54883,15.13965z"
    />
  </>
)
const DELETE_1PT = (
  <>
    <path
      d="M9.94189,18.20117c0.27637,0,0.5-0.22363,0.5-0.5v-8c0-0.27637-0.22363-0.5-0.5-0.5s-0.5,0.22363-0.5,0.5v8
  		C9.44189,17.97754,9.66553,18.20117,9.94189,18.20117z"
    />
    <path
      d="M14.44043,18.20117c0.27637,0,0.5-0.22363,0.5-0.5v-8c0-0.27637-0.22363-0.5-0.5-0.5s-0.5,0.22363-0.5,0.5v8
  		C13.94043,17.97754,14.16406,18.20117,14.44043,18.20117z"
    />
    <path
      d="M22,4.77832h-5.15723V2.88281C16.84277,1.77441,16.08301,1,14.99561,1H9.61816C8.48926,1,7.6377,1.80957,7.6377,2.88281
  		v1.89551H2c-0.27637,0-0.5,0.22363-0.5,0.5s0.22363,0.5,0.5,0.5h2.29688v14.75635C4.29688,21.80176,5.41455,23,6.59717,23h11.09375
  		c1.27637,0,1.96533-1.27002,1.96533-2.46533V5.77832H22c0.27637,0,0.5-0.22363,0.5-0.5S22.27637,4.77832,22,4.77832z
  		 M8.6377,2.88281C8.6377,2.30322,9.13086,2,9.61816,2h5.37744c0.53027,0,0.84717,0.33008,0.84717,0.88281v1.89551H8.6377V2.88281z
  		 M18.65625,20.53467c0,0.72021-0.36084,1.46533-0.96533,1.46533H6.59717c-0.63184,0-1.30029-0.75342-1.30029-1.46533V5.77832
  		h13.35938V20.53467z"
    />
  </>
)

const DELETE_2PT = (
  <>
    <path
      d="M9.94189,18.70117c0.55225,0,1-0.44775,1-1v-8c0-0.55225-0.44775-1-1-1s-1,0.44775-1,1v8
  		C8.94189,18.25342,9.38965,18.70117,9.94189,18.70117z"
    />
    <path
      d="M14.44043,18.70117c0.55225,0,1-0.44775,1-1v-8c0-0.55225-0.44775-1-1-1s-1,0.44775-1,1v8
  		C13.44043,18.25342,13.88818,18.70117,14.44043,18.70117z"
    />
    <path
      d="M22,4.27832h-4.65723V2.88281c0-1.38086-0.9873-2.38281-2.34717-2.38281H9.61816
  		C8.22705,0.5,7.1377,1.54688,7.1377,2.88281v1.39551H2c-0.55225,0-1,0.44775-1,1s0.44775,1,1,1h1.79688v14.25635
  		c0,1.55176,1.33496,2.96533,2.80029,2.96533h11.09375c1.40527,0,2.46533-1.2749,2.46533-2.96533V6.27832H22
  		c0.55225,0,1-0.44775,1-1S22.55225,4.27832,22,4.27832z M9.1377,2.88281C9.1377,2.51074,9.56885,2.5,9.61816,2.5h5.37744
  		c0.16797,0,0.34717,0.02979,0.34717,0.38281v1.39551H9.1377V2.88281z M18.15625,20.53467
  		c0,0.49609-0.22607,0.96533-0.46533,0.96533H6.59717c-0.33057,0-0.80029-0.49902-0.80029-0.96533V6.27832h12.35938V20.53467z"
    />
  </>
)

const SEARCH_1PT = (
  <>
    <path
      d="M22.35059,21.64355l-4.15723-4.0918l-0.09863-0.06396c1.69531-1.75146,2.74219-4.1167,2.74219-6.72607
  		C20.83691,5.37012,16.39648,1,10.91797,1C5.44043,1,1,5.37012,1,10.76172c0,5.39111,4.44043,9.76123,9.91797,9.76123
  		c2.4834,0,4.74707-0.90381,6.48633-2.3877l0.08789,0.12939l4.15723,4.0918C21.74707,22.45215,21.87305,22.5,22,22.5
  		c0.12891,0,0.25879-0.0498,0.35645-0.14941C22.5498,22.15381,22.54785,21.8374,22.35059,21.64355z M10.91797,19.52295
  		C6.00098,19.52295,2,15.59277,2,10.76172C2,5.93018,6.00098,2,10.91797,2c4.91797,0,8.91895,3.93018,8.91895,8.76172
  		C19.83691,15.59277,15.83594,19.52295,10.91797,19.52295z"
    />
  </>
)

const SORT_2PT = (
  <>
    <g>
      <circle cx="6.45009" cy="3.02784" r="2.02784" />
    </g>
    <g>
      <circle cx="17.54991" cy="3.02784" r="2.02784" />
    </g>
    <g>
      <circle cx="6.45009" cy="12.00514" r="2.02784" />
    </g>
    <g>
      <circle cx="17.54991" cy="12.00514" r="2.02784" />
    </g>
    <g>
      <circle cx="6.45009" cy="20.98244" r="2.02784" />
    </g>
    <g>
      <circle cx="17.54991" cy="20.98244" r="2.02784" />
    </g>
  </>
)

const SEARCH_2PT = (
  <path
    d="M22.70117,21.28711l-4.15723-4.0918c-0.03308-0.03259-0.07562-0.04602-0.11182-0.07306
    	c1.49524-1.70911,2.40485-3.92847,2.40485-6.36078C20.83698,5.37036,16.3963,1,10.91846,1C5.44067,1,1,5.37036,1,10.76147
    	c0,5.39117,4.44067,9.76154,9.91846,9.76154c2.30463,0,4.42004-0.78046,6.10406-2.07819
    	c0.03876,0.05957,0.06641,0.12439,0.11908,0.17627l4.15723,4.0918C21.49316,22.9043,21.74707,23,22,23
    	c0.25879,0,0.5166-0.09961,0.71289-0.29834C23.10059,22.30811,23.09473,21.6748,22.70117,21.28711z M10.91846,18.52301
    	C6.55225,18.52301,3,15.0412,3,10.76147C3,6.48181,6.55225,3,10.91846,3c4.36627,0,7.91852,3.48181,7.91852,7.76147
    	C18.83698,15.0412,15.28473,18.52301,10.91846,18.52301z"
  />
)

const ARROW_DOWN_2PT = (
  <path
    d="M22.21045,11.88623c-0.38867-0.39307-1.02148-0.39404-1.41406-0.00684l-7.80518,7.73108V2c0-0.55225-0.44775-1-1-1
    	s-1,0.44775-1,1v17.59308l-7.7876-7.71368c-0.39209-0.38916-1.02588-0.38721-1.41406,0.00684
    	c-0.38867,0.39209-0.38574,1.02539,0.00684,1.41406l9.46991,9.38031c0.09961,0.10608,0.21918,0.19025,0.35663,0.245
    	c0.05261,0.02142,0.10925,0.02106,0.16418,0.0332C11.85516,22.97345,11.91882,23,11.99121,23
    	c0.00153,0,0.00275-0.00085,0.00427-0.00085S11.99847,23,12,23c0.25439,0,0.50879-0.09668,0.70361-0.28955l9.5-9.41016
    	C22.59619,12.91162,22.59912,12.27832,22.21045,11.88623z"
  />
)
const CHECKMARK_1PT = (
  <path
    d="M8.25,19.5c-0.13428,0-0.2627-0.05371-0.35693-0.14941l-6.25-6.36377c-0.19336-0.19727-0.19043-0.51367,0.00635-0.70752
		c0.19678-0.19287,0.5127-0.19092,0.70752,0.00635L8.25,18.28662L21.64307,4.64941c0.19287-0.19727,0.50928-0.19922,0.70752-0.00635
		c0.19678,0.19385,0.19971,0.51025,0.00635,0.70752l-13.75,14C8.5127,19.44629,8.38428,19.5,8.25,19.5z"
  />
)

const CHECKMARK_2PT = (
  <path
    d="M8.25,20c-0.26855,0-0.52539-0.10791-0.71338-0.29932l-6.25-6.36377c-0.38721-0.39404-0.38135-1.02734,0.0127-1.41406
		c0.39404-0.3877,1.02637-0.38184,1.41406,0.0127L8.25,17.57275L21.28662,4.29932c0.38721-0.39453,1.01904-0.40039,1.41406-0.0127
		c0.39404,0.38672,0.3999,1.02002,0.0127,1.41406l-13.75,14C8.77539,19.89209,8.51855,20,8.25,20z"
  />
)

const CHECKBOX_SELECTED_1PT = (
  <path
    d="M19.80762,1.08121c-0.26025-0.05328-0.52966-0.08124-0.80566-0.08124h-0.00006H4.99744
  	c-2.20789,0-3.99774,1.78986-3.99774,3.9978v14.00446c0,2.20789,1.78986,3.99774,3.99774,3.99774h14.00446h0.00006
  	c0.276,0,0.54541-0.02795,0.80566-0.08124c1.82172-0.3728,3.19208-1.98462,3.19208-3.9165V4.99777
  	C22.99969,3.06583,21.62933,1.45401,19.80762,1.08121z M18.33063,8.2778l-8.125,8.27271
  	c-0.11108,0.1131-0.26282,0.17688-0.42151,0.17688s-0.31049-0.06378-0.42157-0.17688l-3.69318-3.76044
  	c-0.22882-0.23279-0.22534-0.60706,0.00751-0.83557c0.23285-0.22906,0.60651-0.22559,0.83557,0.00751l3.27167,3.33105
  	l7.70343-7.84332c0.22876-0.23315,0.60211-0.23663,0.83557-0.00751C18.55597,7.67075,18.55939,8.04495,18.33063,8.2778z"
  />
)

const CHECKBOX_SEMI_SELECTED_1PT = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="mask0_492_61952" maskUnits="userSpaceOnUse" x="0" y="1" width="23" height="22">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.9974 1H19.0019C21.2098 1 22.9996 2.78986 22.9996 4.99777V19.0022C22.9996 21.2101 21.2098 23 19.0019 23H4.9974C2.78949 23 0.999634 21.2101 0.999634 19.0022V4.99777C0.999634 2.78986 2.78949 1 4.9974 1ZM21.9996 4.99777C21.9996 3.34479 20.6548 2 19.0019 2H4.9974C3.34442 2 1.99963 3.34479 1.99963 4.99777V19.0022C1.99963 20.6552 3.34442 22 4.9974 22H19.0019C20.6548 22 21.9996 20.6552 21.9996 19.0022V4.99777Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_492_61952)">
      <rect width="24" height="24" fill="#666666" />
    </g>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5 1C2.79086 1 1 2.79086 1 5V19C1 21.2091 2.79086 23 5 23H19C21.2091 23 23 21.2091 23 19V5C23 2.79086 21.2091 1 19 1H5ZM7 11C6.44772 11 6 11.4477 6 12C6 12.5523 6.44771 13 7 13H16C16.5523 13 17 12.5523 17 12C17 11.4477 16.5523 11 16 11H7Z"
      fill="#06556E"
    />
  </svg>
)

const CHECKBOX_UNSELECTED_1PT = (
  <path
    d="M19.00192,2c1.65298,0,2.99777,1.34479,2.99777,2.99777v14.00446C21.99969,20.65521,20.6549,22,19.00192,22H4.99746
  	c-1.65298,0-2.99777-1.34479-2.99777-2.99777V4.99777C1.99969,3.34479,3.34448,2,4.99746,2H19.00192 M19.00192,1H4.99746
  	C2.78955,1,0.99969,2.78986,0.99969,4.99777v14.00446C0.99969,21.21014,2.78955,23,4.99746,23h14.00446
  	c2.20791,0,3.99777-1.78986,3.99777-3.99777V4.99777C22.99969,2.78986,21.20983,1,19.00192,1L19.00192,1z"
  />
)

const CLOSE_1PT = (
  <path
    d="M12.70703,12l8.64648-8.64648c0.19531-0.19531,0.19531-0.51172,0-0.70703s-0.51172-0.19531-0.70703,0L12,11.29297
		L3.35352,2.64648c-0.19531-0.19531-0.51172-0.19531-0.70703,0s-0.19531,0.51172,0,0.70703L11.29297,12l-8.64648,8.64648
		c-0.19531,0.19531-0.19531,0.51172,0,0.70703C2.74414,21.45117,2.87207,21.5,3,21.5s0.25586-0.04883,0.35352-0.14648L12,12.70703
		l8.64648,8.64648C20.74414,21.45117,20.87207,21.5,21,21.5s0.25586-0.04883,0.35352-0.14648
		c0.19531-0.19531,0.19531-0.51172,0-0.70703L12.70703,12z"
  />
)

const CLOSE_2PT = (
  <path
    d="M13.41406,12l8.29297-8.29297c0.39062-0.39062,0.39062-1.02344,0-1.41406s-1.02344-0.39062-1.41406,0L12,10.58594
  	L3.70703,2.29297c-0.39062-0.39062-1.02344-0.39062-1.41406,0s-0.39062,1.02344,0,1.41406L10.58594,12l-8.29297,8.29297
  	c-0.39062,0.39062-0.39062,1.02344,0,1.41406C2.48828,21.90234,2.74414,22,3,22s0.51172-0.09766,0.70703-0.29297L12,13.41406
  	l8.29297,8.29297C20.48828,21.90234,20.74414,22,21,22s0.51172-0.09766,0.70703-0.29297c0.39062-0.39062,0.39062-1.02344,0-1.41406
  	L13.41406,12z"
  />
)

const RADIO_UNCHECKED_1PT = (
  <>
    <path
      d="M11.99934,1.99999c5.51421,0,10.00035,4.48584,10.00035,9.99966c0,5.51419-4.48614,10.00034-10.00035,10.00034
    	c-5.51383,0-9.99965-4.48615-9.99965-10.00034C1.99969,6.48583,6.48551,1.99999,11.99934,1.99999 M11.99934,0.99999
    	c-6.0746,0-10.99965,4.92505-10.99965,10.99966c0,6.07599,4.92505,11.00034,10.99965,11.00034
    	c6.07663,0,11.00035-4.92435,11.00035-11.00034C22.99969,5.92504,18.07597,0.99999,11.99934,0.99999L11.99934,0.99999z"
    />
  </>
)

const RADIO_CHECKED_1PT = (
  <>
    <path
      d="M11.99934,1.99999c5.51421,0,10.00035,4.48584,10.00035,9.99966c0,5.51419-4.48614,10.00034-10.00035,10.00034
    c-5.51383,0-9.99965-4.48615-9.99965-10.00034C1.99969,6.48583,6.48552,1.99999,11.99934,1.99999 M11.99934,0.99999
    c-6.0746,0-10.99965,4.92505-10.99965,10.99966c0,6.07599,4.92505,11.00034,10.99965,11.00034
    c6.07662,0,11.00035-4.92435,11.00035-11.00034C22.99969,5.92504,18.07597,0.99999,11.99934,0.99999L11.99934,0.99999z"
    />
    <g>
      <circle cx="11.99969" cy="12" r="5.5" />
    </g>
  </>
)

const CHEVRON_DOWN_2PT = (
  <path
    d="M12,18c-0.25586,0-0.51172-0.09766-0.70703-0.29297l-10-10c-0.39062-0.39062-0.39062-1.02344,0-1.41406
		s1.02344-0.39062,1.41406,0L12,15.58594l9.29297-9.29297c0.39062-0.39062,1.02344-0.39062,1.41406,0s0.39062,1.02344,0,1.41406
		l-10,10C12.51172,17.90234,12.25586,18,12,18z"
  />
)

const CHEVRON_UP_1PT = (
  <path
    d="M22 17.5C21.8721 17.5 21.7441 17.4512 21.6465 17.3535L12 7.70703L2.35351 17.3535C2.1582 17.5488
  1.84179 17.5488 1.64648 17.3535C1.45117 17.1582 1.45117 16.8418 1.64648 16.6465L11.6465 6.64648C11.8418 6.45117
  12.1582 6.45117 12.3535 6.64648L22.3535 16.6465C22.5488 16.8418 22.5488 17.1582 22.3535 17.3535C22.2559 17.4512
  22.1279 17.5 22 17.5Z"
  />
)

const CHEVRON_UP_2PT = (
  <path
    transform="rotate(180 11.999999046325684,12.000001907348633)"
    d="m12,18c-0.25586,0 -0.51172,-0.09766 -0.70703,-0.29297l-10,-10c-0.39062,-0.39062 -0.39062,-1.02344 0,-1.41406s1.02344,-0.39062 1.41406,0l9.29297,9.29297l9.29297,-9.29297c0.39062,-0.39062 1.02344,-0.39062 1.41406,0s0.39062,1.02344 0,1.41406l-10,10c-0.19531,0.19531 -0.45117,0.29297 -0.70703,0.29297z"
  />
)

const CHEVRON_DOWN_1PT = (
  <path
    transform="rotate(180 11.999999046325684,12.000001907348633)"
    d="M22 17.5C21.8721 17.5 21.7441 17.4512 21.6465 17.3535L12 7.70703L2.35351 17.3535C2.1582 17.5488
  1.84179 17.5488 1.64648 17.3535C1.45117 17.1582 1.45117 16.8418 1.64648 16.6465L11.6465 6.64648C11.8418 6.45117
  12.1582 6.45117 12.3535 6.64648L22.3535 16.6465C22.5488 16.8418 22.5488 17.1582 22.3535 17.3535C22.2559 17.4512
  22.1279 17.5 22 17.5Z"
  />
)

const CHEVRON_LEFT_2PT = (
  <path
    d="M17,23c-0.25586,0-0.51172-0.09766-0.70703-0.29297l-10-10c-0.39062-0.39062-0.39062-1.02344,0-1.41406l10-10
		c0.39062-0.39062,1.02344-0.39062,1.41406,0s0.39062,1.02344,0,1.41406L8.41406,12l9.29297,9.29297
		c0.39062,0.39062,0.39062,1.02344,0,1.41406C17.51172,22.90234,17.25586,23,17,23z"
  />
)

const CHEVRON_RIGHT_1PT = (
  <path
    d="M7 22.5C6.87207 22.5 6.74414 22.4512 6.64648 22.3535C6.45117 22.1582 6.45117 21.8418 6.64648 21.6465L16.293 12L6.64648 2.35351C6.45117 2.1582 6.45117 1.84179 6.64648 1.64648C6.84179 1.45117 7.1582 1.45117 7.35351 1.64648L17.3535 11.6465C17.5488 11.8418 17.5488 12.1582 17.3535 12.3535L7.35351 22.3535C7.25586 22.4512 7.12793 22.5 7 22.5Z"
    fill="inherit"
  />
)

const CHEVRON_RIGHT_2PT = (
  <path
    d="M7,23c-0.25586,0-0.51172-0.09766-0.70703-0.29297c-0.39062-0.39062-0.39062-1.02344,0-1.41406L15.58594,12
		L6.29297,2.70703c-0.39062-0.39062-0.39062-1.02344,0-1.41406s1.02344-0.39062,1.41406,0l10,10
		c0.39062,0.39062,0.39062,1.02344,0,1.41406l-10,10C7.51172,22.90234,7.25586,23,7,23z"
  />
)

const DOUBLE_CHEVRON_RIGHT_2PT = (
  <>
    <g>
      <path
        d="M13.08984,22c-0.25391,0-0.50879-0.09619-0.70312-0.28955c-0.39258-0.38818-0.39648-1.02148-0.00781-1.41406L20.59277,12
    		l-8.21387-8.29639c-0.38867-0.39258-0.38477-1.02588,0.00781-1.41406c0.3916-0.38965,1.02441-0.38477,1.41406,0.00684l8.91016,9
    		c0.38574,0.38965,0.38574,1.01758,0,1.40723l-8.91016,9C13.60449,21.90088,13.34766,22,13.08984,22z"
      />
    </g>
    <g>
      <path
        d="M2,22c-0.25391,0-0.50879-0.09619-0.70312-0.28955c-0.39258-0.38818-0.39648-1.02148-0.00781-1.41406L9.50293,12
    		L1.28906,3.70361C0.90039,3.31104,0.9043,2.67773,1.29688,2.28955c0.3916-0.38965,1.02441-0.38477,1.41406,0.00684l8.91016,9
    		c0.38574,0.38965,0.38574,1.01758,0,1.40723l-8.91016,9C2.51465,21.90088,2.25781,22,2,22z"
      />
    </g>
  </>
)

const DOUBLE_CHEVRON_LEFT_2PT = (
  <>
    <g>
      <path
        d="M10.91016,22c-0.25781,0-0.51465-0.09912-0.71094-0.29639l-8.91016-9c-0.38574-0.38965-0.38574-1.01758,0-1.40723
  		l8.91016-9c0.38965-0.3916,1.02246-0.39648,1.41406-0.00684c0.39258,0.38818,0.39648,1.02148,0.00781,1.41406L3.40723,12
  		l8.21387,8.29639c0.38867,0.39258,0.38477,1.02588-0.00781,1.41406C11.41895,21.90381,11.16406,22,10.91016,22z"
      />
    </g>
    <g>
      <path
        d="M22,22c-0.25781,0-0.51465-0.09912-0.71094-0.29639l-8.91016-9c-0.38574-0.38965-0.38574-1.01758,0-1.40723l8.91016-9
  		c0.38965-0.3916,1.02246-0.39648,1.41406-0.00684c0.39258,0.38818,0.39648,1.02148,0.00781,1.41406L14.49707,12l8.21387,8.29639
  		c0.38867,0.39258,0.38477,1.02588-0.00781,1.41406C22.50879,21.90381,22.25391,22,22,22z"
      />
    </g>
  </>
)

const RESTAURANT_1PT = (
  <path
    d="M22.5,9.2002l-0.01465-0.23145l-0.00781-0.02539C22.5,8.85156,22.50586,8.75391,22.4707,8.6582l-2.09863-5.82715
		C20.30078,2.63184,20.1123,2.5,19.90137,2.5H3.77441c-0.21973,0-0.41406,0.14355-0.47852,0.35449L1.52148,8.68262
		C1.49316,8.77637,1.50195,8.86914,1.52637,8.95703l-0.01562,0.0498L1.5,9.2002c0,0.7666,0.48828,1.44336,1.23047,1.86719
		l-0.02344,0.11426V21c0,0.27637,0.22363,0.5,0.5,0.5h4.20508c0.27637,0,0.5-0.22363,0.5-0.5v-4.21973h3.11914V21
		c0,0.27637,0.22363,0.5,0.5,0.5h8.97754c0.27637,0,0.5-0.22363,0.5-0.5v-9.80273C21.89648,10.79297,22.5,10.05371,22.5,9.2002z
		 M20.00879,20.5h-7.97754v-4.21973c0-0.27637-0.22363-0.5-0.5-0.5H7.41211c-0.27637,0-0.5,0.22363-0.5,0.5V20.5H3.70703v-9.06836
		c0.25391,0.05469,0.5166,0.09277,0.79297,0.09277c1.04395,0,1.96289-0.41602,2.5-1.04395
		c0.53711,0.62793,1.45605,1.04395,2.5,1.04395s1.96289-0.41602,2.5-1.04395c0.53711,0.62793,1.45605,1.04395,2.5,1.04395
		s1.96289-0.41602,2.5-1.04395c0.53711,0.62793,1.45605,1.04395,2.5,1.04395c0.17383,0,0.34277-0.01758,0.50879-0.04004V20.5z
		 M19.5,10.52441c-1.08398,0-2-0.60645-1.99512-1.41406c0.01172-0.10645-0.01953-0.20312-0.06738-0.29004l-0.03711-0.06836
		l-0.01367-0.0166c-0.10352-0.1416-0.27344-0.23047-0.45605-0.2041c-0.27441,0.03223-0.4707,0.28125-0.4375,0.55469L16.5,9.2002
		c0,0.71777-0.91602,1.32422-2,1.32422s-2-0.60645-1.99512-1.41406c0.01172-0.10645-0.01953-0.20312-0.06836-0.29004
		l-0.03613-0.06836l-0.01465-0.0166c-0.10352-0.1416-0.27246-0.23047-0.45508-0.2041
		c-0.27441,0.03223-0.4707,0.28125-0.4375,0.55469L11.5,9.2002c0,0.71777-0.91602,1.32422-2,1.32422s-2-0.60645-1.99512-1.41406
		C7.5166,9.00293,7.48535,8.90527,7.43555,8.81738l-0.0332-0.0625L7.38281,8.73242C7.27832,8.59277,7.11035,8.50488,6.93066,8.53125
		c-0.27441,0.03223-0.4707,0.28125-0.4375,0.55469L6.5,9.2002c0,0.71777-0.91602,1.32422-2,1.32422s-2-0.60645-1.99512-1.41406
		l-0.01562-0.1709L4.14453,3.5H19.5498l1.95605,5.43066l-0.0127,0.15527L21.5,9.2002C21.5,9.91797,20.58398,10.52441,19.5,10.52441z
		"
  />
)

const GLOBE_1PT = (
  <>
    <path d="M12,22.5A10.5,10.5,0,1,1,22.5,12,10.51,10.51,0,0,1,12,22.5Zm0-20A9.5,9.5,0,1,0,21.5,12,9.51,9.51,0,0,0,12,2.5Z" />
    <path d="M22,12.5H2a.5.5,0,0,1,0-1H22a.5.5,0,0,1,0,1Z" />
    <path d="M12,22.5a.52.52,0,0,1-.37-.16A15.83,15.83,0,0,1,7.5,12,15.84,15.84,0,0,1,11.63,1.66a.53.53,0,0,1,.74,0A15.83,15.83,0,0,1,16.5,12a15.84,15.84,0,0,1-4.13,10.35A.52.52,0,0,1,12,22.5ZM12,2.76A14.77,14.77,0,0,0,8.5,12,14.76,14.76,0,0,0,12,21.24,14.77,14.77,0,0,0,15.5,12,14.76,14.76,0,0,0,12,2.76Z" />
  </>
)

const EDIT_1PT = (
  <path
    d="M21.3916,2.59473c-1.49316-1.47803-3.1543-1.46387-4.67773,0.04395L2.30664,16.89404
c-0.07812,0.07715-0.12891,0.17822-0.14355,0.28662l-0.6582,4.75049c-0.02246,0.15967,0.03418,0.31982,0.15039,0.43066
C1.74902,22.45117,1.87305,22.5,2,22.5l0.09277-0.00879l4.58789-0.86328c0.09766-0.01807,0.18848-0.06543,0.25879-0.13574
l13.09668-12.9585l1.31055-1.2959C22.1123,6.47998,22.5,5.6875,22.5,4.88184C22.5,4.0918,22.12695,3.32227,21.3916,2.59473z
 M6.34473,20.67383l-3.75391,0.70605l0.53906-3.89404L14.51074,6.22461L14.5166,6.2334l3.09277,3.2417l0.03125,0.02197
L6.34473,20.67383z M20.64355,6.52637l-2.30078,2.27588l-0.01074-0.01758l-3.09277-3.2417L15.2168,5.52686l2.2002-2.17725
c1.13281-1.12012,2.17188-1.1333,3.27148-0.04395C21.22656,3.83838,21.5,4.36914,21.5,4.88184
C21.5,5.41064,21.21191,5.96436,20.64355,6.52637z"
  />
)

const HISTORY_1PT = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="mask0_386_78030" maskUnits="userSpaceOnUse" x="2" y="2" width="20" height="20">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M3 4.736V8.00653H21V4.736C21 3.779 20.228 3 19.277 3H4.723C3.772 3 3 3.779 3 4.736ZM15.5 12.5C15.5 12.776 15.276 13 15 13H9C8.724 13 8.5 12.776 8.5 12.5C8.5 12.224 8.724 12 9 12H15C15.276 12 15.5 12.224 15.5 12.5ZM20 19.184C20 20.141 19.228 20.92 18.277 20.92H5.723C4.772 20.92 4 20.141 4 19.184V9H20V19.184ZM4.723 2C3.222 2 2 3.228 2 4.736V8.50524C2 8.78124 2.224 9.00524 2.5 9.00524H3V19.2631C3 20.7711 4.222 21.9991 5.723 21.9991H18.277C19.778 21.9991 21 20.7711 21 19.2631V9.00524H21.5C21.776 9.00524 22 8.78124 22 8.50524V4.736C22 3.228 20.778 2 19.277 2H4.723Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_386_78030)">
      <rect width="24" height="24" fill="#06556E" />
    </g>
  </svg>
)

const INFO_2PT = (
  <>
    <g>
      <path
        d="M12,3c4.96262,0,9,4.03738,9,9s-4.03738,9-9,9s-9-4.03738-9-9S7.03738,3,12,3 M12,1C5.92487,1,1,5.92487,1,12
		s4.92487,11,11,11s11-4.92487,11-11S18.07513,1,12,1L12,1z"
      />
    </g>
    <g>
      <path
        d="M11.94775,17.52002c-0.55225,0-1-0.44775-1-1v-5.49609c0-0.55225,0.44775-1,1-1s1,0.44775,1,1v5.49609
		C12.94775,17.07227,12.5,17.52002,11.94775,17.52002z"
      />
    </g>
    <g>
      <circle cx="11.94797" cy="7.67237" r="1.0752" />
    </g>
  </>
)

const INFO_1PT = (
  <g id="icon / 1px / info">
    <path d="M1 12C1 5.9248 5.9248 1 12 1C18.0752 1 23 5.9248 23 12C23 18.0752 18.0752 23 12 23C5.9248 23 1 18.0752 1 12ZM22 12C22 6.48584 17.5142 2 12 2C6.48584 2 2 6.48584 2 12C2 17.5142 6.48584 22 12 22C17.5142 22 22 17.5142 22 12ZM11.9478 17.02C11.6714 17.02 11.4478 16.7964 11.4478 16.52V11.0239C11.4478 10.7476 11.6714 10.5239 11.9478 10.5239C12.2241 10.5239 12.4478 10.7476 12.4478 11.0239V16.52C12.4478 16.7964 12.2241 17.02 11.9478 17.02ZM11.9478 8.42236C12.362 8.42236 12.6978 8.08657 12.6978 7.67236C12.6978 7.25815 12.362 6.92236 11.9478 6.92236C11.5335 6.92236 11.1978 7.25815 11.1978 7.67236C11.1978 8.08657 11.5335 8.42236 11.9478 8.42236Z" />
  </g>
)

const MORE_VERTICAL_1PT = (
  <path d="M12 5.054c-1.105 0-2-.908-2-2.027C10 1.907 10.895 1 12 1s2 .907 2 2.027-.895 2.027-2 2.027zm0 8.973c-1.105 0-2-.908-2-2.027 0-1.12.895-2.027 2-2.027s2 .908 2 2.027c0 1.12-.895 2.027-2 2.027zM12 23c-1.105 0-2-.907-2-2.027s.895-2.027 2-2.027 2 .908 2 2.027c0 1.12-.895 2.027-2 2.027z" />
)

const IMPORT_1PT = (
  <g>
    <path
      d="M17.19922,18.85449c-0.27637,0-0.5-0.22363-0.5-0.5s0.22363-0.5,0.5-0.5c2.41211,0,4.30078-1.97119,4.30078-4.4873
		s-1.88867-4.4873-4.30078-4.4873c-0.2168,0-0.4707,0.04102-0.72363,0.0791c-0.25781,0.03711-0.48926-0.11719-0.55664-0.35986
		C15.3291,6.47998,13.4375,5,11.31934,5C8.66211,5,6.5,7.26221,6.5,10.04248c0,0.23096-0.00195,0.50098,0.03418,0.73193
		c0.02246,0.14453-0.01953,0.2915-0.11426,0.40283c-0.14844,0.17432-0.39062,0.18896-0.65625,0.14795l-0.12402-0.01562
		C3.9082,11.30957,2.5,12.78662,2.5,14.60254c0,1.79297,1.4082,3.25195,3.13965,3.25195c0.27637,0,0.5,0.22363,0.5,0.5
		s-0.22363,0.5-0.5,0.5c-2.28223,0-4.13965-1.90723-4.13965-4.25195c0-2.31934,1.78223-4.21484,4.00195-4.29053
		C5.5,10.21777,5.5,10.12695,5.5,10.04248C5.5,6.71045,8.11035,4,11.31934,4c2.42188,0,4.59668,1.5874,5.42969,3.91113
		c0.1582-0.01855,0.31055-0.03125,0.4502-0.03125c2.97266,0,5.30078,2.41016,5.30078,5.4873S20.17188,18.85449,17.19922,18.85449z"
    />
    <path
      d="M15.23438,15.47461l-3.20508-3.25879c-0.09082-0.09521-0.21777-0.15625-0.36035-0.15625l-0.00098,0.00049l-0.00195-0.00049
		c-0.13379,0-0.2627,0.05371-0.35645,0.14941l-3.21289,3.26562c-0.19336,0.19678-0.19141,0.51318,0.00586,0.70703
		c0.19727,0.19287,0.51367,0.19189,0.70703-0.00586l2.35938-2.39795V19.5c0,0.27637,0.22363,0.5,0.5,0.5s0.5-0.22363,0.5-0.5
		v-5.71631l2.35254,2.39209c0.09766,0.09961,0.22754,0.14941,0.35645,0.14941c0.12695,0,0.25293-0.04785,0.35059-0.14355
		C15.42578,15.98779,15.42773,15.67139,15.23438,15.47461z"
    />
  </g>
)

const IMPORT_2PT = (
  <>
    <g>
      <path
        d="M17.19922,19.35449c-0.55273,0-1-0.44775-1-1s0.44727-1,1-1c2.13086,0,3.80078-1.75146,3.80078-3.9873
			s-1.66992-3.9873-3.80078-3.9873c-0.19434,0-0.42188,0.03906-0.64844,0.07373c-0.50391,0.07568-0.97852-0.23389-1.11328-0.7207
			C14.90723,6.82959,13.21387,5.5,11.31934,5.5C8.9375,5.5,7,7.5376,7,10.04248c0,0.20654-0.00488,0.44824,0.02832,0.65479
			c0.04492,0.28857-0.03809,0.58252-0.22852,0.80469c-0.25781,0.30273-0.65137,0.38818-1.11133,0.31738
			C4.20801,11.79053,3,13.0625,3,14.60254c0,1.4917,1.20898,2.75195,2.63965,2.75195c0.55273,0,1,0.44775,1,1s-0.44727,1-1,1
			C3.08105,19.35449,1,17.22266,1,14.60254c0-2.41943,1.74414-4.42627,4.00293-4.74805C5.09863,6.3335,7.89551,3.5,11.31934,3.5
			c2.50586,0,4.76758,1.55957,5.75879,3.88232c0.04004-0.00146,0.08105-0.00244,0.12109-0.00244
			c3.25293,0,5.80078,2.62988,5.80078,5.9873S20.45215,19.35449,17.19922,19.35449z"
      />
    </g>
    <path
      d="M15.59082,15.12402l-3.20776-3.26141c-0.18158-0.18604-0.43335-0.30304-0.71411-0.30304
	c-0.00049,0-0.00098,0.00031-0.00146,0.00031s-0.00098-0.00031-0.00146-0.00031c-0.24353,0-0.47186,0.09882-0.65417,0.25909
	c-0.0152,0.01349-0.03601,0.01971-0.05035,0.03406c-0.00244,0.00244-0.00592,0.00323-0.00836,0.00568l-3.21289,3.26562
	c-0.3877,0.39355-0.38184,1.02686,0.01172,1.41406c0.39355,0.38672,1.02734,0.38135,1.41406-0.01172l1.50293-1.52759V19.5
	c0,0.55225,0.44727,1,1,1s1-0.44775,1-1v-4.49487l1.49609,1.52124c0.19531,0.19922,0.4541,0.29883,0.71289,0.29883
	c0.25293,0,0.50684-0.0957,0.70117-0.28711C15.97266,16.15088,15.97852,15.51758,15.59082,15.12402z"
    />
  </>
)

const ARROW_LEFT_1PT = (
  <>
    <path
      id="a"
      d="M22 11.491H3.208l8.558-8.64a.5.5 0 0 0-.711-.703L1.672 11.62a.492.492 0 0 0-.172.371l.001.004a.497.497 0 0 0 .144.357l9.41 9.5a.5.5 0 0 0 .71-.704L3.19 12.49H22a.5.5 0 1 0 0-1z"
    />
  </>
)

const SCROLL_ARROW_LEFT_2PT = (
  <g transform="translate(1.5 1.5) scale(2)">
    <path
      d="M10.5 4.74598H1.70346L5.5603 0.852178C5.75463 0.656133 5.75317 0.339483 5.55688 0.145148C5.36059 -0.0496774 5.04369 -0.0482124 4.84985 0.148568L0.1597 4.88352C0.10669 4.9333 0.064605 4.99299 0.037235 5.06168C0.025545 5.09037 0.02527 5.12125 0.01914 5.15125C0.012815 5.18299 0 5.21238 0 5.24598C0 5.24668 0.000395 5.24726 0.000395 5.24796C-0.000215 5.37598 0.047605 5.50416 0.144775 5.60218L4.84985 10.3522C4.94751 10.4511 5.07642 10.5004 5.20508 10.5004C5.33227 10.5004 5.45947 10.452 5.55688 10.3556C5.75317 10.1613 5.75464 9.84461 5.56031 9.64857L1.69476 5.74598H10.5C10.7761 5.74598 11 5.5221 11 5.24598C11 4.96985 10.7761 4.74598 10.5 4.74598Z"
      fill="currentColor"
    />
  </g>
)

const SCROLL_ARROW_RIGHT_2PT = (
  <g transform="translate(1.5 1.5) scale(2)">
    <path
      d="M11 5.25422C11 5.25352 10.9996 5.25294 10.9996 5.25224C11.0002 5.12422 10.9524 4.99604 10.8552 4.89802L6.15015 0.148015C5.95606 -0.0482747 5.63916 -0.0492497 5.44312 0.144595C5.24683 0.33893 5.24536 0.65558 5.43969 0.851625L9.30523 4.75422H0.5C0.223875 4.75422 0 4.97809 0 5.25422C0 5.53034 0.223875 5.75422 0.5 5.75422H9.29654L5.4397 9.64802C5.24537 9.84406 5.24683 10.1607 5.44312 10.355C5.54052 10.4517 5.66773 10.4998 5.79492 10.4998C5.92358 10.4998 6.05249 10.4505 6.15015 10.3516L10.8403 5.61668C10.8933 5.5669 10.9354 5.50721 10.9628 5.43858C10.9747 5.40925 10.9751 5.37776 10.9813 5.34706C10.9873 5.3159 11 5.28715 11 5.25422Z"
      fill="#666666"
    />
  </g>
)

const CALENDAR_2PT = (
  <>
    <path
      id="a"
      d="M20.063 3.241h-1.966V2a1 1 0 0 0-2 0v1.241H7.88V2a1 1 0 0 0-2 0v1.241H3.93A2.933 2.933 0 0 0 1 6.171V20.07A2.933 2.933 0 0 0 3.93 23h16.133a2.933 2.933 0 0 0 2.93-2.93V6.17a2.933 2.933 0 0 0-2.93-2.93zm.93 16.829c0 .513-.417.93-.93.93H3.93a.931.931 0 0 1-.93-.93V6.17c0-.512.417-.93.93-.93h1.95v1.241a1 1 0 0 0 2 0v-1.24h8.217v1.24a1 1 0 0 0 2 0v-1.24h1.966c.513 0 .93.417.93.93V20.07z"
    />
  </>
)

const CALENDAR_4PT = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="mask0_830_74040" maskUnits="userSpaceOnUse" x="1" y="1" width="22" height="22">
      <path
        d="M17.1016 1.5C17.378 1.5 17.6016 1.72363 17.6016 2V3.74121H20.0693C21.4096 3.74121 22.5 4.83203 22.5 6.17188V20.0703C22.4997 21.4099 21.4095 22.5 20.0693 22.5H3.93066C2.59054 22.5 1.50026 21.4099 1.5 20.0703V6.17188C1.5 4.83203 2.59039 3.74121 3.93066 3.74121H6.38184V2C6.38184 1.72363 6.60538 1.5 6.88184 1.5C7.15821 1.50011 7.38184 1.7237 7.38184 2V3.74121H16.6016V2C16.6016 1.72369 16.8252 1.5001 17.1016 1.5ZM3.93066 4.74121C3.14183 4.74121 2.5 5.38331 2.5 6.17188V20.0693C2.5 20.8579 3.14184 21.5 3.93066 21.5H20.0693C20.8582 21.5 21.5 20.8579 21.5 20.0693V6.17188C21.5 5.3833 20.8582 4.74121 20.0693 4.74121H17.6016V6.48242C17.6016 6.75879 17.378 6.98242 17.1016 6.98242C16.8252 6.98231 16.6016 6.75872 16.6016 6.48242V4.74121H7.38184V6.48242C7.38183 6.75873 7.15821 6.98232 6.88184 6.98242C6.60538 6.98242 6.38184 6.75879 6.38184 6.48242V4.74121H3.93066Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_830_74040)">
      <rect width="24" height="24" fill="#666666" />
    </g>
  </svg>
)

const FILTER_1PT = (
  <>
    <g>
      <path d="M1.99658,5.22754h6.75h0.52783V7.4541c0,0.27637,0.22363,0.5,0.5,0.5s0.5-0.22363,0.5-0.5V5.22754h11.72217c0.27637,0,0.5-0.22363,0.5-0.5s-0.22363-0.5-0.5-0.5H10.27441V2c0-0.27637-0.22363-0.5-0.5-0.5s-0.5,0.22363-0.5,0.5v2.22754H8.74658h-6.75c-0.27637,0-0.5,0.22363-0.5,0.5S1.72021,5.22754,1.99658,5.22754z" />
      <path d="M21.99658,11.5h-3.88867h-1.16699V9.27246c0-0.27637-0.22363-0.5-0.5-0.5s-0.5,0.22363-0.5,0.5V11.5H1.99658c-0.27637,0-0.5,0.22363-0.5,0.5s0.22363,0.5,0.5,0.5h13.94434v2.22754c0,0.27637,0.22363,0.5,0.5,0.5s0.5-0.22363,0.5-0.5V12.5h1.16699h3.88867c0.27637,0,0.5-0.22363,0.5-0.5S22.27295,11.5,21.99658,11.5z" />
      <path d="M21.99658,18.77246H8.05225V16.5459c0-0.27637-0.22363-0.5-0.5-0.5s-0.5,0.22363-0.5,0.5v2.22656h-1.1665H1.99658c-0.27637,0-0.5,0.22363-0.5,0.5s0.22363,0.5,0.5,0.5h3.88916h1.1665V22c0,0.27637,0.22363,0.5,0.5,0.5s0.5-0.22363,0.5-0.5v-2.22754h13.94434c0.27637,0,0.5-0.22363,0.5-0.5S22.27295,18.77246,21.99658,18.77246z" />
    </g>
  </>
)

const FILTER_STACK_1PT = (
  <>
    <g>
      <path d="M3 7.5L21 7.5" stroke="currentColor" strokeLinecap="round" />
      <path d="M6 12L18 12" stroke="currentColor" strokeLinecap="round" />
      <path d="M9 16.5L15 16.5" stroke="currentColor" strokeLinecap="round" />
    </g>
  </>
)

const FILTER_2PT = (
  <>
    <g>
      <path d="M1.99658,5.72754h6.75h0.02783V7.4541c0,0.55273,0.44775,1,1,1s1-0.44727,1-1V5.72754h11.22217c0.55225,0,1-0.44727,1-1s-0.44775-1-1-1H10.77441V2c0-0.55273-0.44775-1-1-1s-1,0.44727-1,1v1.72754H8.74658h-6.75c-0.55225,0-1,0.44727-1,1S1.44434,5.72754,1.99658,5.72754z" />
      <path d="M21.99658,11h-3.88867h-0.66699V9.27246c0-0.55273-0.44775-1-1-1s-1,0.44727-1,1V11H1.99658c-0.55225,0-1,0.44727-1,1s0.44775,1,1,1h13.44434v1.72754c0,0.55273,0.44775,1,1,1s1-0.44727,1-1V13h0.66699h3.88867c0.55225,0,1-0.44727,1-1S22.54883,11,21.99658,11z" />
      <path d="M21.99658,18.27246H8.55225V16.5459c0-0.55273-0.44775-1-1-1s-1,0.44727-1,1v1.72656h-0.6665H1.99658c-0.55225,0-1,0.44727-1,1s0.44775,1,1,1h3.88916h0.6665V22c0,0.55273,0.44775,1,1,1s1-0.44727,1-1v-1.72754h13.44434c0.55225,0,1-0.44727,1-1S22.54883,18.27246,21.99658,18.27246z" />
    </g>
  </>
)

const SAVED_ACCOUNT_1PT = (
  <>
    <path
      d="M12.05713,22.5C6.23584,22.5,1.5,17.79004,1.5,12S6.23584,1.5,12.05713,1.5c1.13135,0,2.24512,0.17676,3.30957,0.52539
		c0.26221,0.08691,0.40527,0.36914,0.31934,0.63086c-0.08594,0.2627-0.36768,0.4043-0.63086,0.32031
		C14.09131,2.66016,13.08252,2.5,12.05713,2.5C6.78711,2.5,2.5,6.76172,2.5,12s4.28711,9.5,9.55713,9.5
		c4.68408,0,8.74609-3.46484,9.44873-8.05957c0.0415-0.27344,0.2915-0.46094,0.56982-0.41895
		c0.27295,0.04199,0.46045,0.29688,0.41846,0.56934C21.71777,18.66992,17.23096,22.5,12.05713,22.5z"
    />
    <path
      d="M11.66699,15.51562c-0.12744,0-0.25488-0.04883-0.35254-0.14551l-4.3335-4.30859
		c-0.1958-0.19434-0.19678-0.51074-0.00195-0.70703c0.19531-0.19629,0.51172-0.19531,0.70703-0.00195l3.98096,3.95801
		l9.18115-9.12793c0.19531-0.19531,0.51172-0.19531,0.70703,0.00195c0.19482,0.19629,0.19385,0.5127-0.00195,0.70703
		l-9.53369,9.47852C11.92188,15.4668,11.79443,15.51562,11.66699,15.51562z"
    />
  </>
)

const SAVED_ACCOUNT_2PT = (
  <>
    <g>
      <path
        d="M12.05713,23C5.96045,23,1,18.06543,1,12S5.96045,1,12.05713,1c1.18457,0,2.3501,0.18555,3.46533,0.55078
		c0.5249,0.17188,0.81055,0.7373,0.63867,1.26172s-0.73389,0.8125-1.26172,0.63867C13.98584,3.15137,13.02979,3,12.05713,3
		C7.06299,3,3,7.03711,3,12s4.06299,9,9.05713,9c4.43945,0,8.28906-3.28223,8.95459-7.63574
		c0.08301-0.5459,0.60059-0.91797,1.13916-0.83691c0.54639,0.08301,0.9209,0.59375,0.8374,1.13965
		C22.17529,18.9873,17.47559,23,12.05713,23z"
      />
    </g>
    <g>
      <path
        d="M11.66699,16.01562c-0.25488,0-0.51025-0.09668-0.70508-0.29102l-4.3335-4.30859
		c-0.3916-0.38965-0.39355-1.02246-0.00391-1.41406C7.01416,9.61133,7.64697,9.6084,8.03857,9.99805l3.62842,3.60742
		l8.82861-8.77734c0.3916-0.39062,1.02441-0.3877,1.41406,0.00391s0.3877,1.02441-0.00391,1.41406l-9.53369,9.47852
		C12.17725,15.91895,11.92188,16.01562,11.66699,16.01562z"
      />
    </g>
  </>
)

const SPIN_COUNTER_CLOCKWISE_2PT = (
  <path
    d="M13.621 4.007C10.62.98 5.818.802 2.604 3.597V.8C2.604.358 2.25 0 1.813 0c-.437 0-.791.358-.791.8v5.686h5.624c.437 0 .791-.358.791-.8 0-.441-.354-.8-.79-.8H3.542c2.185-1.975 5.404-2.236 7.873-.638 2.468 1.598 3.579 4.663 2.717 7.494-.863 2.831-3.487 4.734-6.417 4.653-2.93-.082-5.447-2.128-6.154-5.003-.11-.428-.542-.687-.966-.576-.425.11-.68.547-.57.976.83 3.36 3.66 5.828 7.07 6.168 3.41.34 6.662-1.522 8.126-4.653 1.464-3.13.823-6.853-1.6-9.3z"
    transform="translate(1, 0) scale(1.3)"
  />
)

const SPIN_CLOCKWISE_2PT = (
  <path
    d="M13.621 4.007C10.62.98 5.818.802 2.604 3.597V.8C2.604.358 2.25 0 1.813 0c-.437 0-.791.358-.791.8v5.686h5.624c.437 0 .791-.358.791-.8 0-.441-.354-.8-.79-.8H3.542c2.185-1.975 5.404-2.236 7.873-.638 2.468 1.598 3.579 4.663 2.717 7.494-.863 2.831-3.487 4.734-6.417 4.653-2.93-.082-5.447-2.128-6.154-5.003-.11-.428-.542-.687-.966-.576-.425.11-.68.547-.57.976.83 3.36 3.66 5.828 7.07 6.168 3.41.34 6.662-1.522 8.126-4.653 1.464-3.13.823-6.853-1.6-9.3z"
    transform="translate(1, 0) scale(1.3) matrix(-1 0 0 1 16 0)"
  />
)

const FLOORPLAN_1PT = (
  <path
    d="M22.917 1.563H2.083c-.288 0-.52.232-.52.52v20.834c0 .288.232.52.52.52h20.834c.288 0
    .52-.232.52-.52V2.083c0-.288-.232-.52-.52-.52zm-9.57 20.833v-3.732c0-.288-.234-.521-.521-.521-.288 0-.521.233-.521.52v3.733h-9.7V10.18h9.7v3.072c0
    .288.233.52.52.52.288 0 .521-.232.521-.52V6.067c0-.288-.233-.52-.52-.52-.288 0-.521.232-.521.52v3.071h-9.7V2.604h19.79v10.127h-2.864c-.288 0-.52.233-.52.52 0
    .289.232.522.52.522h2.865v8.623h-9.05z"
  />
)

const ADD_CIRCLE_1PT = (
  <path
    d="M10 .833c5.063 0 9.167 4.104 9.167 9.167S15.063 19.167 10 19.167.833
    15.063.833 10 4.937.833 10 .833zm0 .834c-4.595 0-8.333 3.738-8.333 8.333 0 4.595 3.738 8.333
    8.333 8.333 4.595 0 8.333-3.738 8.333-8.333 0-4.595-3.738-8.333-8.333-8.333zm.014 4.701c.23
    0 .417.187.417.417v2.798h2.798c.23 0 .417.187.417.417 0 .23-.187.417-.417.417h-2.798v2.798c0
    .23-.187.417-.417.417-.23 0-.417-.187-.417-.417v-2.798H6.8c-.23 0-.417-.187-.417-.417
    0-.23.186-.417.417-.417h2.798V6.785c0-.23.187-.417.417-.417z"
  />
)

const ADD_CIRCLE_2PT = (
  <>
    <mask id="mask0_1_6617" maskUnits="userSpaceOnUse" x="1" y="1" width="22" height="22">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 1C5.9248 1 1 5.9248 1 11.9995C1 18.0757 5.9248 23 12 23C18.0762 23 23 18.0757 23 11.9995C23 5.9248 18.0762 1 12 1ZM12 22C6.48633 22 2 17.5137 2 11.9995C2 6.48584 6.48633 2 12 2C17.5137 2 22 6.48584 22 11.9995C22 17.5137 17.5137 22 12 22ZM12.5166 11.5H15.875C16.1514 11.5 16.375 11.7236 16.375 12C16.375 12.2764 16.1514 12.5 15.875 12.5H12.5166V15.8579C12.5166 16.1343 12.293 16.3579 12.0166 16.3579C11.7402 16.3579 11.5166 16.1343 11.5166 15.8579V12.5H8.1582C7.88183 12.5 7.6582 12.2764 7.6582 12C7.6582 11.7236 7.88183 11.5 8.1582 11.5H11.5166V8.14209C11.5166 7.86572 11.7402 7.64209 12.0166 7.64209C12.293 7.64209 12.5166 7.86572 12.5166 8.14209V11.5Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_1_6617)">
      <rect width="24" height="24" fill="inherit" />
    </g>
  </>
)

const TABLET_1PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M3.84983 0.708496H13.1502C14.1013 0.708496 14.875 1.48185 14.875 2.43298V14.5666C14.875 15.5178 14.1013 16.2918 13.1502 16.2918H3.84983C2.89871 16.2918 2.125 15.5178 2.125 14.5666V2.43298C2.125 1.48185 2.89871 0.708496 3.84983 0.708496ZM8.49999 15.0668C8.7903 15.0668 9.02564 14.8334 9.02564 14.5454C9.02564 14.2575 8.7903 14.024 8.49999 14.024C8.20969 14.024 7.97435 14.2575 7.97435 14.5454C7.97435 14.8334 8.20969 15.0668 8.49999 15.0668ZM3.84983 1.41683H13.1502C13.7108 1.41683 14.1667 1.87268 14.1667 2.43298V12.7751H2.83333V2.43298C2.83333 1.87268 3.28918 1.41683 3.84983 1.41683ZM3.84983 15.5835H13.1502C13.7108 15.5835 14.1667 15.1276 14.1667 14.5667V13.4834H2.83333V14.5667C2.83333 15.1276 3.28918 15.5835 3.84983 15.5835Z"
  />
)

const GIFT_1PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M16.5 1.5C18.1543 1.5 19.5 2.846 19.5 4.5C19.5 5.271 19.1992 5.968 18.7197 6.5H22C22.2764 6.5 22.5 6.724 22.5 7V12C22.5 12.276 22.2764 12.5 22 12.5H20.5V22C20.5 22.276 20.2764 22.5 20 22.5H4C3.7236 22.5 3.5 22.276 3.5 22V12.5H2C1.7236 12.5 1.5 12.276 1.5 12V7C1.5 6.724 1.7236 6.5 2 6.5H5.2803C4.8008 5.968 4.5 5.271 4.5 4.5C4.5 2.846 5.8457 1.5 7.5 1.5C10.034 1.5 11.3671 3.723 12 5.319C12.6329 3.723 13.966 1.5 16.5 1.5ZM4.5 21.5H11.5V12.5H4.5V21.5ZM12.5 21.5H19.5V12.5H12.5V21.5ZM2.5 11.5H11.5V7.5H2.5V11.5ZM12.5 7.5V11.5H21.5V7.5H12.5ZM7.5 2.5C6.3975 2.5 5.5 3.397 5.5 4.5C5.5 5.603 6.3975 6.5 7.5 6.5H11.3516C10.9727 5.254 9.8691 2.5 7.5 2.5ZM16.5 2.5C14.1309 2.5 13.0273 5.254 12.6484 6.5H16.5C17.6025 6.5 18.5 5.603 18.5 4.5C18.5 3.397 17.6025 2.5 16.5 2.5Z"
  />
)

const CASH_1PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M9 16.875C4.65784 16.875 1.125 13.3425 1.125 9C1.125 4.65747 4.65784 1.125 9 1.125C13.3422 1.125 16.875 4.65747 16.875 9C16.875 13.3425 13.3422 16.875 9 16.875ZM9 1.875C5.07129 1.875 1.875 5.07129 1.875 9C1.875 12.9287 5.07129 16.125 9 16.125C12.9287 16.125 16.125 12.9287 16.125 9C16.125 5.07129 12.9287 1.875 9 1.875ZM7.021 7.62817C7.021 8.17749 7.46851 8.625 8.01819 8.625H9.97815C10.9417 8.625 11.7253 9.40869 11.7253 10.3718C11.7253 11.3357 10.9417 12.1194 9.97815 12.1194H9.36878L9.37317 12.1406V13.3125C9.37317 13.5198 9.20545 13.6875 8.99817 13.6875C8.79089 13.6875 8.62317 13.5198 8.62317 13.3125V12.1406L8.62757 12.1194H6.646C6.43872 12.1194 6.271 11.9517 6.271 11.7444C6.271 11.5371 6.43872 11.3694 6.646 11.3694H9.97815C10.5278 11.3694 10.9753 10.9219 10.9753 10.3718C10.9753 9.82251 10.5278 9.375 9.97815 9.375H8.01819C7.05469 9.375 6.271 8.59131 6.271 7.62817C6.271 6.6643 7.05469 5.88062 8.01819 5.88062H8.62757L8.62317 5.85938V4.6875C8.62317 4.48022 8.79089 4.3125 8.99817 4.3125C9.20545 4.3125 9.37317 4.48022 9.37317 4.6875V5.85938L9.36878 5.88062H10.9585C11.1658 5.88062 11.3335 6.04834 11.3335 6.25562C11.3335 6.46289 11.1658 6.63062 10.9585 6.63062H8.01819C7.46851 6.63062 7.021 7.07812 7.021 7.62817Z"
  />
)

const SWITCH_USER_1PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M13.5919 7.64306C14.4935 8.02978 16.1693 9.02111 16.7545 11.2096C16.9735 12.0325 16.8959 12.7869 16.5282 13.3922C16.1781 13.9694 15.5753 14.3788 14.8312 14.5443L14.7499 14.5535C14.5777 14.5535 14.4225 14.4348 14.3837 14.2601C14.339 14.058 14.4664 13.8577 14.6686 13.8126C15.2106 13.6914 15.6441 13.4039 15.8873 13.0029C16.1459 12.5767 16.1949 12.0233 16.0294 11.403C15.3907 9.01379 13.1803 8.26636 12.858 8.16968C12.8319 8.184 12.8066 8.19923 12.7814 8.21443C12.7421 8.23808 12.7029 8.26167 12.661 8.28173L12.4984 8.31872C12.3592 8.31872 12.2252 8.24072 12.1607 8.10632C12.0706 7.91992 12.149 7.6958 12.3358 7.60571C13.3722 7.10693 14.0255 6.08117 14.0409 4.92871C14.0519 4.12414 13.7501 3.36352 13.1913 2.78711C12.6324 2.21105 11.8839 1.88806 11.0826 1.87744L11.0416 1.87707C10.3216 1.87707 9.6273 2.13599 9.08238 2.6084C8.92783 2.7439 8.68906 2.72705 8.55357 2.57068C8.41807 2.41394 8.43492 2.17736 8.59165 2.0415C9.28525 1.43945 10.1612 1.09228 11.0929 1.12744C12.0941 1.14098 13.0309 1.54492 13.7296 2.26489C14.4276 2.98486 14.8048 3.93444 14.7909 4.93896C14.777 5.99072 14.3338 6.95532 13.5919 7.64306ZM10.5567 7.10913C10.5436 8.06347 10.1649 8.92187 9.57018 9.57812C10.3334 9.9447 11.9615 10.973 12.5482 13.2747C12.7313 13.9921 12.6068 14.6326 12.1776 15.1786C11.2101 16.4109 8.85533 16.8562 7.0294 16.8734L7.01549 16.8763L6.94152 16.8767H6.92247H6.91735L6.79356 16.876L6.79137 16.8756C5.87218 16.8613 2.77916 16.6976 1.57579 15.1665C1.14879 14.623 1.02428 13.9866 1.20592 13.2747C1.76622 11.0767 3.27794 10.0395 4.07775 9.63085C3.40905 8.91711 3.04357 7.99096 3.05748 7.00879C3.08678 4.93457 4.81163 3.24194 6.85802 3.29761C7.85997 3.31115 8.79674 3.71509 9.49473 4.43506C10.1935 5.15503 10.5707 6.10461 10.5567 7.10913ZM6.84777 4.04761L6.80749 4.04724C5.17199 4.04724 3.83019 5.37219 3.80749 7.01904C3.7965 7.82361 4.09826 8.58423 4.6571 9.16064C5.21594 9.7367 5.9652 10.0597 6.76647 10.0703L6.80676 10.0707C8.44225 10.0707 9.78405 8.74573 9.80676 7.09888C9.81774 6.29431 9.51599 5.53369 8.95715 4.95728C8.39831 4.38122 7.64904 4.05823 6.84777 4.04761ZM11.588 14.7153C10.8277 15.6829 8.68834 16.1183 6.95689 16.1252C5.20861 16.1399 2.94908 15.6997 2.16539 14.7029C1.88634 14.3477 1.81017 13.9408 1.93248 13.46C2.50597 11.2129 4.21544 10.3662 4.69664 10.1688C5.29943 10.5819 6.00914 10.8101 6.75621 10.8203L6.80675 10.8207C7.60655 10.8207 8.34556 10.5614 8.9564 10.1296C9.17906 10.208 11.1896 10.9807 11.8216 13.46C11.9454 13.9467 11.8692 14.3572 11.588 14.7153Z"
  />
)

const DEVICE_1PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M5.66152 1.04199H19.3385C20.7372 1.04199 21.875 2.17928 21.875 3.578V21.4216C21.875 22.8204 20.7372 23.9587 19.3385 23.9587H5.66152C4.2628 23.9587 3.125 22.8203 3.125 21.4216V3.578C3.125 2.17928 4.2628 1.04199 5.66152 1.04199ZM12.5 22.1572C12.9269 22.1572 13.273 21.8139 13.273 21.3904C13.273 20.9669 12.9269 20.6236 12.5 20.6236C12.0731 20.6236 11.727 20.9669 11.727 21.3904C11.727 21.8139 12.0731 22.1572 12.5 22.1572ZM5.66152 2.08366H19.3385C20.163 2.08366 20.8333 2.75403 20.8333 3.578V18.7869H4.16667V3.578C4.16667 2.75403 4.83703 2.08366 5.66152 2.08366ZM5.66152 22.917H19.3385C20.163 22.917 20.8333 22.2466 20.8333 21.4216V19.8286H4.16667V21.4216C4.16667 22.2466 4.83703 22.917 5.66152 22.917Z"
  />
)

const TIME_2PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M11.9997 1C5.92505 1 1 5.92505 1 11.9997C1 18.0756 5.92505 23 11.9997 23C18.0763 23 23 18.0756 23 11.9997C23 5.92505 18.0763 1 11.9997 1ZM11.9997 3C16.9625 3 21 7.03723 21 11.9997C21 16.9625 16.9625 21 11.9997 21C7.03723 21 3 16.9625 3 11.9997C3 7.03723 7.03723 3 11.9997 3ZM14.2642 15.96C14.4556 16.1333 14.6958 16.2188 14.9351 16.2188C15.2075 16.2188 15.4795 16.1079 15.6768 15.8901C16.0474 15.481 16.0161 14.8486 15.6069 14.4775L13 12.1157V6.85254C13 6.30029 12.5522 5.85254 12 5.85254C11.4478 5.85254 11 6.30029 11 6.85254V12.5591C11 12.8413 11.1191 13.1103 11.3286 13.3003L14.2642 15.96Z"
  />
)

const ALERT_1PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M3.48858 20.9981C3.05401 20.9933 2.63018 20.878 2.25713 20.6661C1.67412 20.3331 1.25811 19.794 1.08525 19.1495C0.913374 18.5069 1.00322 17.838 1.33818 17.2628L9.85382 3.1964C9.8543 3.19591 9.85455 3.19542 9.85479 3.19494C9.85503 3.19445 9.85528 3.19396 9.85577 3.19347C10.0648 2.85363 10.3568 2.56456 10.7005 2.35753C11.8831 1.6505 13.4271 2.02452 14.1448 3.19347L22.6585 17.256C22.8782 17.6329 22.9954 18.0538 23.0003 18.4815C23.015 19.8536 21.9017 20.9825 20.5179 20.9981H3.48858ZM11.5003 13.4981C11.5003 13.7745 11.7239 13.9981 12.0003 13.9981C12.2767 13.9981 12.5003 13.7745 12.5003 13.4981V7.49815C12.5003 7.22178 12.2767 6.99815 12.0003 6.99815C11.7239 6.99815 11.5003 7.22178 11.5003 7.49815V13.4981ZM12.0003 17.4979C12.5526 17.4979 13.0003 17.0502 13.0003 16.4979C13.0003 15.9456 12.5526 15.4979 12.0003 15.4979C11.448 15.4979 11.0003 15.9456 11.0003 16.4979C11.0003 17.0502 11.448 17.4979 12.0003 17.4979ZM2.19757 17.7735L10.7073 3.7169C10.8333 3.5128 11.0091 3.33897 11.2152 3.21495C11.929 2.78819 12.8607 3.01182 13.2913 3.71397L21.7991 17.7667C21.928 17.9874 21.9974 18.2384 22.0003 18.4923C22.0091 19.3136 21.3392 19.9884 20.512 19.9981H3.49444C3.23468 19.9952 2.97882 19.9259 2.75323 19.797C2.40362 19.5978 2.1546 19.2755 2.05108 18.8897C1.94854 18.5079 2.00225 18.1095 2.19757 17.7735Z"
  />
)

const MINUS_1PT = (
  <path d="M22.0455 13.7873H2.95455C2.42736 13.7873 2 13.3599 2 12.8327C2 12.3055 2.42736 11.8782 2.95455 11.8782H22.0455C22.5726 11.8782 23 12.3055 23 12.8327C23 13.3599 22.5726 13.7873 22.0455 13.7873Z" />
)

const PLUS_1PT = (
  <path d="M22 11H13V2C13 1.44775 12.5523 1 12 1C11.4477 1 11 1.44775 11 2V11H2C1.44775 11 1 11.4477 1 12C1 12.5523 1.44775 13 2 13H11V22C11 22.5523 11.4477 23 12 23C12.5523 23 13 22.5523 13 22V13H22C22.5523 13 23 12.5523 23 12C23 11.4477 22.5523 11 22 11Z" />
)

const ALERT_ROUND_1PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M1 12C1 5.9248 5.9248 1 12 1C18.0752 1 23 5.9248 23 12C23 18.0752 18.0752 23 12 23C5.9248 23 1 18.0752 1 12ZM22 12C22 6.48633 17.5142 2 12 2C6.48584 2 2 6.48633 2 12C2 17.5137 6.48584 22 12 22C17.5142 22 22 17.5137 22 12ZM12.0522 13.4756C11.7759 13.4756 11.5522 13.252 11.5522 12.9756V7.48047C11.5522 7.2041 11.7759 6.98047 12.0522 6.98047C12.3286 6.98047 12.5522 7.2041 12.5522 7.48047V12.9756C12.5522 13.252 12.3286 13.4756 12.0522 13.4756ZM12.0522 15.252C11.458 15.252 10.977 15.7334 10.977 16.3272C10.977 16.9219 11.458 17.4033 12.0522 17.4033C12.646 17.4033 13.1274 16.9219 13.1274 16.3272C13.1274 15.7334 12.646 15.252 12.0522 15.252Z"
  />
)

const ALERT_ROUND_RED_2PT = (
  <>
    <circle cx="12" cy="12" r="11" fill="#DB331A" />
    <rect x="11" y="6.5" width="2" height="7" rx="1" fill="#FFFFFF" />
    <circle cx="12" cy="16.5" r="1.25" fill="#FFFFFF" />
  </>
)

const REFRESH_1PT = (
  <path
    d="M22.5 13.3887C22.5031 13.3887 22.5057 13.3906 22.5088 13.3906C22.5474 13.3914 22.5844 13.4035 22.6221 13.4131C22.648 13.4198 22.6761 13.4211 22.7002 13.4316C22.7211 13.4409 22.738 13.4586 22.7578 13.4707C22.7933 13.4926 22.8296 13.5131 22.8584 13.543C22.8606 13.5452 22.864 13.5455 22.8662 13.5479C22.8846 13.5676 22.8926 13.5925 22.9072 13.6143C22.9274 13.6441 22.9494 13.6721 22.9629 13.7061C22.9781 13.7446 22.9821 13.7846 22.9873 13.8252C22.9902 13.8468 23 13.8662 23 13.8887V19.5566C23 19.833 22.7764 20.0566 22.5 20.0566C22.2236 20.0566 22 19.833 22 19.5566V15.0371L18.4121 18.373C16.6465 20.1201 14.3232 20.9951 11.999 20.9951C9.67187 20.9951 7.34277 20.1191 5.57031 18.3652C4.61721 17.4238 3.87501 16.2597 3.4248 15.002C3.33209 14.7412 3.46682 14.4551 3.72656 14.3623C3.98727 14.2725 4.27342 14.4053 4.36621 14.6641C4.76659 15.7832 5.4258 16.8164 6.27344 17.6543C9.42871 20.7764 14.5645 20.7754 17.7197 17.6514L21.2275 14.3887H16.7725C16.4961 14.3887 16.2725 14.165 16.2725 13.8887C16.2725 13.6123 16.4961 13.3887 16.7725 13.3887H22.5ZM8.97461 3.5166C13.6943 1.86234 18.9023 4.32037 20.5752 8.99805C20.668 9.2578 20.5332 9.54394 20.2725 9.63672C20.0117 9.72754 19.7266 9.59472 19.6338 9.33398C18.1445 5.17291 13.5146 2.98635 9.30469 4.45996C8.17482 4.85546 7.1289 5.50881 6.28027 6.34766L2.77246 9.61035H7.22754C7.50388 9.61035 7.72749 9.83402 7.72754 10.1104C7.72754 10.3867 7.50391 10.6104 7.22754 10.6104H1.5C1.43057 10.6104 1.36474 10.5958 1.30469 10.5703C1.30085 10.5687 1.29766 10.5653 1.29395 10.5635C1.23658 10.5372 1.18481 10.5009 1.1416 10.4561C1.1394 10.4538 1.13591 10.4535 1.13379 10.4512C1.11561 10.4316 1.10811 10.4072 1.09375 10.3857C1.07345 10.3558 1.05062 10.328 1.03711 10.2939C1.02185 10.2554 1.01795 10.2143 1.0127 10.1738C1.00969 10.1522 1 10.1328 1 10.1104V4.44336C1.00004 4.16704 1.22366 3.94336 1.5 3.94336C1.77634 3.94336 1.99996 4.16703 2 4.44336V8.96191L5.58789 5.62598C6.53026 4.69436 7.70511 3.96094 8.97461 3.5166Z"
    fill="inherit"
  />
)

const REFRESH_2PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M8.45459 10.0142C8.45459 10.596 8.00684 11.0668 7.45459 11.0668H2C1.86884 11.0668 1.73792 11.0389 1.61499 10.985C1.4914 10.9307 1.38123 10.8509 1.28864 10.752C1.28807 10.7514 1.28739 10.751 1.28671 10.7506C1.28599 10.7502 1.28526 10.7498 1.28467 10.7492C1.2714 10.7349 1.26284 10.7182 1.25427 10.7014C1.24744 10.6881 1.24059 10.6747 1.23133 10.6625C1.17151 10.584 1.11255 10.5048 1.07593 10.4102C1.04379 10.3271 1.03431 10.2396 1.0248 10.1517C1.02413 10.1455 1.02345 10.1393 1.02277 10.1331C1.0208 10.1156 1.01633 10.0989 1.01186 10.0823C1.00592 10.0603 1 10.0383 1 10.0142V4.04861C1 3.46685 1.44775 2.99609 2 2.99609C2.55225 2.99609 3 3.46685 3 4.04861V7.5149L5.51953 4.92536C6.4585 3.89752 7.64551 3.07935 8.93017 2.58187C11.2022 1.70203 13.6592 1.82332 15.8491 2.92312C18.1226 4.06403 19.833 6.10842 20.665 8.6801C20.8428 9.23 20.5635 9.82821 20.0405 10.0153C19.5176 10.2064 18.9492 9.90837 18.7715 9.35847C18.1147 7.32847 16.771 5.71783 14.9878 4.82257C13.2886 3.97049 11.3818 3.87593 9.6206 4.55739C8.61816 4.94489 7.69091 5.58524 6.93896 6.40855L4.45538 8.96173H7.45459C8.00684 8.96173 8.45459 9.43249 8.45459 10.0142ZM22.9751 13.8522C22.9758 13.8585 22.9765 13.8647 22.9772 13.8709C22.9791 13.8885 22.9836 13.9052 22.9881 13.9218C22.9941 13.9439 23 13.9659 23 13.99V19.9556C23 20.5374 22.5522 21.0081 22 21.0081C21.4478 21.0081 21 20.5374 21 19.9556V16.4893L18.4805 19.0788C16.7676 20.9536 14.457 21.999 11.9976 22H11.9951C9.53613 22 7.22607 20.9557 5.49073 19.0583C4.53175 18.0099 3.78663 16.7189 3.33497 15.3241C3.15723 14.7742 3.43653 14.176 3.95948 13.9889C4.48096 13.7988 5.04981 14.0948 5.22852 14.6457C5.58594 15.7496 6.1748 16.7713 6.93164 17.5987C8.28614 19.0788 10.0845 19.8949 11.9951 19.8949H11.9971C13.9077 19.8939 15.7065 19.0778 17.061 17.5956L19.5446 15.0425H16.5454C15.9931 15.0425 15.5454 14.5717 15.5454 13.9899C15.5454 13.4082 15.9931 12.9374 16.5454 12.9374H22C22.0016 12.9374 22.003 12.938 22.0044 12.9384C22.0054 12.9388 22.0064 12.939 22.0075 12.939C22.1295 12.9399 22.2504 12.9676 22.3655 13.015C22.4963 13.0694 22.6143 13.1485 22.7114 13.2522C22.712 13.253 22.7129 13.2534 22.7137 13.2538C22.7143 13.2542 22.7149 13.2545 22.7153 13.255C22.7285 13.2692 22.7371 13.2859 22.7456 13.3025C22.7524 13.3158 22.7592 13.3291 22.7684 13.3412C22.8284 13.4199 22.8874 13.4992 22.9241 13.5941C22.9562 13.6771 22.9656 13.7645 22.9751 13.8522Z"
  />
)

const DOWNLOAD = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask
      id="mask0_2198_155278"
      mask-type="alpha"
      maskUnits="userSpaceOnUse"
      x="1"
      y="3"
      width="18"
      height="14"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.4897 15.3285C14.5272 15.5295 14.7021 15.6699 14.8991 15.6699L14.9756 15.663C17.3039 15.2345 18.75 13.502 18.75 11.1415C18.75 8.57764 16.8099 6.56917 14.3327 6.56917C14.2163 6.56917 14.0894 6.57975 13.9575 6.59521C13.2633 4.65877 11.451 3.33594 9.43278 3.33594C6.75862 3.33594 4.58333 5.59465 4.58333 8.37134V8.37137C4.58333 8.44175 4.58333 8.51742 4.58496 8.59595C2.73519 8.65902 1.25 10.2386 1.25 12.1714C1.25 14.1208 2.18994 15.2622 4.12272 15.6602C4.34489 15.7057 4.56868 15.5625 4.61426 15.3363C4.66146 15.1108 4.51579 14.8903 4.29037 14.8439C2.76367 14.5298 2.08333 13.7054 2.08333 12.1714C2.08333 10.6581 3.25683 9.42725 4.69971 9.42725L4.80306 9.44026C5.02442 9.47444 5.22623 9.46224 5.34993 9.31697C5.42887 9.2242 5.46387 9.10172 5.44515 8.98169C5.4165 8.79814 5.41658 8.58549 5.41666 8.39947C5.41666 8.39002 5.41667 8.38064 5.41667 8.37134C5.41667 6.05445 7.21842 4.16927 9.43278 4.16927C11.1979 4.16927 12.7743 5.40259 13.2658 7.16854C13.3219 7.37076 13.5148 7.49935 13.7297 7.46842C13.7451 7.4661 13.7605 7.46377 13.7759 7.46143C13.9712 7.43187 14.1652 7.4025 14.3327 7.4025C16.3428 7.4025 17.9167 9.04476 17.9167 11.1415C17.9167 13.1032 16.7611 14.4871 14.8242 14.8435C14.598 14.885 14.4482 15.1023 14.4897 15.3285ZM12.1012 13.235C12.2616 13.071 12.526 13.0694 12.6904 13.2297C12.8548 13.3913 12.8564 13.6549 12.6953 13.8193L10.0187 16.5448C9.9406 16.6245 9.83317 16.6693 9.72168 16.6693L9.72006 16.6689L9.71924 16.6693C9.60042 16.6693 9.49463 16.6184 9.41894 16.5391L6.74723 13.8193C6.5861 13.655 6.58773 13.3913 6.75212 13.2301C6.9165 13.069 7.18017 13.071 7.34131 13.235L9.30258 15.2313V10.46C9.30258 10.2297 9.48893 10.0433 9.71924 10.0433C9.94955 10.0433 10.1359 10.2297 10.1359 10.46V15.2362L12.1012 13.235Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_2198_155278)">
      <rect width="20" height="20" fill="#06556E" />
    </g>
  </svg>
)

const PRINT = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask
      id="mask0_2198_155285"
      mask-type="alpha"
      maskUnits="userSpaceOnUse"
      x="1"
      y="1"
      width="18"
      height="18"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.6191 5.94971H16.7293C17.8434 5.94971 18.75 6.85018 18.75 7.95736V14.3661C18.75 15.4728 17.8434 16.3733 16.7293 16.3733H14.7917L14.6183 16.333L14.6191 18.3309C14.6191 18.4416 14.5752 18.5478 14.4971 18.6259C14.419 18.704 14.3131 18.748 14.2025 18.748L5.79997 18.7516C5.56966 18.7516 5.3833 18.5653 5.3833 18.3354L5.38248 16.3733H3.27067C2.15657 16.3733 1.25 15.4728 1.25 14.3661V7.95736C1.25 6.85018 2.15657 5.94971 3.27067 5.94971H5.38086V1.66667C5.38086 1.43636 5.56722 1.25 5.79752 1.25H14.2025C14.4328 1.25 14.6191 1.43636 14.6191 1.66667V5.94971ZM13.7858 2.08333H6.21419V5.94971H13.7858V2.08333ZM6.21663 17.9183L6.21419 14.1569L13.7834 14.1528L13.7858 17.9146L6.21663 17.9183ZM16.7293 15.54C17.3844 15.54 17.9167 15.0134 17.9167 14.366V7.95736C17.9167 7.30997 17.3844 6.78304 16.7293 6.78304H14.2025H5.79752H3.27067C2.61556 6.78304 2.08333 7.30997 2.08333 7.95736V14.366C2.08333 15.0134 2.61556 15.5399 3.27067 15.5399H5.38167L5.38086 13.7406C5.38086 13.63 5.4248 13.5238 5.50292 13.4456C5.58105 13.3675 5.68685 13.3236 5.79752 13.3236L14.2 13.3195C14.4303 13.3195 14.6167 13.5059 14.6167 13.7358L14.6175 15.5806L14.7917 15.54H16.7293Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_2198_155285)">
      <rect width="20" height="20" fill="#06556E" />
    </g>
  </svg>
)

const SEND = (
  <>
    <mask
      id="mask0_2198_155298"
      mask-type="alpha"
      maskUnits="userSpaceOnUse"
      x="1"
      y="1"
      width="24"
      height="24"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.7486 1.69511C18.7551 1.6141 18.7388 1.53595 18.7006 1.46553L18.6973 1.44884L18.6737 1.42075L18.6306 1.36947L18.5752 1.32347L18.5492 1.30149L18.5337 1.29823C18.4637 1.26078 18.3855 1.24491 18.3041 1.25142L18.2683 1.25549L18.2227 1.26038L1.5513 6.06546C1.39175 6.11147 1.27454 6.24823 1.25337 6.41349C1.23221 6.57874 1.31198 6.74034 1.45526 6.825L8.84262 11.1848L13.2514 18.5473C13.3271 18.6743 13.4639 18.75 13.6088 18.75L13.6633 18.7463C13.8286 18.7248 13.9645 18.6067 14.0101 18.4468L18.7397 1.77447L18.7445 1.72767L18.7486 1.69511ZM16.9048 2.50755L9.07868 10.3561L2.71211 6.59869L16.9048 2.50755ZM9.66967 10.9442L13.4712 17.2916L17.4991 3.09328L9.66967 10.9442Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_2198_155298)">
      <rect width="24" height="24" fill="#06556E" />
    </g>
  </>
)

const MY_LOCATION_1PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M10.5131 15.1C10.557 15.2396 10.6868 15.3343 10.8331 15.3334C10.9807 15.3296 11.1081 15.2292 11.1465 15.0867L15.8131 1.0867C15.8519 0.967499 15.8204 0.836654 15.7318 0.748031C15.6432 0.659407 15.5123 0.627986 15.3931 0.666702L1.39314 5.33337C1.25135 5.38103 1.15868 5.51732 1.16647 5.6667C1.15955 5.8179 1.25537 5.95479 1.39981 6.00004L8.29981 8.20004L10.5131 15.1ZM8.66647 7.62004L2.57314 5.66004L14.9731 1.5267L10.8398 13.9267L8.87981 7.83337C8.84587 7.73287 8.76697 7.65398 8.66647 7.62004Z"
  />
)

const BUILDING_1PT = (
  <path
    fill-rule="evenodd"
    clip-rule="evenodd"
    d="M3 2.12366V24.0753H2V2.12366C2 1.57137 2.44771 1.12366 3 1.12366H13.2258C13.7781 1.12366 14.2258 1.57137 14.2258 2.12366V24.0753H13.2258V2.12366H3ZM21.3333 12.1022H14.3495V11.1022H21.3333C21.8856 11.1022 22.3333 11.5499 22.3333 12.1022V24.0753H21.3333V12.1022ZM4.77332 5.23117C4.77332 4.95503 4.99717 4.73117 5.27332 4.73117H6.84841C7.12455 4.73117 7.34841 4.95503 7.34841 5.23117V6.98386C7.34841 7.26 7.12455 7.48386 6.84841 7.48386H5.27332C4.99717 7.48386 4.77332 7.26 4.77332 6.98386V5.23117ZM4.77332 10.7366C4.77332 10.4604 4.99717 10.2366 5.27332 10.2366H6.84841C7.12455 10.2366 7.34841 10.4604 7.34841 10.7366V12.4893C7.34841 12.7654 7.12455 12.9893 6.84841 12.9893H5.27332C4.99717 12.9893 4.77332 12.7654 4.77332 12.4893V10.7366ZM5.27332 15.7419C4.99717 15.7419 4.77332 15.9658 4.77332 16.2419V17.9946C4.77332 18.2708 4.99717 18.4946 5.27332 18.4946H6.84841C7.12455 18.4946 7.34841 18.2708 7.34841 17.9946V16.2419C7.34841 15.9658 7.12455 15.7419 6.84841 15.7419H5.27332ZM4.77332 21.7473C4.77332 21.4712 4.99717 21.2473 5.27332 21.2473H6.84841C7.12455 21.2473 7.34841 21.4712 7.34841 21.7473V23.5C7.34841 23.7761 7.12455 24 6.84841 24H5.27332C4.99717 24 4.77332 23.7761 4.77332 23.5V21.7473ZM10.4235 4.73117C10.1474 4.73117 9.92349 4.95503 9.92349 5.23117V6.98386C9.92349 7.26 10.1474 7.48386 10.4235 7.48386H11.9986C12.2747 7.48386 12.4986 7.26 12.4986 6.98386V5.23117C12.4986 4.95503 12.2747 4.73117 11.9986 4.73117H10.4235ZM9.92349 16.2419C9.92349 15.9658 10.1474 15.7419 10.4235 15.7419H11.9986C12.2747 15.7419 12.4986 15.9658 12.4986 16.2419V17.9946C12.4986 18.2708 12.2747 18.4946 11.9986 18.4946H10.4235C10.1474 18.4946 9.92349 18.2708 9.92349 17.9946V16.2419ZM17.1488 15.7419C16.8727 15.7419 16.6488 15.9658 16.6488 16.2419V17.9946C16.6488 18.2708 16.8727 18.4946 17.1488 18.4946H18.7239C19 18.4946 19.2239 18.2708 19.2239 17.9946V16.2419C19.2239 15.9658 19 15.7419 18.7239 15.7419H17.1488ZM9.92349 21.7473C9.92349 21.4712 10.1474 21.2473 10.4235 21.2473H11.9986C12.2747 21.2473 12.4986 21.4712 12.4986 21.7473V23.5C12.4986 23.7761 12.2747 24 11.9986 24H10.4235C10.1474 24 9.92349 23.7761 9.92349 23.5V21.7473ZM10.4235 10.2366C10.1474 10.2366 9.92349 10.4604 9.92349 10.7366V12.4893C9.92349 12.7654 10.1474 12.9893 10.4235 12.9893H11.9986C12.2747 12.9893 12.4986 12.7654 12.4986 12.4893V10.7366C12.4986 10.4604 12.2747 10.2366 11.9986 10.2366H10.4235ZM16.6488 21.7473C16.6488 21.4712 16.8727 21.2473 17.1488 21.2473H18.7239C19 21.2473 19.2239 21.4712 19.2239 21.7473V23.5C19.2239 23.7761 19 24 18.7239 24H17.1488C16.8727 24 16.6488 23.7761 16.6488 23.5V21.7473Z"
  />
)

const BUILDING_2PT = (
  <>
    <path d="M20 10.3823H14.3633V4C14.3627 3.46983 14.1519 2.96153 13.777 2.5866C13.4022 2.21167 12.8939 2.00071 12.3638 2H4C3.46973 2.00053 2.96133 2.21141 2.58637 2.58637C2.21141 2.96133 2.00053 3.46973 2 4V22H4V4H12.3633V22H14.3633V12.3823H20V22H22V12.3823C21.9994 11.8521 21.7885 11.3437 21.4135 10.9688C21.0386 10.5938 20.5302 10.3829 20 10.3823Z" />
    <path d="M17.4999 14.6666H16.4999C16.2237 14.6666 15.9999 14.8905 15.9999 15.1666V16.3888C15.9999 16.6649 16.2237 16.8888 16.4999 16.8888H17.4999C17.776 16.8888 17.9999 16.6649 17.9999 16.3888V15.1666C17.9999 14.8905 17.776 14.6666 17.4999 14.6666Z" />
    <path d="M17.4999 19.1111H16.4999C16.2237 19.1111 15.9999 19.3349 15.9999 19.6111V20.8333C15.9999 21.1095 16.2237 21.3333 16.4999 21.3333H17.4999C17.776 21.3333 17.9999 21.1095 17.9999 20.8333V19.6111C17.9999 19.3349 17.776 19.1111 17.4999 19.1111Z" />
    <path d="M7.00031 5.77777H6.00031C5.72417 5.77777 5.50031 6.00163 5.50031 6.27777V7.5C5.50031 7.77614 5.72417 8 6.00031 8H7.00031C7.27645 8 7.50031 7.77614 7.50031 7.5V6.27777C7.50031 6.00163 7.27645 5.77777 7.00031 5.77777Z" />
    <path d="M7.00031 10.2223H6.00031C5.72417 10.2223 5.50031 10.4461 5.50031 10.7223V11.9445C5.50031 12.2207 5.72417 12.4445 6.00031 12.4445H7.00031C7.27645 12.4445 7.50031 12.2207 7.50031 11.9445V10.7223C7.50031 10.4461 7.27645 10.2223 7.00031 10.2223Z" />
    <path d="M7.00031 14.6667H6.00031C5.72417 14.6667 5.50031 14.8905 5.50031 15.1667V16.3889C5.50031 16.6651 5.72417 16.8889 6.00031 16.8889H7.00031C7.27645 16.8889 7.50031 16.6651 7.50031 16.3889V15.1667C7.50031 14.8905 7.27645 14.6667 7.00031 14.6667Z" />
    <path d="M7.00031 19.1112H6.00031C5.72417 19.1112 5.50031 19.3351 5.50031 19.6112V20.8334C5.50031 21.1096 5.72417 21.3334 6.00031 21.3334H7.00031C7.27645 21.3334 7.50031 21.1096 7.50031 20.8334V19.6112C7.50031 19.3351 7.27645 19.1112 7.00031 19.1112Z" />
    <path d="M10.5002 5.77777H9.50018C9.22404 5.77777 9.00018 6.00163 9.00018 6.27777V7.5C9.00018 7.77614 9.22404 8 9.50018 8H10.5002C10.7763 8 11.0002 7.77614 11.0002 7.5V6.27777C11.0002 6.00163 10.7763 5.77777 10.5002 5.77777Z" />
    <path d="M10.5002 10.2223H9.50018C9.22404 10.2223 9.00018 10.4461 9.00018 10.7223V11.9445C9.00018 12.2207 9.22404 12.4445 9.50018 12.4445H10.5002C10.7763 12.4445 11.0002 12.2207 11.0002 11.9445V10.7223C11.0002 10.4461 10.7763 10.2223 10.5002 10.2223Z" />
    <path d="M10.5002 14.6667H9.50018C9.22404 14.6667 9.00018 14.8905 9.00018 15.1667V16.3889C9.00018 16.6651 9.22404 16.8889 9.50018 16.8889H10.5002C10.7763 16.8889 11.0002 16.6651 11.0002 16.3889V15.1667C11.0002 14.8905 10.7763 14.6667 10.5002 14.6667Z" />
    <path d="M10.5002 19.1112H9.50018C9.22404 19.1112 9.00018 19.3351 9.00018 19.6112V20.8334C9.00018 21.1096 9.22404 21.3334 9.50018 21.3334H10.5002C10.7763 21.3334 11.0002 21.1096 11.0002 20.8334V19.6112C11.0002 19.3351 10.7763 19.1112 10.5002 19.1112Z" />
  </>
)

const PROFILE_1PT = (
  <path
    fill-rule="evenodd"
    clip-rule="evenodd"
    d="M1 11.9995C1 5.9248 5.9248 1 11.9995 1C18.0762 1 23 5.9248 23 11.9995C23 18.0757 18.0762 23 11.9995 23C5.9248 23 1 18.0757 1 11.9995ZM5.41943 19.3008L5.32324 19.4233C7.0957 21.019 9.43262 22 11.9995 22C14.4609 22 16.7134 21.1021 18.4575 19.6221C17.5293 16.7529 15.1748 15.7305 14.7475 15.5674C13.917 16.1621 12.9077 16.521 11.8105 16.521C10.7373 16.521 9.74462 16.1812 8.92382 15.6094C8.32959 15.8608 6.33984 16.8594 5.41943 19.3008ZM11.8105 15.521C9.56641 15.521 7.74073 13.6953 7.74073 11.4517C7.74073 9.20752 9.56641 7.38184 11.8105 7.38184C14.0542 7.38184 15.8799 9.20752 15.8799 11.4517C15.8799 13.6953 14.0542 15.521 11.8105 15.521ZM15.5605 14.8325C16.4922 15.2852 18.3115 16.4424 19.2534 18.8633C20.9507 17.0703 22 14.6577 22 11.9995C22 6.48584 17.5137 2 11.9995 2C6.48584 2 2 6.48584 2 11.9995C2 14.5752 2.9873 16.9189 4.5923 18.6934C5.51857 16.4775 7.17237 15.3613 8.10548 14.8872C7.26563 13.9819 6.74073 12.7808 6.74073 11.4517C6.74073 8.65625 9.01514 6.38184 11.8105 6.38184C14.606 6.38184 16.8799 8.65625 16.8799 11.4517C16.8799 12.7554 16.3716 13.9336 15.5605 14.8325Z"
  />
)

const PROFILE_2PT = (
  <path
    fill-rule="evenodd"
    clip-rule="evenodd"
    d="M1 12C1 5.925 5.925 1 12 1c6.076 0 11 4.925 11 11 0 6.076-4.924 11-11 11-6.075 0-11-4.924-11-11Zm20 0c0-4.963-4.038-9-9-9-4.963 0-9 4.037-9 9a8.94 8.94 0 0 0 1.749 5.308 8.068 8.068 0 0 1 2.585-2.556 5.532 5.532 0 0 1-1.093-3.3 5.576 5.576 0 0 1 5.57-5.57 5.576 5.576 0 0 1 5.569 5.57 5.53 5.53 0 0 1-1.046 3.236 8.017 8.017 0 0 1 2.797 2.78A8.944 8.944 0 0 0 21 12Zm-12.76-.548a3.574 3.574 0 0 1 3.57-3.57 3.574 3.574 0 0 1 3.57 3.57 3.573 3.573 0 0 1-3.57 3.569 3.574 3.574 0 0 1-3.57-3.57Zm.637 4.725a6.042 6.042 0 0 0-2.696 2.677A8.954 8.954 0 0 0 12 21c2.149 0 4.123-.76 5.672-2.02a6.018 6.018 0 0 0-2.861-2.844 5.53 5.53 0 0 1-5.934.041Z"
  />
)

const DUPLICATE_ICON_2PT = (
  <>
    <path
      d="M19.94824,23h-9.23047c-1.68262,0-3.05078-1.37402-3.05078-3.06299v-9.28369c0-1.68896,1.36816-3.06299,3.05078-3.06299
		h9.23047C21.63086,7.59033,23,8.96436,23,10.65332v9.28369C23,21.62598,21.63086,23,19.94824,23z M10.71777,9.59033
		c-0.5791,0-1.05078,0.47705-1.05078,1.06299v9.28369C9.66699,20.52295,10.13867,21,10.71777,21h9.23047
		C20.52832,21,21,20.52295,21,19.93701v-9.28369c0-0.58594-0.47168-1.06299-1.05176-1.06299H10.71777z"
    />

    <path
      d="M5.07715,16.40967H4.05176C2.36914,16.40967,1,15.03564,1,13.34668V4.06299C1,2.37402,2.36914,1,4.05176,1h9.23047
		c1.68262,0,3.05078,1.37402,3.05078,3.06299v1.03174c0,0.55225-0.44727,1-1,1s-1-0.44775-1-1V4.06299
		C14.33301,3.47705,13.86133,3,13.28223,3H4.05176C3.47168,3,3,3.47705,3,4.06299v9.28369
		c0,0.58594,0.47168,1.06299,1.05176,1.06299h1.02539c0.55273,0,1,0.44775,1,1S5.62988,16.40967,5.07715,16.40967z"
    />
  </>
)

const NOTE_1PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M20.4609 8.80762L20.3525 8.64551L13.3545 1.64746L13.1924 1.53906L13 1.5H6C4.62109 1.5 3.5 2.62109 3.5 4V20C3.5 21.3789 4.62109 22.5 6 22.5H18C19.3789 22.5 20.5 21.3789 20.5 20V9L20.4609 8.80762ZM13.5 3.20703L18.793 8.5H13.5V3.20703ZM6 21.5H18C18.8272 21.5 19.5 20.8272 19.5 20V9.5H13C12.7236 9.5 12.5 9.27637 12.5 9V2.5H6C5.17285 2.5 4.5 3.17285 4.5 4V20C4.5 20.8272 5.17285 21.5 6 21.5ZM15.8525 13.5488H8.04004C7.76367 13.5488 7.54004 13.3252 7.54004 13.0488C7.54004 12.7725 7.76367 12.5488 8.04004 12.5488H15.8525C16.1289 12.5488 16.3525 12.7725 16.3525 13.0488C16.3525 13.3252 16.1289 13.5488 15.8525 13.5488ZM8.04004 17.6113H15.8525C16.1289 17.6113 16.3525 17.3877 16.3525 17.1113C16.3525 16.835 16.1289 16.6113 15.8525 16.6113H8.04004C7.76367 16.6113 7.54004 16.835 7.54004 17.1113C7.54004 17.3877 7.76367 17.6113 8.04004 17.6113Z"
  />
)

const NOTE_2PT = (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M20.9224 8.61511C20.8715 8.49322 20.7978 8.38342 20.7057 8.29168L13.7083 1.29424C13.6166 1.20214 13.5068 1.12847 13.3849 1.07757C13.2641 1.02716 13.1337 1 13 1H6C4.3457 1 3 2.3457 3 4V20C3 21.6543 4.3457 23 6 23H18C19.6543 23 21 21.6543 21 20V9C21 8.86633 20.9728 8.7359 20.9224 8.61511ZM14 4L18 8H14V4ZM6 21H18C18.5518 21 19 20.5518 19 20V10H13C12.4473 10 12 9.55273 12 9V3H6C5.44824 3 5 3.44824 5 4V20C5 20.5518 5.44824 21 6 21ZM8.01911 14C7.45582 14 7 13.5527 7 13C7 12.4473 7.45582 12 8.01911 12H15.9809C16.5442 12 17 12.4473 17 13C17 13.5527 16.5442 14 15.9809 14H8.01911ZM7 17C7 17.5527 7.45582 18 8.01911 18H15.9809C16.5442 18 17 17.5527 17 17C17 16.4473 16.5442 16 15.9809 16H8.01911C7.45582 16 7 16.4473 7 17Z"
  />
)

const ARCHIVE_1PT = (
  <path
    fill-rule="evenodd"
    clip-rule="evenodd"
    d="M3 4.736V8.00653H21V4.736C21 3.779 20.228 3 19.277 3H4.723C3.772 3 3 3.779 3 4.736ZM15.5 12.5C15.5 12.776 15.276 13 15 13H9C8.724 13 8.5 12.776 8.5 12.5C8.5 12.224 8.724 12 9 12H15C15.276 12 15.5 12.224 15.5 12.5ZM20 19.184C20 20.141 19.228 20.92 18.277 20.92H5.723C4.772 20.92 4 20.141 4 19.184V9H20V19.184ZM4.723 2C3.222 2 2 3.228 2 4.736L2 8.50524C2 8.78124 2.224 9.00524 2.5 9.00524H3V19.2631C3 20.7711 4.222 21.9991 5.723 21.9991H18.277C19.778 21.9991 21 20.7711 21 19.2631V9.00524H21.5C21.776 9.00524 22 8.78124 22 8.50524V4.736C22 3.228 20.778 2 19.277 2H4.723Z"
  />
)

const CAMERA_1PT = (
  <path
    fill-rule="evenodd"
    clip-rule="evenodd"
    d="M17 5C17 3.89543 16.1046 3 15 3H9C7.89543 3 7 3.89543 7 5H3C1.89543 5 1 5.89543 1 7V19C1 20.1046 1.89543 21 3 21H21C22.1046 21 23 20.1046 23 19V7C23 5.89543 22.1046 5 21 5H17ZM9 4H15C15.5523 4 16 4.44772 16 5V6H21C21.5523 6 22 6.44772 22 7V19C22 19.5523 21.5523 20 21 20H3C2.44772 20 2 19.5523 2 19V7C2 6.44772 2.44772 6 3 6H8V5C8 4.44772 8.44772 4 9 4ZM17 13C17 15.7614 14.7614 18 12 18C9.23858 18 7 15.7614 7 13C7 10.2386 9.23858 8 12 8C14.7614 8 17 10.2386 17 13ZM8 13C8 15.2091 9.79086 17 12 17C14.2091 17 16 15.2091 16 13C16 10.7909 14.2091 9 12 9C9.79086 9 8 10.7909 8 13ZM18.5 9.5C19.0523 9.5 19.5 9.05228 19.5 8.5C19.5 7.94772 19.0523 7.5 18.5 7.5C17.9477 7.5 17.5 7.94772 17.5 8.5C17.5 9.05228 17.9477 9.5 18.5 9.5Z"
  />
)

const CAMERAFLIP_1PT = (
  <>
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M17 5C17 3.89543 16.1046 3 15 3H9C7.89543 3 7 3.89543 7 5H3C1.89543 5 1 5.89543 1 7V19C1 20.1046 1.89543 21 3 21H21C22.1046 21 23 20.1046 23 19V7C23 5.89543 22.1046 5 21 5H17ZM9 4H15C15.5523 4 16 4.44772 16 5V6H21C21.5523 6 22 6.44772 22 7V19C22 19.5523 21.5523 20 21 20H3C2.44772 20 2 19.5523 2 19V7C2 6.44772 2.44772 6 3 6H8V5C8 4.44772 8.44772 4 9 4Z"
    />

    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M9.61377 12.3053H6.75C6.71524 12.3053 6.68222 12.2981 6.65216 12.2853C6.65114 12.2849 6.65027 12.2842 6.64939 12.2835C6.64857 12.2828 6.64774 12.2821 6.6468 12.2817C6.61811 12.2686 6.59223 12.2507 6.57062 12.2283C6.57009 12.2278 6.56944 12.2274 6.56879 12.227C6.56812 12.2267 6.56745 12.2263 6.5669 12.2257C6.56131 12.2197 6.55772 12.2128 6.55413 12.2059C6.55183 12.2014 6.54953 12.197 6.5467 12.1928C6.54447 12.1895 6.54219 12.1863 6.53992 12.1831C6.53178 12.1716 6.52374 12.1602 6.51846 12.1468C6.51177 12.1299 6.50959 12.1124 6.50738 12.0947C6.50707 12.0922 6.50676 12.0897 6.50644 12.0872C6.50583 12.0827 6.50462 12.0783 6.50341 12.074C6.5017 12.068 6.5 12.0619 6.5 12.0553V9.22178C6.5 9.0836 6.61182 8.97178 6.75 8.97178C6.88818 8.97178 7 9.0836 7 9.22178V11.4812L8.79395 9.81309C9.26514 9.34727 9.85254 8.98057 10.4873 8.7584C12.8472 7.93125 15.4512 9.16026 16.2876 11.4991C16.334 11.629 16.2666 11.7721 16.1362 11.8185C16.0059 11.8639 15.8633 11.7975 15.8169 11.6671C15.0723 9.58653 12.7573 8.49327 10.6523 9.23008C10.0874 9.42784 9.56446 9.7545 9.14014 10.1739L7.38608 11.8053H9.61377C9.75196 11.8053 9.86377 11.9171 9.86377 12.0553C9.86377 12.1935 9.75196 12.3053 9.61377 12.3053ZM17.4927 13.9054C17.493 13.9079 17.4933 13.9103 17.4936 13.9128C17.4942 13.9173 17.4954 13.9216 17.4966 13.9259C17.4983 13.9319 17.5 13.9379 17.5 13.9444V16.7784C17.5 16.9166 17.3882 17.0284 17.25 17.0284C17.1118 17.0284 17 16.9166 17 16.7784V14.5185L15.2061 16.1866C14.3232 17.0602 13.1616 17.4977 11.9995 17.4977C10.8359 17.4977 9.67138 17.0597 8.78515 16.1827C8.30859 15.712 7.9375 15.13 7.71239 14.5011C7.66601 14.3707 7.73339 14.2276 7.86327 14.1813C7.99364 14.1363 8.13671 14.2027 8.1831 14.3321C8.38329 14.8917 8.71288 15.4083 9.13671 15.8273C10.7143 17.3883 13.2822 17.3878 14.8599 15.8258L16.6139 14.1944H14.3862C14.248 14.1944 14.1362 14.0826 14.1362 13.9444C14.1362 13.8063 14.248 13.6944 14.3862 13.6944H17.25C17.2508 13.6944 17.2515 13.6947 17.2522 13.6949C17.2529 13.6951 17.2536 13.6953 17.2544 13.6953C17.2698 13.6956 17.2845 13.6996 17.2994 13.7037C17.3032 13.7047 17.307 13.7057 17.3109 13.7067C17.3157 13.708 17.3207 13.7088 17.3257 13.7097C17.334 13.7111 17.3423 13.7125 17.3499 13.7158C17.3562 13.7186 17.3618 13.7229 17.3675 13.7272C17.3712 13.73 17.3749 13.7329 17.3789 13.7353C17.3814 13.7369 17.384 13.7385 17.3866 13.74C17.4018 13.7493 17.417 13.7585 17.4293 13.7713C17.4299 13.7719 17.4305 13.7723 17.4312 13.7726C17.4319 13.773 17.4325 13.7734 17.4331 13.774C17.4387 13.7801 17.4424 13.787 17.446 13.794C17.4483 13.7984 17.4506 13.8029 17.4534 13.8071C17.4557 13.8105 17.4581 13.8138 17.4604 13.8172C17.4684 13.8285 17.4763 13.8397 17.4815 13.8529C17.4883 13.8699 17.4905 13.8875 17.4927 13.9054Z"
    />

    <path d="M6.65216 12.2853L6.73048 12.1013L6.7285 12.1005L6.65216 12.2853ZM6.64939 12.2835L6.77597 12.1286L6.77595 12.1286L6.64939 12.2835ZM6.6468 12.2817L6.56361 12.4636L6.56432 12.4639L6.6468 12.2817ZM6.57062 12.2283L6.71467 12.0896L6.71396 12.0888L6.57062 12.2283ZM6.56879 12.227L6.66657 12.0526L6.66656 12.0526L6.56879 12.227ZM6.5669 12.2257L6.7139 12.0901L6.71322 12.0893L6.5669 12.2257ZM6.55413 12.2059L6.37663 12.298L6.37663 12.298L6.55413 12.2059ZM6.5467 12.1928L6.71267 12.0812L6.71216 12.0804L6.5467 12.1928ZM6.53992 12.1831L6.70324 12.0676L6.70324 12.0676L6.53992 12.1831ZM6.51846 12.1468L6.70454 12.0735L6.70444 12.0732L6.51846 12.1468ZM6.50738 12.0947L6.30892 12.1194L6.30892 12.1194L6.50738 12.0947ZM6.50644 12.0872L6.70478 12.0615L6.70467 12.0606L6.50644 12.0872ZM6.50341 12.074L6.69596 12.0199L6.69596 12.0199L6.50341 12.074ZM7 11.4812H6.8V11.9403L7.13619 11.6277L7 11.4812ZM8.79395 9.81309L8.9302 9.95962L8.93455 9.95532L8.79395 9.81309ZM10.4873 8.7584L10.5534 8.94717L10.5535 8.94714L10.4873 8.7584ZM16.2876 11.4991L16.4759 11.4318L16.4759 11.4318L16.2876 11.4991ZM16.1362 11.8185L16.202 12.0073L16.2033 12.0069L16.1362 11.8185ZM15.8169 11.6671L16.0053 11.6001L16.0052 11.5997L15.8169 11.6671ZM10.6523 9.23008L10.5863 9.04131L10.5863 9.04131L10.6523 9.23008ZM9.14014 10.1739L9.27641 10.3205L9.28074 10.3162L9.14014 10.1739ZM7.38608 11.8053L7.24987 11.6588L6.87736 12.0053H7.38608V11.8053ZM17.4936 13.9128L17.2953 13.9383L17.2954 13.9391L17.4936 13.9128ZM17.4927 13.9054L17.2942 13.93L17.2942 13.93L17.4927 13.9054ZM17.4966 13.9259L17.3041 13.98L17.3041 13.98L17.4966 13.9259ZM17 14.5185H17.2V14.0594L16.8638 14.372L17 14.5185ZM15.2061 16.1866L15.0698 16.0401L15.0654 16.0445L15.2061 16.1866ZM8.78515 16.1827L8.92583 16.0406L8.9257 16.0404L8.78515 16.1827ZM7.71239 14.5011L7.52397 14.5681L7.52409 14.5685L7.71239 14.5011ZM7.86327 14.1813L7.79812 13.9922L7.79601 13.9929L7.86327 14.1813ZM8.1831 14.3321L8.37141 14.2648L8.37137 14.2646L8.1831 14.3321ZM9.13671 15.8273L9.27739 15.6851L9.27731 15.685L9.13671 15.8273ZM14.8599 15.8258L14.7236 15.6793L14.7191 15.6837L14.8599 15.8258ZM16.6139 14.1944L16.7501 14.3409L17.1226 13.9944H16.6139V14.1944ZM17.2522 13.6949L17.3108 13.5037L17.3107 13.5036L17.2522 13.6949ZM17.2544 13.6953L17.2505 13.8953L17.2506 13.8953L17.2544 13.6953ZM17.2994 13.7037L17.3518 13.5106L17.3518 13.5106L17.2994 13.7037ZM17.3109 13.7067L17.3609 13.5131L17.3602 13.5129L17.3109 13.7067ZM17.3257 13.7097L17.2921 13.9068L17.2921 13.9068L17.3257 13.7097ZM17.3499 13.7158L17.4301 13.5326L17.43 13.5325L17.3499 13.7158ZM17.3675 13.7272L17.4891 13.5684L17.4891 13.5684L17.3675 13.7272ZM17.3789 13.7353L17.4841 13.5652L17.4835 13.5648L17.3789 13.7353ZM17.3866 13.74L17.4906 13.5692L17.4905 13.5692L17.3866 13.74ZM17.4293 13.7713L17.2854 13.9102L17.286 13.9109L17.4293 13.7713ZM17.4312 13.7726L17.3312 13.9458L17.3313 13.9459L17.4312 13.7726ZM17.4331 13.774L17.2863 13.9099L17.2868 13.9104L17.4331 13.774ZM17.446 13.794L17.6235 13.7018L17.6234 13.7018L17.446 13.794ZM17.4534 13.8071L17.2874 13.9186L17.2877 13.919L17.4534 13.8071ZM17.4604 13.8172L17.297 13.9325L17.297 13.9325L17.4604 13.8172ZM17.4815 13.8529L17.2956 13.9265L17.2956 13.9266L17.4815 13.8529ZM6.75 12.5053H9.61377V12.1053H6.75V12.5053ZM6.57385 12.4693C6.62783 12.4923 6.68743 12.5053 6.75 12.5053V12.1053C6.74305 12.1053 6.73661 12.1039 6.73048 12.1013L6.57385 12.4693ZM6.52282 12.4383C6.52287 12.4384 6.52332 12.4387 6.52383 12.4391C6.52438 12.4396 6.52523 12.4403 6.52625 12.4411C6.52825 12.4426 6.53149 12.4452 6.53559 12.448C6.54362 12.4537 6.55732 12.4625 6.57583 12.4702L6.7285 12.1005C6.74599 12.1077 6.75874 12.116 6.76586 12.121C6.77228 12.1255 6.77739 12.1298 6.77597 12.1286L6.52282 12.4383ZM6.56432 12.4639C6.54907 12.457 6.5379 12.4495 6.53168 12.4451C6.52606 12.4411 6.52155 12.4373 6.52283 12.4383L6.77595 12.1286C6.7759 12.1286 6.77549 12.1282 6.77504 12.1279C6.77454 12.1275 6.77379 12.1268 6.77288 12.1261C6.7711 12.1247 6.76822 12.1225 6.7646 12.1199C6.75752 12.1148 6.74547 12.1068 6.72927 12.0995L6.56432 12.4639ZM6.42658 12.3671C6.46695 12.409 6.51397 12.4409 6.56361 12.4636L6.72998 12.0998C6.72225 12.0963 6.71751 12.0925 6.71467 12.0896L6.42658 12.3671ZM6.47101 12.4015C6.47242 12.4023 6.46625 12.3989 6.45938 12.3944C6.4516 12.3892 6.43977 12.3806 6.42729 12.3678L6.71396 12.0888C6.70094 12.0755 6.68852 12.0664 6.68012 12.0608C6.67595 12.0581 6.67246 12.056 6.67025 12.0547C6.66913 12.054 6.66821 12.0535 6.66761 12.0532C6.66707 12.0528 6.66658 12.0526 6.66657 12.0526L6.47101 12.4015ZM6.41991 12.3613C6.43366 12.3762 6.44707 12.3863 6.45625 12.3924C6.46079 12.3955 6.46461 12.3978 6.46701 12.3992C6.46823 12.3999 6.46924 12.4005 6.46989 12.4009C6.47049 12.4012 6.47102 12.4015 6.47103 12.4015L6.66656 12.0526C6.66506 12.0517 6.67183 12.0554 6.67935 12.0604C6.68788 12.0662 6.70068 12.0757 6.71389 12.0901L6.41991 12.3613ZM6.37663 12.298C6.37851 12.3017 6.39353 12.333 6.42058 12.362L6.71322 12.0893C6.72203 12.0988 6.72759 12.1072 6.73036 12.1117C6.73259 12.1154 6.73395 12.1182 6.73162 12.1137L6.37663 12.298ZM6.38072 12.3044C6.37807 12.3004 6.37639 12.2975 6.37582 12.2965C6.37542 12.2957 6.37527 12.2954 6.37663 12.298L6.73162 12.1137C6.73013 12.1108 6.72283 12.0963 6.71267 12.0812L6.38072 12.3044ZM6.3766 12.2985C6.37905 12.302 6.38024 12.3037 6.38123 12.3051L6.71216 12.0804C6.7087 12.0753 6.70533 12.0706 6.70324 12.0676L6.3766 12.2985ZM6.33239 12.2201C6.34791 12.2595 6.37059 12.29 6.3766 12.2985L6.70324 12.0676C6.70106 12.0645 6.69991 12.0629 6.69894 12.0615C6.69806 12.0602 6.69792 12.06 6.69818 12.0604C6.69869 12.0612 6.70156 12.0659 6.70454 12.0735L6.33239 12.2201ZM6.30892 12.1194C6.31064 12.1332 6.31495 12.176 6.33249 12.2204L6.70444 12.0732C6.70704 12.0798 6.7078 12.0841 6.70771 12.0836C6.70767 12.0834 6.70748 12.0823 6.70715 12.08C6.70679 12.0774 6.70643 12.0746 6.70585 12.0699L6.30892 12.1194ZM6.3081 12.1129C6.30835 12.1148 6.3086 12.1168 6.30892 12.1194L6.70585 12.0699C6.70555 12.0675 6.70518 12.0645 6.70478 12.0615L6.3081 12.1129ZM6.31086 12.1281C6.31162 12.1308 6.31124 12.1296 6.3108 12.1277C6.31024 12.1254 6.30911 12.1205 6.30821 12.1138L6.70467 12.0606C6.70188 12.0399 6.69658 12.0222 6.69596 12.0199L6.31086 12.1281ZM6.3 12.0553C6.3 12.0937 6.31018 12.1257 6.31086 12.1281L6.69596 12.0199C6.69479 12.0158 6.69593 12.0195 6.69695 12.0245C6.69819 12.0305 6.7 12.0414 6.7 12.0553H6.3ZM6.3 9.22178V12.0553H6.7V9.22178H6.3ZM6.75 8.77178C6.50136 8.77178 6.3 8.97315 6.3 9.22178H6.7C6.7 9.19406 6.72227 9.17178 6.75 9.17178V8.77178ZM7.2 9.22178C7.2 8.97314 6.99864 8.77178 6.75 8.77178V9.17178C6.77773 9.17178 6.8 9.19405 6.8 9.22178H7.2ZM7.2 11.4812V9.22178H6.8V11.4812H7.2ZM8.65775 9.66663L6.86381 11.3348L7.13619 11.6277L8.93014 9.95956L8.65775 9.66663ZM10.4212 8.56963C9.7587 8.80152 9.14578 9.18403 8.65334 9.67086L8.93455 9.95532C9.38449 9.51051 9.94638 9.15962 10.5534 8.94717L10.4212 8.56963ZM16.4759 11.4318C15.602 8.98801 12.8834 7.70661 10.4211 8.56966L10.5535 8.94714C12.8109 8.15589 15.3004 9.3325 16.0993 11.5665L16.4759 11.4318ZM16.2033 12.0069C16.4382 11.9233 16.5594 11.6655 16.4759 11.4318L16.0992 11.5664C16.1086 11.5926 16.095 11.6208 16.0692 11.63L16.2033 12.0069ZM15.6285 11.7341C15.7125 11.9703 15.9701 12.0881 16.202 12.0073L16.0704 11.6296C16.0416 11.6396 16.0141 11.6246 16.0053 11.6001L15.6285 11.7341ZM10.7184 9.41885C12.7212 8.71783 14.9215 9.75889 15.6286 11.7345L16.0052 11.5997C15.223 9.41416 12.7935 8.26871 10.5863 9.04131L10.7184 9.41885ZM9.28074 10.3162C9.68396 9.91759 10.1814 9.60682 10.7184 9.41885L10.5863 9.04131C9.99336 9.24886 9.44495 9.5914 8.99954 10.0317L9.28074 10.3162ZM7.52229 11.9517L9.27635 10.3204L9.00393 10.0275L7.24987 11.6588L7.52229 11.9517ZM9.61377 11.6053H7.38608V12.0053H9.61377V11.6053ZM10.0638 12.0553C10.0638 11.8066 9.86241 11.6053 9.61377 11.6053V12.0053C9.6415 12.0053 9.66377 12.0276 9.66377 12.0553H10.0638ZM9.61377 12.5053C9.86241 12.5053 10.0638 12.3039 10.0638 12.0553H9.66377C9.66377 12.083 9.6415 12.1053 9.61377 12.1053V12.5053ZM17.692 13.8873C17.6917 13.8854 17.6915 13.8834 17.6912 13.8808L17.2942 13.93C17.2945 13.9323 17.2949 13.9353 17.2953 13.9383L17.692 13.8873ZM17.6892 13.8718C17.6884 13.8691 17.6888 13.8704 17.6893 13.8723C17.6898 13.8747 17.691 13.8797 17.6919 13.8865L17.2954 13.9391C17.2981 13.96 17.3035 13.9778 17.3041 13.98L17.6892 13.8718ZM17.7 13.9444C17.7 13.9061 17.6898 13.8742 17.6892 13.8718L17.3041 13.98C17.3052 13.9841 17.3041 13.9803 17.3031 13.9753C17.3018 13.9692 17.3 13.9584 17.3 13.9444H17.7ZM17.7 16.7784V13.9444H17.3V16.7784H17.7ZM17.25 17.2284C17.4986 17.2284 17.7 17.0271 17.7 16.7784H17.3C17.3 16.8062 17.2777 16.8284 17.25 16.8284V17.2284ZM16.8 16.7784C16.8 17.0271 17.0014 17.2284 17.25 17.2284V16.8284C17.2223 16.8284 17.2 16.8062 17.2 16.7784H16.8ZM16.8 14.5185V16.7784H17.2V14.5185H16.8ZM15.3422 16.3331L17.1362 14.6649L16.8638 14.372L15.0699 16.0402L15.3422 16.3331ZM11.9995 17.6977C13.2116 17.6977 14.4246 17.2412 15.3467 16.3288L15.0654 16.0445C14.2219 16.8791 13.1117 17.2977 11.9995 17.2977V17.6977ZM8.64448 16.3249C9.57004 17.2408 10.786 17.6977 11.9995 17.6977V17.2977C10.8859 17.2977 9.77273 16.8786 8.92583 16.0406L8.64448 16.3249ZM7.52409 14.5685C7.75931 15.2256 8.14674 15.8333 8.64461 16.325L8.9257 16.0404C8.47045 15.5908 8.11569 15.0343 7.9007 14.4337L7.52409 14.5685ZM7.79601 13.9929C7.56134 14.0767 7.44089 14.3346 7.52397 14.5681L7.90082 14.434C7.89113 14.4068 7.90543 14.3786 7.93054 14.3696L7.79601 13.9929ZM8.37137 14.2646C8.28696 14.0292 8.02898 13.9126 7.79812 13.9922L7.92843 14.3703C7.95831 14.3601 7.98647 14.3763 7.99483 14.3996L8.37137 14.2646ZM9.27731 15.685C8.87472 15.2871 8.56147 14.796 8.37141 14.2648L7.99479 14.3995C8.20512 14.9874 8.55105 15.5296 8.99612 15.9695L9.27731 15.685ZM14.7191 15.6837C13.2194 17.1685 10.7771 17.169 9.27739 15.6851L8.99604 15.9694C10.6516 17.6076 13.345 17.6071 15.0006 15.9679L14.7191 15.6837ZM16.4777 14.048L14.7237 15.6793L14.9961 15.9722L16.7501 14.3409L16.4777 14.048ZM14.3862 14.3944H16.6139V13.9944H14.3862V14.3944ZM13.9362 13.9444C13.9362 14.1931 14.1376 14.3944 14.3862 14.3944V13.9944C14.3585 13.9944 14.3362 13.9722 14.3362 13.9444H13.9362ZM14.3862 13.4944C14.1376 13.4944 13.9362 13.6958 13.9362 13.9444H14.3362C14.3362 13.9167 14.3585 13.8944 14.3862 13.8944V13.4944ZM17.25 13.4944H14.3862V13.8944H17.25V13.4944ZM17.3107 13.5036C17.3107 13.5036 17.3105 13.5036 17.3104 13.5035C17.3102 13.5035 17.3099 13.5034 17.3096 13.5033C17.3089 13.5031 17.3078 13.5028 17.3066 13.5024C17.304 13.5017 17.3 13.5006 17.2949 13.4994C17.2847 13.4971 17.2691 13.4944 17.25 13.4944V13.8944C17.2316 13.8944 17.2168 13.8919 17.2074 13.8897C17.199 13.8879 17.1921 13.8856 17.1937 13.8861L17.3107 13.5036ZM17.2583 13.4954C17.2754 13.4957 17.2893 13.4982 17.2981 13.5003C17.3058 13.502 17.3123 13.5041 17.3108 13.5037L17.1937 13.8861C17.1937 13.8861 17.1942 13.8863 17.1948 13.8865C17.1954 13.8866 17.1964 13.8869 17.1976 13.8873C17.2 13.888 17.2038 13.889 17.2085 13.8901C17.218 13.8923 17.2326 13.8949 17.2505 13.8953L17.2583 13.4954ZM17.3518 13.5106C17.341 13.5077 17.3024 13.4962 17.2582 13.4954L17.2506 13.8953C17.2423 13.8951 17.237 13.894 17.2367 13.894C17.2364 13.8939 17.237 13.894 17.2388 13.8945C17.2408 13.895 17.2431 13.8956 17.247 13.8967L17.3518 13.5106ZM17.3602 13.5129C17.3582 13.5124 17.3559 13.5117 17.3518 13.5106L17.247 13.8967C17.2506 13.8976 17.2559 13.8991 17.2616 13.9005L17.3602 13.5129ZM17.3593 13.5125C17.358 13.5123 17.3572 13.5122 17.3566 13.512C17.356 13.5119 17.3559 13.5119 17.356 13.5119C17.3561 13.512 17.358 13.5123 17.3609 13.5131L17.2608 13.9003C17.2752 13.9041 17.2886 13.9062 17.2921 13.9068L17.3593 13.5125ZM17.43 13.5325C17.3962 13.5178 17.3631 13.5132 17.3593 13.5125L17.2921 13.9068C17.2944 13.9072 17.2951 13.9073 17.2954 13.9074C17.2957 13.9074 17.2946 13.9073 17.2928 13.9068C17.2894 13.906 17.2808 13.9039 17.2698 13.8991L17.43 13.5325ZM17.4891 13.5684C17.4871 13.5669 17.4624 13.5467 17.4301 13.5326L17.2698 13.899C17.2583 13.894 17.2503 13.8889 17.2469 13.8866C17.2441 13.8847 17.2422 13.8831 17.2458 13.8859L17.4891 13.5684ZM17.4835 13.5648C17.4873 13.5672 17.4898 13.569 17.4906 13.5696C17.4911 13.5699 17.4913 13.5701 17.4891 13.5684L17.2458 13.8859C17.2481 13.8877 17.26 13.897 17.2743 13.9058L17.4835 13.5648ZM17.4905 13.5692C17.4879 13.5676 17.4859 13.5664 17.4841 13.5652L17.2737 13.9054C17.2769 13.9074 17.2801 13.9093 17.2826 13.9109L17.4905 13.5692ZM17.5732 13.6324C17.54 13.598 17.502 13.5761 17.4906 13.5692L17.2826 13.9109C17.2866 13.9133 17.2891 13.9148 17.2913 13.9162C17.2933 13.9175 17.2942 13.9181 17.2943 13.9182C17.2948 13.9185 17.2908 13.9158 17.2854 13.9102L17.5732 13.6324ZM17.5312 13.5994C17.5298 13.5986 17.5357 13.6019 17.5422 13.6063C17.5496 13.6113 17.5608 13.6196 17.5726 13.6318L17.286 13.9109C17.2984 13.9236 17.3102 13.9323 17.3182 13.9378C17.3222 13.9405 17.3256 13.9425 17.3277 13.9438C17.3287 13.9444 17.3296 13.9449 17.3302 13.9453C17.3307 13.9456 17.3312 13.9458 17.3312 13.9458L17.5312 13.5994ZM17.5799 13.6382C17.5668 13.624 17.554 13.6143 17.5452 13.6083C17.5409 13.6053 17.5372 13.603 17.5349 13.6016C17.5337 13.6009 17.5328 13.6003 17.5321 13.6C17.5315 13.5996 17.531 13.5994 17.531 13.5993L17.3313 13.9459C17.3328 13.9468 17.3263 13.9432 17.3192 13.9382C17.311 13.9327 17.2989 13.9235 17.2863 13.9099L17.5799 13.6382ZM17.6234 13.7018C17.6215 13.6981 17.6065 13.6667 17.5794 13.6377L17.2868 13.9104C17.278 13.901 17.2725 13.8926 17.2697 13.8881C17.2675 13.8845 17.2662 13.8817 17.2685 13.8862L17.6234 13.7018ZM17.6195 13.6956C17.6221 13.6995 17.6237 13.7024 17.6243 13.7034C17.6247 13.7041 17.6248 13.7044 17.6235 13.7018L17.2685 13.8862C17.27 13.8891 17.2773 13.9036 17.2874 13.9186L17.6195 13.6956ZM17.6238 13.7019C17.6213 13.6983 17.6201 13.6966 17.6192 13.6952L17.2877 13.919C17.2913 13.9244 17.2949 13.9294 17.297 13.9325L17.6238 13.7019ZM17.6675 13.7792C17.6521 13.7403 17.6297 13.7102 17.6238 13.7018L17.297 13.9325C17.2992 13.9355 17.3003 13.9371 17.3012 13.9385C17.3021 13.9398 17.3022 13.94 17.302 13.9396C17.3014 13.9387 17.2986 13.934 17.2956 13.9265L17.6675 13.7792ZM17.6912 13.8808C17.6894 13.8669 17.6851 13.8237 17.6674 13.7791L17.2956 13.9266C17.293 13.92 17.2923 13.9157 17.2923 13.9162C17.2924 13.9164 17.2926 13.9174 17.2929 13.9198C17.2933 13.9224 17.2936 13.9253 17.2942 13.93L17.6912 13.8808Z" />
  </>
)

const CHEFHAT_1PT = (
  <>
    <g fill="#06556e">
      <path d="M5.30 23.62 c-0.77 -0.65 -0.80 -0.85 -0.80 -5.23 l0 -4.15 -0.82 -0.22 c-1.10 -0.28 -2.52 -1.55 -3.13 -2.82 -0.42 -0.83 -0.47 -1.13 -0.47 -2.45 0.02 -1.22 0.08 -1.67 0.40 -2.33 1.03 -2.18 2.77 -3.28 5.22 -3.28 1.07 0 1.30 -0.03 1.38 -0.28 0.25 -0.83 2.02 -2.20 3.33 -2.60 2.22 -0.67 4.88 0.25 6.25 2.15 l0.53 0.73 1.18 -0.02 c2.47 0 4.43 1.38 5.30 3.75 0.98 2.72 -0.45 5.98 -3.10 7.05 l-1 0.40 -0.08 4.22 c-0.08 4.45 -0.13 4.68 -0.90 5.20 -0.37 0.23 -1.15 0.27 -6.63 0.27 -6.20 0 -6.22 0 -6.67 -0.38z m12.93 -0.88 c0.20 -0.18 0.27 -0.60 0.27 -1.50 l0 -1.23 -6.50 0 -6.50 0 0 1.20 c0 1.93 -0.47 1.80 6.52 1.80 5.20 0 6 -0.03 6.22 -0.27z m0.27 -6.47 l0 -2.75 0.95 -0.27 c1.20 -0.35 2.05 -0.95 2.68 -1.92 0.70 -1.03 0.92 -1.88 0.82 -3 -0.25 -2.88 -2.85 -4.73 -5.75 -4.08 -0.42 0.08 -0.50 0.02 -0.83 -0.55 -0.20 -0.35 -0.73 -0.98 -1.18 -1.42 -2.30 -2.15 -5.77 -1.48 -7.52 1.43 -0.33 0.58 -0.42 0.62 -0.90 0.52 -2.13 -0.48 -4.32 0.50 -5.27 2.38 -0.57 1.10 -0.58 3.13 -0.05 4.15 0.68 1.30 1.93 2.25 3.33 2.53 l0.63 0.13 0.05 2.78 0.03 2.78 6.50 0 6.50 0 0 -2.73z" />
      <path d="M11.58 15.30 c-0.05 -0.12 -0.07 -1.92 -0.05 -3.97 l0.05 -3.75 0.42 0 0.42 0 0 3.92 0 3.92 -0.38 0.05 c-0.20 0.03 -0.40 -0.05 -0.45 -0.17z" />
      <path d="M7.70 14.38 c-0.58 -0.23 -1.28 -2.73 -1.17 -4.15 0.05 -0.57 0.10 -0.65 0.47 -0.65 0.40 0 0.42 0.07 0.53 1.25 0.13 1.33 0.37 2.20 0.77 2.78 0.38 0.62 0.07 1.03 -0.60 0.77z" />
      <path d="M15.72 14.38 c-0.30 -0.12 -0.27 -0.52 0.10 -1.03 0.32 -0.43 0.65 -1.85 0.73 -3.10 0.03 -0.58 0.08 -0.67 0.45 -0.67 0.42 0 0.42 0.02 0.40 1.27 -0.02 1.43 -0.43 2.93 -0.93 3.37 -0.35 0.30 -0.42 0.32 -0.75 0.17z" />
    </g>
  </>
)

const TIME_4PT = (
  <>
    {/* <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> */}
    <mask id="mask0_908_121445" maskUnits="userSpaceOnUse" x="1" y="1" width="22" height="22">
      <path
        d="M11.999 1C18.0755 1 22.9997 5.92503 23 11.999C23 18.0752 18.0757 23 11.999 23C5.92454 22.9997 1 18.075 1 11.999C1.00026 5.92519 5.9247 1.00026 11.999 1ZM11.999 2C6.48574 2.00026 2.00026 6.48574 2 11.999C2 17.513 6.48558 21.9997 11.999 22C17.5132 22 22 17.5132 22 11.999C21.9997 6.48558 17.513 2 11.999 2ZM12 6.35254C12.2764 6.35254 12.5 6.57617 12.5 6.85254V12.3369L15.2715 14.8477C15.4761 15.0332 15.4912 15.3496 15.3057 15.5547C15.2071 15.6634 15.0717 15.7188 14.9355 15.7188C14.8155 15.7187 14.6958 15.6761 14.5996 15.5898L11.6641 12.9297C11.5596 12.835 11.5 12.6997 11.5 12.5586V6.85254C11.5 6.57617 11.7236 6.35254 12 6.35254Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_908_121445)">
      <rect width="24" height="24" fill="#666666" />
    </g>
    {/* </svg> */}
  </>
)

const EXPAND_1PT = (
  <path
    d="M9.69922 13.5742C9.89453 13.3789 10.2109 13.3789 10.4062 13.5742C10.6016 13.7685 10.6016 14.0859 10.4062 14.2812L3.22852 21.4688L9.62109 21.5C9.89746 21.501 10.1201 21.7266 10.1191 22.002C10.1182 22.2773 9.89453 22.5 9.61914 22.5H9.61719L2.03418 22.4639C1.89551 22.4629 1.77148 22.4062 1.68066 22.3154L1.67871 22.3135L1.60352 22.2002L1.5752 22.1582L1.53613 21.9404L1.5 14.3711C1.49902 14.0957 1.72168 13.8701 1.99805 13.8691H2C2.27539 13.8691 2.49902 14.0918 2.5 14.3672L2.53125 20.7529L9.69922 13.5742ZM14.3828 1.5L21.9658 1.53613C22.1045 1.53711 22.2285 1.59375 22.3193 1.68457L22.3213 1.68652L22.3926 1.79297L22.4248 1.8418L22.4639 2.05957L22.5 9.62891C22.501 9.9043 22.2783 10.1299 22.002 10.1309H22C21.7246 10.1309 21.501 9.9082 21.5 9.63281L21.4688 3.24707L14.3008 10.4258C14.2031 10.5234 14.0752 10.5723 13.9473 10.5723C13.8193 10.5723 13.6914 10.5234 13.5938 10.4258C13.3984 10.2315 13.3984 9.91406 13.5938 9.71875L20.7715 2.53125L14.3789 2.5C14.1025 2.49902 13.8799 2.27343 13.8809 1.99805C13.8818 1.72266 14.1055 1.5 14.3809 1.5H14.3828Z"
    fill="inherit"
  />
)

const COLLAPSE_1PT = (
  <path
    d="M2.4375 13.4014L10.0518 13.4375C10.1904 13.4375 10.3145 13.4951 10.4053 13.5859L10.4072 13.5879L10.4785 13.6943L10.5107 13.7432L10.5498 13.9609L10.5859 21.5557C10.5869 21.8311 10.3643 22.0566 10.0879 22.0576H10.0859C9.81055 22.0576 9.58692 21.835 9.58594 21.5596L9.55566 15.1475L2.35352 22.3535C2.25586 22.4512 2.12793 22.5 2 22.5C1.87207 22.5 1.74413 22.4512 1.64648 22.3535C1.45117 22.1582 1.45117 21.8418 1.64648 21.6465L8.85645 14.4326L2.43359 14.4014C2.15722 14.4004 1.93457 14.1748 1.93555 13.8994C1.93653 13.624 2.16016 13.4014 2.43555 13.4014H2.4375ZM21.6484 1.64648C21.8437 1.45118 22.1602 1.45118 22.3555 1.64648C22.5508 1.8418 22.5508 2.15823 22.3555 2.35352L15.1455 9.56738L21.5684 9.59863C21.8447 9.59961 22.0674 9.8252 22.0664 10.1006C22.0654 10.376 21.8418 10.5986 21.5664 10.5986H21.5645L13.9502 10.5625C13.8115 10.5625 13.6875 10.5049 13.5967 10.4141L13.5947 10.4121L13.5195 10.2988L13.4912 10.2568L13.4521 10.0391L13.416 2.44434C13.415 2.16895 13.6377 1.94336 13.9141 1.94238H13.916C14.1914 1.94238 14.415 2.16504 14.416 2.44043L14.4463 8.85254L21.6484 1.64648Z"
    fill="inherit"
  />
)

const MENU_NAVIGATION_1PT = (
  <path
    d="M22 18C22.2764 18 22.5 18.2236 22.5 18.5C22.5 18.7764 22.2764 19 22 19H2C1.72363 19 1.5 18.7764 1.5 18.5C1.5 18.2236 1.72363 18 2 18H22ZM22 11.5342C22.2764 11.5342 22.5 11.7578 22.5 12.0342C22.5 12.3105 22.2764 12.5342 22 12.5342H2C1.72363 12.5342 1.5 12.3105 1.5 12.0342C1.5 11.7578 1.72363 11.5342 2 11.5342H22ZM22 5C22.2764 5 22.5 5.22363 22.5 5.5C22.5 5.77637 22.2764 6 22 6H2C1.72363 6 1.5 5.77637 1.5 5.5C1.5 5.22363 1.72363 5 2 5H22Z"
    fill="inherit"
  />
)

const LINE_GRAPH_1PT = (
  <g fill="none">
    <path d="M21 21L3 21L3 3" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M6 18L11.5714 16.1053L13.4286 11.0526H16.5238L19 6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </g>
)

const PIE_CHART_1PT = (
  <path
    d="M10.8623 3.77637C11.1385 3.77663 11.3623 4.00016 11.3623 4.27637V12.6377H19.7236C19.9998 12.638 20.2236 12.8615 20.2236 13.1377C20.2234 18.2999 16.0236 22.4988 10.8623 22.499C5.70036 22.499 1.50026 18.3001 1.5 13.1377C1.5 7.97608 5.70019 3.77637 10.8623 3.77637ZM10.3623 4.79102C5.9834 5.05029 2.5 8.69482 2.5 13.1377C2.50026 17.7483 6.25163 21.499 10.8623 21.499C15.3048 21.4988 18.9495 18.0167 19.209 13.6377H10.8623C10.5861 13.6377 10.3626 13.4138 10.3623 13.1377V4.79102ZM14.1289 1.5C18.7446 1.5 22.5 5.25488 22.5 9.87012C22.5 10.1463 22.2761 10.3699 22 10.3701H14.1289C13.8525 10.3701 13.6289 10.1465 13.6289 9.87012V2C13.6289 1.72363 13.8525 1.5 14.1289 1.5ZM14.6289 9.37012H21.4834C21.2363 5.70362 18.2959 2.76367 14.6289 2.5166V9.37012Z"
    fill="inherit"
  />
)

const EXPORT_REPORT_1PT = (
  <path
    d="M11.6631 12.0488C11.9395 12.0488 12.1631 12.2725 12.1631 12.5488V18.2803L14.5215 15.8789C14.7139 15.6821 15.0312 15.6807 15.2285 15.873C15.4255 16.0669 15.4276 16.3829 15.2344 16.5801L12.0225 19.8506C11.9287 19.9463 11.7998 20 11.666 20L11.6641 19.999L11.6631 20C11.5205 20 11.3936 19.939 11.3027 19.8438L8.09668 16.5801C7.90332 16.3828 7.90528 16.0664 8.10254 15.873C8.2998 15.6797 8.61621 15.6821 8.80957 15.8789L11.1631 18.2744V12.5488C11.1631 12.2725 11.3867 12.0488 11.6631 12.0488ZM11.3193 4C13.7412 4 15.916 5.5874 16.749 7.91113C16.9072 7.89258 17.0596 7.87988 17.1992 7.87988C20.1719 7.87988 22.5 10.2905 22.5 13.3672C22.4998 16.1995 20.7645 18.2788 17.9707 18.793L17.8789 18.8008C17.6426 18.8008 17.4326 18.6318 17.3877 18.3906C17.3382 18.1193 17.5177 17.8584 17.7891 17.8086C20.1131 17.3809 21.4998 15.7209 21.5 13.3672C21.5 10.8511 19.6113 8.87988 17.1992 8.87988C16.9824 8.87988 16.7285 8.9209 16.4756 8.95898C16.2178 8.99609 15.9863 8.8413 15.9189 8.59863C15.329 6.47974 13.4373 5 11.3193 5C8.66226 5 6.50025 7.26195 6.5 10.042C6.5 10.2728 6.49813 10.5431 6.53418 10.7744C6.55662 10.9183 6.51448 11.0655 6.41992 11.1768C6.27148 11.3511 6.0293 11.3662 5.76367 11.3252L5.63965 11.3096C3.9082 11.3096 2.5 12.7866 2.5 14.6025C2.5 16.4434 3.31641 17.4326 5.14844 17.8096C5.41895 17.8652 5.59375 18.1299 5.53711 18.4004C5.48242 18.6719 5.21387 18.8438 4.94727 18.7891C2.62793 18.3115 1.5 16.9419 1.5 14.6025C1.5 12.2832 3.28223 10.3872 5.50195 10.3115C5.50001 10.2175 5.5 10.1263 5.5 10.042C5.50025 6.71019 8.1105 4 11.3193 4Z"
    fill="inherit"
  />
)

const PLAY_ICON_1PT = <path d="M5 4L19 12L5 20V4Z" fill="inherit" />

const ROWS_ICON_1PT = (
  <path
    d="M22 18C22.2764 18 22.5 18.2236 22.5 18.5C22.5 18.7764 22.2764 19 22 19H2C1.72363 19 1.5 18.7764 1.5 18.5C1.5 18.2236 1.72363 18 2 18H22ZM22 11.5342C22.2764 11.5342 22.5 11.7578 22.5 12.0342C22.5 12.3105 22.2764 12.5342 22 12.5342H2C1.72363 12.5342 1.5 12.3105 1.5 12.0342C1.5 11.7578 1.72363 11.5342 2 11.5342H22ZM22 5C22.2764 5 22.5 5.22363 22.5 5.5C22.5 5.77637 22.2764 6 22 6H2C1.72363 6 1.5 5.77637 1.5 5.5C1.5 5.22363 1.72363 5 2 5H22Z"
    fill="inherit"
  />
)

const COLUMNS_ICON_1PT = (
  <path
    d="M18 2C18 1.72363 18.2236 1.5 18.5 1.5C18.7764 1.5 19 1.72363 19 2V22C19 22.2764 18.7764 22.5 18.5 22.5C18.2236 22.5 18 22.2764 18 22V2ZM11.5342 2C11.5342 1.72363 11.7578 1.5 12.0342 1.5C12.3105 1.5 12.5342 1.72363 12.5342 2V22C12.5342 22.2764 12.3105 22.5 12.0342 22.5C11.7578 22.5 11.5342 22.2764 11.5342 22V2ZM5 2C5 1.72363 5.22363 1.5 5.5 1.5C5.77637 1.5 6 1.72363 6 2L6 22C6 22.2764 5.77637 22.5 5.5 22.5C5.22363 22.5 5 22.2764 5 22L5 2Z"
    fill="inherit"
  />
)

const TABLE_1PT = (
  <path
    d="M3 20.4V3.6C3 3.26863 3.26863 3 3.6 3H20.4C20.7314 3 21 3.26863 21 3.6V20.4C21 20.7314 20.7314 21 20.4 21H3.6C3.26863 21 3 20.7314 3 20.4ZM3 16.5H21M3 12H21M21 7.5H3M12 21V3"
    fill="none"
    stroke="currentColor"
    strokeWidth="1"
    strokeLinecap="round"
    strokeLinejoin="round"
  />
)

const VALUES_ICON_1PT = (
  <path
    d="M7.10933 19.566H19V21H5V19.9057L11.664 11.5094L5.14933 4.11321V3H18.3467V4.43396H7.352L13.624 11.4717L7.10933 19.566Z"
    fill="inherit"
  />
)

const DRAG_ICON = (
  <>
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M15.0009 7.50003H16.5009V9.00003H15.0009V7.50003ZM16.5009 16.5H15.0009V15H16.5009V16.5ZM9.00094 9.00003H7.50094V7.50003H9.00094V9.00003ZM9.00094 16.5H7.50094V15H9.00094V16.5ZM12.0009 0.0195312L0.0234375 12.0015L7.85644 19.833L12.0009 23.9775L23.9769 12L12.0009 0.0195312Z"
      fill="none"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M21.8476 11.9957L18.0121 7.78223V10.5257H11.9986H5.99712V7.78223L2.14062 11.9957L5.99863 16.2077L5.99712 13.4957H11.9986H18.0121L18.0106 16.2077L21.8476 11.9957Z"
      fill="currenColor"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M13.4839 12.0001H13.5034V5.99855L16.2139 6.00005L12.0034 2.14355L7.78994 5.99855H10.5034V12.0001H10.5154V18.0031L7.77344 18.0016L11.9839 21.8671L16.1914 18.0031H13.4839V12.0001Z"
      fill="currenColor"
    />
  </>
)

const DRAG_HANDLE_ICON = (
  <path
    d="M6.5 19C7.60457 19 8.5 19.8954 8.5 21C8.5 22.1046 7.60457 23 6.5 23C5.39543 23 4.5 22.1046 4.5 21C4.5 19.8954 5.39543 19 6.5 19ZM17.5 19C18.6046 19 19.5 19.8954 19.5 21C19.5 22.1046 18.6046 23 17.5 23C16.3954 23 15.5 22.1046 15.5 21C15.5 19.8954 16.3954 19 17.5 19ZM6.5 10C7.60457 10 8.5 10.8954 8.5 12C8.5 13.1046 7.60457 14 6.5 14C5.39543 14 4.5 13.1046 4.5 12C4.5 10.8954 5.39543 10 6.5 10ZM17.5 10C18.6046 10 19.5 10.8954 19.5 12C19.5 13.1046 18.6046 14 17.5 14C16.3954 14 15.5 13.1046 15.5 12C15.5 10.8954 16.3954 10 17.5 10ZM6.5 1C7.60457 1 8.5 1.89543 8.5 3C8.5 4.10457 7.60457 5 6.5 5C5.39543 5 4.5 4.10457 4.5 3C4.5 1.89543 5.39543 1 6.5 1ZM17.5 1C18.6046 1 19.5 1.89543 19.5 3C19.5 4.10457 18.6046 5 17.5 5C16.3954 5 15.5 4.10457 15.5 3C15.5 1.89543 16.3954 1 17.5 1Z"
    fill="currentColor"
  />
)

const BLOCK_ICON = (
  <>
    <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" />
    <path d="M18 6L6 18" fill="none" stroke="currentColor" />
  </>
)

/**
 * Paint bucket with a color bar underneath. Render inside an <SvgIcon>; `barColor` tints just
 * the bar (e.g. to preview a picked color), defaulting to currentColor like the glyph.
 */
export const PaintBucketIcon = ({ barColor }: { barColor?: string }): JSX.Element => (
  <>
    <g transform="translate(3.6 1)">
      <rect
        x="7.90188"
        y="2.30838"
        width="9.81668"
        height="8.41429"
        transform="rotate(45 7.90188 2.30838)"
        fill="none"
        stroke="currentColor"
      />
      <path
        d="M14.8433 9.24981L8.89351 15.1996L1.95208 8.25818L14.8433 9.24981Z"
        fill="currentColor"
      />
      <path d="M7.7616 2.1626L5.95255 0.353553" fill="none" stroke="currentColor" />
      <path
        d="M16.439 13.3337C16.578 13.5514 16.6596 13.8091 16.6597 14.0866C16.6597 14.8613 16.032 15.4898 15.2573 15.4899C14.4826 15.4899 13.854 14.8613 13.854 14.0866C13.8541 13.8307 13.9247 13.5919 14.0444 13.3854H14.0396L15.2544 11.2829L16.439 13.3337Z"
        fill="currentColor"
      />
    </g>
    <rect x="3" y="19" width="18" height="4" fill={barColor ?? 'currentColor'} />
  </>
)

const MAP_MARKER_ICON = (
  <>
    <path
      d="M12 0.5C17.2467 0.5 21.5 4.75329 21.5 10C21.5 17.0259 13.0258 22.9029 12.3154 23.3955C12.3001 23.4062 12.2882 23.4144 12.2803 23.4199C12.1954 23.4704 12.0987 23.498 12 23.5C11.9013 23.498 11.8046 23.4704 11.7197 23.4199C11.7118 23.4144 11.6999 23.4062 11.6846 23.3955C10.9742 22.9029 2.5 17.0259 2.5 10C2.5 4.75329 6.75329 0.5 12 0.5ZM12 1.5C7.30786 1.50551 3.50551 5.30786 3.5 10C3.5 15.9998 10.4997 21.3194 12 22.3896C13.4904 21.3194 20.5 15.9998 20.5 10C20.4945 5.30786 16.6921 1.50551 12 1.5ZM12 6.5C13.933 6.5 15.5 8.067 15.5 10C15.5 10.9283 15.131 11.8182 14.4746 12.4746C13.8182 13.131 12.9283 13.5 12 13.5C10.067 13.5 8.5 11.933 8.5 10C8.5 8.067 10.067 6.5 12 6.5ZM12 7.5C10.6193 7.5 9.5 8.61929 9.5 10C9.5 11.3807 10.6193 12.5 12 12.5C13.3807 12.5 14.5 11.3807 14.5 10C14.5 8.61929 13.3807 7.5 12 7.5Z"
      fill="inherit"
    />
  </>
)

const FUNNEL_ICON_1PT = (
  <g fill="none">
    <path
      d="M2 4.85714V2H22V4.85714L13.7143 16.8571V19.1429L10.2857 21.1429V16.8571L2 4.85714Z"
      stroke="currentColor"
      strokeLinecap="round"
    />
    <path d="M2.28906 5.14258H12.0033" stroke="currentColor" strokeLinecap="round" />
  </g>
)

const NUMBER_ICON_1PT = (
  <path
    d="M10 3L6 21M20.5 16H2.5M22 7H4M18 3L14 21"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  />
)

const OPEN_IN_NEW_WINDOW_ICON_1PT = (
  <g fill="none">
    <path
      d="M21 3L15 3M21 3L12 12M21 3V9"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 13V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H11"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
    />
  </g>
)

const wrapIcon = (icon: ReactNode): JSX.Element => (
  <svg focusable="false" viewBox="0 0 24 24" aria-hidden="true">
    {icon}
  </svg>
)

// MENU
const IconRestaurant = wrapIcon(RESTAURANT_1PT)
const IconEdit = wrapIcon(EDIT_1PT)
const IconHistory = wrapIcon(HISTORY_1PT)
const IconDashboard = wrapIcon(DASHBOARD_1PT)
const IconMenu = wrapIcon(MENU_1PT)
const IconOrdering = wrapIcon(ORDERING_1PT)
const IconReports = wrapIcon(REPORTS_1PT)
const IconPayments = wrapIcon(PAYMENTS_1PT)
const IconMarketplace = wrapIcon(MARKETPLACE_1PT)
const IconSettings = wrapIcon(SETTINGS_1PT)
const IconStaff = wrapIcon(STAFF_1PT)
const IconHelp = wrapIcon(HELP_1PT)
const IconLogout = wrapIcon(LOGOUT_1PT)
const IconDevice = wrapIcon(DEVICE_1PT)
const IconGuestEngagement = wrapIcon(GUEST_ENGAGEMENT_1PT)
const IconGlobe = wrapIcon(GLOBE_1PT)

// APP BAR
const IconHamburger = wrapIcon(HAMBURGER_1PT)
const IconNotification = wrapIcon(NOTIFICATION_1PT)
const IconMessage = wrapIcon(MESSAGE_1PT)

// MISC
const OpenLink = wrapIcon(OpenLinkIcon({}))
const Eye1pt = wrapIcon(EYE_1PT)
const Eye2pt = wrapIcon(EYE_2PT)
const EyeCross1pt = wrapIcon(EYE_CROSS_1PT)
const EyeCross2pt = wrapIcon(EYE_CROSS_2PT)
const ErrorIcon1pt = wrapIcon(ERROR_1PT)
const ErrorIcon2pt = wrapIcon(ERROR_2PT)
const SuccessIcon = wrapIcon(SUCCESS_1PT)
const IconDelete = wrapIcon(DELETE_1PT)
const IconSearch1pt = wrapIcon(SEARCH_1PT)
const IconSearch2pt = wrapIcon(SEARCH_2PT)
const ArrowDown2pt = wrapIcon(ARROW_DOWN_2PT)
const Checkmark1pt = wrapIcon(CHECKMARK_1PT)
const Checkmark2pt = wrapIcon(CHECKMARK_2PT)
const CheckBoxSelected1pt = wrapIcon(CHECKBOX_SELECTED_1PT)
const CheckBoxUnselected1pt = wrapIcon(CHECKBOX_UNSELECTED_1PT)
const CheckBoxSemiSelected1pt = wrapIcon(CHECKBOX_SEMI_SELECTED_1PT)
const IconDelete2pt = wrapIcon(DELETE_2PT)
const Sort2pt = wrapIcon(SORT_2PT)
const Close1pt = wrapIcon(CLOSE_1PT)
const Close2pt = wrapIcon(CLOSE_2PT)
const RadioChecked1pt = wrapIcon(RADIO_CHECKED_1PT)
const RadioUnchecked1pt = wrapIcon(RADIO_UNCHECKED_1PT)
const ChevronDown2pt = wrapIcon(CHEVRON_DOWN_2PT)
const ChevronDown1pt = wrapIcon(CHEVRON_DOWN_1PT)
const ChevronUp2pt = wrapIcon(CHEVRON_UP_2PT)
const ChevronUp1pt = wrapIcon(CHEVRON_UP_1PT)
const ChevronRight1pt = wrapIcon(CHEVRON_RIGHT_1PT)
const ChevronRight2pt = wrapIcon(CHEVRON_RIGHT_2PT)
const ChevronLeft2pt = wrapIcon(CHEVRON_LEFT_2PT)
const DoubleChevronRight2pt = wrapIcon(DOUBLE_CHEVRON_RIGHT_2PT)
const DoubleChevronLeft2pt = wrapIcon(DOUBLE_CHEVRON_LEFT_2PT)
const Info2pt = wrapIcon(INFO_2PT)
const Info1pt = wrapIcon(INFO_1PT)
const MoreVertical1pt = wrapIcon(MORE_VERTICAL_1PT)
const Schedule1pt = wrapIcon(SCHEDULE_1PT)
const Schedule2pt = wrapIcon(SCHEDULE_2PT)
const Import1pt = wrapIcon(IMPORT_1PT)
const Import2pt = wrapIcon(IMPORT_2PT)
const CalendarIcon = wrapIcon(CALENDAR_2PT)
const CalendarIcon4pt = wrapIcon(CALENDAR_4PT)
const Filter1pt = wrapIcon(FILTER_1PT)
const Filter2pt = wrapIcon(FILTER_2PT)
const FilterStack1pt = wrapIcon(FILTER_STACK_1PT)
const ArrowLeft1pt = wrapIcon(ARROW_LEFT_1PT)
const ScrollArrowLeft2pt = wrapIcon(SCROLL_ARROW_LEFT_2PT)
const ScrollArrowRight2pt = wrapIcon(SCROLL_ARROW_RIGHT_2PT)
const SavedAccount1pt = wrapIcon(SAVED_ACCOUNT_1PT)
const SavedAccount2pt = wrapIcon(SAVED_ACCOUNT_2PT)
const TimeIcon2Pt = wrapIcon(TIME_2PT)
const Alert1pt = wrapIcon(ALERT_1PT)
const MinusIcon1Pt = wrapIcon(MINUS_1PT)
const PlusIcon1Pt = wrapIcon(PLUS_1PT)
const DollarSignIcon1Pt = wrapIcon(DOLLAR_SIGN_1PT)
const PercentageSignIcon1Pt = wrapIcon(PERCENTAGE_SIGN_1PT)
const AlertRound1Pt = wrapIcon(ALERT_ROUND_1PT)
const AlertRoundRed2Pt = wrapIcon(ALERT_ROUND_RED_2PT)
const Refresh1Pt = wrapIcon(REFRESH_1PT)
const Refresh2Pt = wrapIcon(REFRESH_2PT)
const Download = wrapIcon(DOWNLOAD)
const Print = wrapIcon(PRINT)
const Send = wrapIcon(SEND)
const Building1Pt = wrapIcon(BUILDING_1PT)
/** @public */
const Building2Pt = wrapIcon(BUILDING_2PT)
const MyLocation1Pt = wrapIcon(MY_LOCATION_1PT)
const Profile1Pt = wrapIcon(PROFILE_1PT)
/** @public */
const Profile2Pt = wrapIcon(PROFILE_2PT)
const AddCircle2Pt = wrapIcon(ADD_CIRCLE_2PT)
const DuplicateIcon2Pt = wrapIcon(DUPLICATE_ICON_2PT)
const Note1Pt = wrapIcon(NOTE_1PT)
const Note2Pt = wrapIcon(NOTE_2PT)
const Archive1Pt = wrapIcon(ARCHIVE_1PT)
const Camera1Pt = wrapIcon(CAMERA_1PT)
const CameraFlip1Pt = wrapIcon(CAMERAFLIP_1PT)
const ChefHat1Pt = wrapIcon(CHEFHAT_1PT)
const Time4Pt = wrapIcon(TIME_4PT)
const Expand1Pt = wrapIcon(EXPAND_1PT)
const Collapse1Pt = wrapIcon(COLLAPSE_1PT)
const MenuNavigation1Pt = wrapIcon(MENU_NAVIGATION_1PT)
const LineGraph1Pt = wrapIcon(LINE_GRAPH_1PT)
const PieChart1Pt = wrapIcon(PIE_CHART_1PT)
const Table1Pt = wrapIcon(TABLE_1PT)
const ExportReport1Pt = wrapIcon(EXPORT_REPORT_1PT)
const Play1Pt = wrapIcon(PLAY_ICON_1PT)
const OpenInNewWindowIcon1Pt = wrapIcon(OPEN_IN_NEW_WINDOW_ICON_1PT)

// FLOORPLAN
const SpinClockwise2pt = wrapIcon(SPIN_CLOCKWISE_2PT)
const SpinCounterClockwise2pt = wrapIcon(SPIN_COUNTER_CLOCKWISE_2PT)
const Floorplan1pt = wrapIcon(FLOORPLAN_1PT)
const AddCircle1pt = wrapIcon(ADD_CIRCLE_1PT)

// ORDERING
const Tablet1pt = wrapIcon(TABLET_1PT)
const Cash1pt = wrapIcon(CASH_1PT)
const Gift1pt = wrapIcon(GIFT_1PT)
const SwitchUser1pt = wrapIcon(SWITCH_USER_1PT)

// REPORTING
const RowsIcon1pt = wrapIcon(ROWS_ICON_1PT)
const ColumnsIcon1pt = wrapIcon(COLUMNS_ICON_1PT)
const ValuesIcon1pt = wrapIcon(VALUES_ICON_1PT)
const DragIcon = wrapIcon(DRAG_ICON)
const DragHandleIcon = wrapIcon(DRAG_HANDLE_ICON)
const BlockIcon = wrapIcon(BLOCK_ICON)
const MapMarkerIcon = wrapIcon(MAP_MARKER_ICON)
const FunnelIcon1pt = wrapIcon(FUNNEL_ICON_1PT)
const NumberIcon1pt = wrapIcon(NUMBER_ICON_1PT)

// MENU
export { IconRestaurant }
export { IconEdit }
export { IconHistory }
export { IconDashboard }
export { IconMenu }
export { IconOrdering }
export { IconReports }
export { IconPayments }
export { IconMarketplace }
export { IconSettings }
export { IconStaff }
export { IconHelp }
export { IconLogout }
export { IconDevice }
export { IconGuestEngagement }
export { IconGlobe }
// APP BAR
export { IconHamburger }
export { IconNotification }
export { IconMessage }
// MISC
export { OpenLink }
export { Eye1pt }
export { Eye2pt }
export { EyeCross1pt }
export { EyeCross2pt }
export { ErrorIcon1pt }
export { ErrorIcon2pt }
export { SuccessIcon }
export { IconDelete }
export { IconSearch1pt }
export { IconSearch2pt }
export { ArrowDown2pt }
export { Checkmark1pt }
export { Checkmark2pt }
export { CheckBoxSelected1pt }
export { CheckBoxUnselected1pt }
export { CheckBoxSemiSelected1pt }
export { IconDelete2pt }
export { Sort2pt }
export { Close1pt }
export { Close2pt }
export { RadioChecked1pt }
export { RadioUnchecked1pt }
export { ChevronDown2pt }
export { ChevronDown1pt }
export { ChevronUp2pt }
export { ChevronUp1pt }
export { ChevronRight1pt }
export { ChevronRight2pt }
export { ChevronLeft2pt }
export { DoubleChevronRight2pt }
export { DoubleChevronLeft2pt }
export { Info2pt }
export { Info1pt }
export { MoreVertical1pt }
export { Schedule1pt }
export { Schedule2pt }
export { Import1pt }
export { Import2pt }
export { CalendarIcon }
export { CalendarIcon4pt }
export { Filter1pt }
export { Filter2pt }
export { FilterStack1pt }
export { ArrowLeft1pt }
export { ScrollArrowLeft2pt }
export { ScrollArrowRight2pt }
export { SavedAccount1pt }
export { SavedAccount2pt }
export { TimeIcon2Pt }
export { Alert1pt }
export { MinusIcon1Pt }
export { PlusIcon1Pt }
export { DollarSignIcon1Pt }
export { PercentageSignIcon1Pt }
export { AlertRound1Pt }
export { AlertRoundRed2Pt }
export { Refresh1Pt }
export { Refresh2Pt }
export { Download }
export { Print }
export { Send }
export { MyLocation1Pt }
export { Building1Pt }
export { /** @public */ Building2Pt }
export { Profile1Pt }
export { /** @public */ Profile2Pt }
export { AddCircle2Pt }
export { DuplicateIcon2Pt }
export { Note1Pt }
export { Note2Pt }
export { Archive1Pt }
export { Camera1Pt }
export { CameraFlip1Pt }
export { ChefHat1Pt }
export { Time4Pt }
export { Expand1Pt }
export { Collapse1Pt }
export { MenuNavigation1Pt }
export { LineGraph1Pt }
export { PieChart1Pt }
export { Table1Pt }
export { ExportReport1Pt }
export { Play1Pt }
export { OpenInNewWindowIcon1Pt }

// FLOORPLAN
export { SpinClockwise2pt }
export { SpinCounterClockwise2pt }
export { Floorplan1pt }
export { AddCircle1pt }

// ORDERING
export { Tablet1pt }
export { Cash1pt }
export { Gift1pt }
export { SwitchUser1pt }

// REPORTING
export { RowsIcon1pt }
export { ColumnsIcon1pt }
export { ValuesIcon1pt }
export { DragIcon }
export { DragHandleIcon }
export { BlockIcon }
export { MapMarkerIcon }
export { FunnelIcon1pt }
export { NumberIcon1pt }
